import React, { useMemo, useState, useEffect } from 'react';
import { getTicketContext } from '../knowledge';
import { openVideoSignOff } from '../utils/buildVideoSignOff';
import { buildFreewheelSlackMessage } from '../utils/buildFreewheelSlackMessage';
import { computeVideoBidHealth } from '../validation/bidHealth';
import { buildHarFromRequests } from '../utils/buildHar';
import { findPackByIu } from '../api/appPacks';
import { postVideoSessions } from '../api/lifecycle';
import { useLatestWins } from '../utils/latestWins';
import { buildVideoPassport } from '../validation/videoPassport';
import { videoCoherence } from '../validation/videoCoherence';
import { videoPodRealization } from '../validation/videoPodRealization';
// Shared debounce (WP5, docs/frontend-validation-port-spec.md): this used to
// inline its own `400` independently of AdLifecycleView.jsx's identical
// literal — folded into one exported constant so the two can't drift apart.
import useCoalescedPost from '../utils/useCoalescedPost';
import { rewriteLinesForRow, REWRITE_LABEL } from '../utils/slotRewrite';

// Evaluate a single App Pack contentApis.brandRules entry (when/expect pair)
// against this session's parsed media tags + resolved video IU. Mirrors the
// proxy's own brandRules evaluation in proxyOnRequestHandler.js (~2886-2906)
// so the UI's verdict always matches what the proxy already flagged via
// validData.brandRuleMatched/issues — see proxy-server/config/apps/README.md
// contentApis.brandRules for the schema (id/when/expect/severity/code/message).
function evaluateBrandRule(rule, { mediaTags = [], iu = '' }) {
  const matchTerm = rule?.when?.mediaTagMatch;
  if (!matchTerm) return null;
  let re;
  try { re = new RegExp(String(matchTerm), 'i'); } catch { return null; }
  const tagMatched = mediaTags.some(t => re.test(String(t)));
  const expect = rule.expect || {};
  const iuLower = String(iu || '').toLowerCase();
  const mustContain = expect.iuContains ? String(expect.iuContains).toLowerCase() : null;
  const mustNotContain = expect.notContains ? String(expect.notContains).toLowerCase() : null;
  const hasRequired = mustContain ? iuLower.includes(mustContain) : true;
  const hasForbidden = mustNotContain ? iuLower.includes(mustNotContain) : false;
  return { rule, tagMatched, hasRequired, hasForbidden, mustContain, mustNotContain };
}

/* ─── timestamp helpers ─────────────────────────────────────── */
const toMs = (v) => {
  if (!v && v !== 0) return 0;
  if (typeof v === 'number') return v;
  const n = Date.parse(v);
  return isNaN(n) ? 0 : n;
};
const ts = (r) =>
  toMs(r?.startedAt) || toMs(r?.startedAtMs) || toMs(r?.timing?.start) ||
  toMs(r?.startedAtISO) || toMs(r?.timestamp) || 0;

const fmtTime  = (n) => n ? new Date(n).toTimeString().slice(0, 8) : '—';
const fmtDelta = (n) => {
  if (!n) return '';
  const s = Math.round(n / 1000);
  if (s < 60) return `+${s}s`;
  const m = Math.floor(s / 60), r = s % 60;
  return `+${m}m${r ? r + 's' : ''}`;
};

/* ─── metadata maps ─────────────────────────────────────────── */
const CONTENT_TYPE_META = {
  live:         { icon: '🔴', label: 'Live',        color: '#e53e3e' },
  full_episode: { icon: '📺', label: 'Full Episode', color: '#3182ce' },
  clip:         { icon: '✂️', label: 'Clip',         color: '#805ad5' },
  unknown:      { icon: '🎬', label: 'Video',        color: '#718096' },
};

const QUARTILE_ORDER  = ['start', 'firstquartile', 'midpoint', 'thirdquartile', 'complete'];

// Count only the 5 canonical quartile events a break fired (ignores non-quartile
// beacons like impression/creativeView/pause that inflate firedQuartiles.size).
const canonicalQuartiles = (b) => QUARTILE_ORDER.filter(q => b?.firedQuartiles?.has(q)).length;

/* ─── timing helpers ─────────────────────────────────────────── */
const fmtMs = (ms) => {
  if (ms == null || ms < 0) return '—';
  return ms < 1000 ? `${Math.round(ms)}ms` : `${(ms / 1000).toFixed(1)}s`;
};
const fmtDur = (sec) => {
  if (sec == null) return '—';
  const m = Math.floor(sec / 60), s = Math.round(sec % 60);
  return m > 0 ? `${m}:${String(s).padStart(2, '0')}` : `${s}s`;
};

// Detect whether an ad was skippable / skipped from its tracking beacons and
// (when available) its parsed VAST creative. A 'skip'/'skipped' beacon proves
// the ad had a skip button; a VAST skipoffset declares one even if not clicked.
const SKIP_EVENTS = ['skip', 'skipped'];
function detectSkip(beacons, creative) {
  const skipped   = (beacons || []).some(r => SKIP_EVENTS.includes(r.validData?.event));
  const skippable = skipped || !!creative?.skippable;
  return { skipped, skippable, skipOffset: creative?.skipOffset || null };
}

// Earliest timestamp per quartile event across a break's beacons.
function quartileTimeline(beacons) {
  const times = {};
  for (const r of (beacons || [])) {
    const ev = r.validData?.event?.toLowerCase();
    if (!ev) continue;
    const t = ts(r);
    if (!t) continue;
    if (times[ev] == null || t < times[ev]) times[ev] = t;
  }
  return times;
}

// A real DAI stream GUID (from the registration response stream_id) — as opposed
// to the URL-fragment fallback (which always contains a '/'). Only real GUIDs are
// trustworthy join keys for binding impression beacons to their stream.
function isRealStreamGuid(id) {
  return typeof id === 'string' && id.length >= 12 && !id.includes('/') && id !== 'DAI Stream';
}

// The DAI stream GUID a DAI impression beacon carries in its URL
// (.../impression/stream/{guid}/...), extracted by the proxy into
// rawParams.streamId. This is the order-independent join key to a session.
function daiStreamId(r) {
  const g = r?.validData?.rawParams?.streamId;
  return isRealStreamGuid(g) ? g : null;
}

// Lowercased searchable text for a session (ad unit, advertiser, content, stream).
function sessionSearchText(s) {
  const parts = [
    s.displayTitle, s.adUnit, s.streamId, s.contentType, s.sessionType,
    s.gamCreative?.advertiser, s.vast?.creative?.advertiser,
    ...(s.adBreaks || []).map(b => b.advertiser || b.creative?.advertiser),
  ];
  return parts.filter(Boolean).join(' ').toLowerCase();
}

// Buffer latency (anchor → start), inter-quartile gaps, play duration, stall detection.
function computeBreakTiming(anchorTs, quartileTimes, durationSeconds) {
  const startT   = quartileTimes.start ?? null;
  const bufferMs = (startT != null && anchorTs) ? Math.max(0, startT - anchorTs) : null;
  const gaps = [];
  let prev = null, prevName = null;
  for (const q of QUARTILE_ORDER) {
    const t = quartileTimes[q];
    if (t == null) continue;
    if (prev != null) gaps.push({ from: prevName, to: q, ms: t - prev });
    prev = t; prevName = q;
  }
  const playMs = (quartileTimes.complete != null && startT != null) ? quartileTimes.complete - startT : null;
  let stall = null;
  if (durationSeconds && gaps.length) {
    const expectedSeg = (durationSeconds * 1000) / 4;
    const worst = gaps.reduce((a, b) => (b.ms > a.ms ? b : a), gaps[0]);
    if (worst.ms > expectedSeg * 2.5) stall = { ...worst, expectedMs: Math.round(expectedSeg) };
  }
  return { bufferMs, gaps, playMs, stall };
}

/* ─── status colours ────────────────────────────────────────── */
const S = {
  pass:    { bg: 'rgba(56,161,105,0.10)',  border: '#38a169', text: '#276749' },
  warn:    { bg: 'rgba(221,107,32,0.10)',  border: '#dd6b20', text: '#9c4221' },
  fail:    { bg: 'rgba(229,62,62,0.10)',   border: '#e53e3e', text: '#9b2c2c' },
  info:    { bg: 'rgba(49,130,206,0.08)',  border: '#3182ce', text: '#2b6cb0' },
  neutral: { bg: 'rgba(113,128,150,0.06)', border: '#a0aec0', text: '#4a5568' },
};

/* Request-level HLS stats across ALL rows — powers the always-visible
   "HLS watch" stat so the user can see the tool is monitoring even at 0 fails.
   Counts only HLS requests that actually passed through the proxy. */
export function hlsRequestStats(rows = []) {
  const isHls = (r) => {
    const u = (r.url || '').toLowerCase();
    const t = r.type || r.requestType || '';
    return /\.m3u8|\.ts(?:[?#]|$)|\.m4s|\.cmf|\/hls\//.test(u) ||
      (t === 'video_content' && /\/media\/.*hls/.test(u)) ||
      !!r.validData?.manifest?.ok;
  };
  const st = (r) => Number(r.status ?? r.responseCode ?? 0);
  let total = 0, failed = 0; const byCode = {};
  for (const r of rows || []) {
    if (!isHls(r)) continue;
    total++;
    const s = st(r);
    if (s >= 400) { failed++; byCode[s] = (byCode[s] || 0) + 1; }
  }
  return { total, failed, byCode };
}

/* ─── capture completeness (read-only) ───────────────────────────
   A request's response is "captured" only when the proxy recorded an HTTP
   status. status 0 / null / undefined = the request was seen but its response
   was never captured (TLS drop, proxy reconnect, body stripped on history
   reload). A green check sitting on such a request is NOT actually confirmed,
   so we surface this so the scan isn't mistaken for complete. Purely
   informational — never changes fill/bid/sign-off verdicts. */
export const responseSeen = (r) => {
  if (!r) return false;
  const s = Number(r.status ?? r.responseCode ?? 0);
  return Number.isFinite(s) && s >= 100;
};

// Summarise capture across the lifecycle REQUEST rows of a session (rows that
// are expected to return a response — bids, registration, GAM video). Pure
// fire-and-forget beacons are excluded: a 0-status pixel is normal for them.
function captureSummary(keyRows = []) {
  const roleOf = (r) => {
    const t = (r && (r.type || r.requestType)) || '';
    if (/amazon/.test(t)) return 'APS bid';
    if (/springserve_ad_request/.test(t)) return 'SS C2S bid';
    if (t === 'ima') return 'IMA registration';
    if (/gam_video/.test(t)) return 'GAM video';
    if (/springserve_vast/.test(t)) return 'SS VAST';
    return t || 'request';
  };
  const rows = keyRows.filter(Boolean);
  const uncaptured = rows.filter(r => !responseSeen(r));
  return {
    total: rows.length,
    captured: rows.length - uncaptured.length,
    uncaptured: uncaptured.length,
    rows: uncaptured.map(r => ({ row: r, role: roleOf(r), status: Number(r.status ?? r.responseCode ?? 0) })),
    complete: uncaptured.length === 0,
  };
}

/* ═══════════════════════════════════════════════════════════════
   buildVideoSessions
   Returns two session types:
     { sessionType: 'dai_stream', ... }   — IMA DAI registration + ad breaks
     { sessionType: 'vast',       ... }   — GAM VAST request + one ad
   ═══════════════════════════════════════════════════════════════ */
export function buildVideoSessions(allRows) {
  const rows   = (allRows || []).filter(r => ts(r) > 0);
  const sorted = [...rows].sort((a, b) => ts(a) - ts(b));
  const byType = (type) => sorted.filter(r => (r.type || r.requestType) === type);

  const gamVideoRows    = byType('gam_video');
  const amazonVideoRows = byType('amazon_video');
  const ssAdRequestRows = byType('springserve_ad_request');
  const imaRows         = byType('ima');
  const ssVastRows      = byType('springserve_vast');
  const vastTrackRows   = byType('vast_tracking');
  const vastErrorRows   = byType('vast_error');
  // FreeWheel (Comcast MRM) — client-side VAST/VMAP ad server. The get-ads request
  // (freewheel_ad) carries the parsed pod; its callbacks (freewheel_tracking) carry
  // the quartile events (cn=). Its own session flow, no APS/SS/IMA chain.
  const freewheelAdRows = byType('freewheel_ad');
  const fwTrackRows     = byType('freewheel_tracking');
  // Content API calls (per-pack contentApis.contentHosts) — used to evaluate
  // each app pack's brandRules (content-flag → required/forbidden IU segment).
  const contentMetaRows = byType('video_content');
  // OMID viewability/measurement events for video (ad verification).
  const omidSessionRows = byType('omid_session');
  const omidVideoRows   = byType('omid_video');
  const omidImpRows     = byType('omid_impression');
  const omidMeasureRows = byType('omid_measurement');

  // Parsed HLS/DASH manifests — attached by the proxy to 'ima' rows (Google DAI)
  // and 'video_content' rows (e.g. a publisher's VOD HLS for SpringServe SSAI).
  const manifestRows = sorted.filter(r => r.validData?.manifest?.ok);
  const nearestManifest = (refTs) => {
    let best = null, bestDist = Infinity;
    for (const r of manifestRows) {
      const d = ts(r) - refTs;
      if (d < -60_000 || d > 120_000) continue; // manifest fetched around the ad
      // Prefer a master playlist (has the bitrate ladder) over a media playlist.
      const dist = Math.abs(d) - (r.validData.manifest.isMaster ? 30_000 : 0);
      if (dist < bestDist) { bestDist = dist; best = r; }
    }
    return best;
  };

  // Summarise + decode OMID activity in a window around a session anchor.
  // OMID (Open Measurement) measurement runs when the verification script loads,
  // a session starts, an impression registers, and video/geometry events fire.
  // We grade whether measurement actually EXECUTED, not just that a node exists.
  const omidNear = (refTs, wBefore = 12_000, wAfter = 180_000) => {
    const inW = (pool) => pool.filter(r => { const d = ts(r) - refTs; return d >= -wBefore && d <= wAfter; });
    const sess = inW(omidSessionRows), vid = inW(omidVideoRows), imp = inW(omidImpRows), meas = inW(omidMeasureRows);
    const present = (sess.length + vid.length + imp.length + meas.length) > 0;
    // Build an ordered event timeline (script load → session start → impression → video).
    const timeline = [...sess.map(r => ({ t: ts(r), kind: 'sessionStart' })),
                      ...imp.map(r => ({ t: ts(r), kind: 'impression' })),
                      ...vid.map(r => ({ t: ts(r), kind: 'video' })),
                      ...meas.map(r => ({ t: ts(r), kind: 'verificationScript' }))]
      .sort((a, b) => a.t - b.t);
    // Verdict: full = script + session + impression(+video); partial = session only;
    // declared = verification fetched but no session; none.
    const verdict = sess.length > 0
      ? (imp.length > 0 ? 'measured' : 'session-only')
      : (meas.length > 0 ? 'script-only' : 'none');
    return {
      present, verdict,
      sessions: sess.length, videoEvents: vid.length, impressions: imp.length, scripts: meas.length,
      timeline, rows: [...sess, ...vid, ...imp, ...meas],
    };
  };

  // HLS delivery failures — manifest/segment requests that returned 4xx/5xx.
  // A failed ad segment buffers/stalls the ad; a failed manifest breaks the stream.
  const hlsStatusOf = (r) => Number(r.status ?? r.responseCode ?? 0);
  const isHlsReq = (r) => {
    const u = (r.url || '').toLowerCase();
    const t = r.type || r.requestType || '';
    return /\.m3u8|\.ts(?:[?#]|$)|\.m4s|\.cmf|\/hls\//.test(u) ||
      (t === 'video_content' && /\/media\/.*hls/.test(u)) ||
      !!r.validData?.manifest?.ok;
  };
  const hlsFailRows = sorted.filter(r => isHlsReq(r) && hlsStatusOf(r) >= 400);
  const hlsFailNear = (refTs, wBefore = 12_000, wAfter = 180_000) => {
    const rows = hlsFailRows.filter(r => { const d = ts(r) - refTs; return d >= -wBefore && d <= wAfter; });
    const byCode = {};
    rows.forEach(r => { const c = hlsStatusOf(r); byCode[c] = (byCode[c] || 0) + 1; });
    return { count: rows.length, byCode, rows };
  };

  // Only bail when there is NO video ad signal of any kind. Previously this
  // returned early unless a GAM video request or IMA/DAI registration existed —
  // which hid SpringServe-SSAI / direct-VAST flows (no GAM, no IMA) entirely.
  if (!gamVideoRows.length && !imaRows.length && !amazonVideoRows.length &&
      !ssAdRequestRows.length && !ssVastRows.length && !vastTrackRows.length &&
      !freewheelAdRows.length) return [];

  const claimed = new Set();
  const claim   = (r) => { if (r) claimed.add(r._k ?? r.id); };

  const nearest = (pool, refTs, wBefore, wAfter = 0, skipClaimed = true) => {
    let best = null, bestDist = Infinity;
    for (const r of pool) {
      const key = r._k ?? r.id;
      if (skipClaimed && claimed.has(key)) continue;
      const d = ts(r) - refTs;
      if (d < -wBefore || d > wAfter) continue;
      const dist = Math.abs(d);
      if (dist < bestDist) { bestDist = dist; best = r; }
    }
    return best;
  };

  const inWindow = (pool, refTs, wBefore, wAfter, skipClaimed = true) =>
    pool.filter(r => {
      if (skipClaimed && claimed.has(r._k ?? r.id)) return false;
      const d = ts(r) - refTs;
      return d >= -wBefore && d <= wAfter;
    });

  /* helper: analyse bid token pass-through */
  const bidAnalysis = (amazonRow, ssC2sRow, imaExpanded) => {
    const hasAmzn = imaExpanded?.hasAmznBid;
    const hasSs   = imaExpanded?.hasSsBid;
    const src     = imaExpanded?.custParamSource || 'none';
    // APS is only 'bid' when the response contains actual bid tokens in at least one slot.
    // An empty APS response (no slots, no ads) is a no-bid — should never flag
    // amzn_b as missing from IMA because there was nothing to forward.
    // Note: APS validData.expanded does NOT have a 'filled' flag — we check slot ads.
    // Three-state bid status: 'bid' | 'nobid' | 'unknown' | 'not_seen'.
    // 'unknown' = the request exists but its response was never captured/parsed
    // (e.g. loaded from history after a proxy reconnect, body stripped). We must
    // NOT default an unparsed response to 'bid' — that falsely shows "bid ✓" on
    // sessions where we simply can't see the response.
    const amznParsed = !!(amazonRow?.validData?.expanded || amazonRow?.validData?.bidOutcome);
    const amznSlots = amazonRow?.validData?.expanded?.slots;
    const amznStatus = !amazonRow ? 'not_seen'
      : !amznParsed ? 'unknown'
      : (Array.isArray(amznSlots) && amznSlots.some(s => s.ads?.some(a => a.bid)) ? 'bid' : 'nobid');

    const ssParsed = !!(ssC2sRow?.validData?.bidResponse || ssC2sRow?.validData?.ortb);
    const ssHasBid = !!(ssC2sRow?.validData?.bidResponse?.topBid) || ssC2sRow?.validData?.ortb?.filled === true;
    const ssStatus = !ssC2sRow ? 'not_seen'
      : !ssParsed ? 'unknown'
      : ssHasBid ? 'bid'
      : 'nobid';
    const amznExpected = amazonRow && amznStatus === 'bid';
    const ssExpected   = ssC2sRow  && ssStatus   === 'bid';
    return {
      amazon: { row: amazonRow, status: amznStatus, passed: hasAmzn, expected: amznExpected },
      ss:     { row: ssC2sRow,  status: ssStatus,   passed: hasSs,   expected: ssExpected   },
      tokensOk: !(amznExpected && !hasAmzn) && !(ssExpected && !hasSs),
      custParamSource: src,
      amznMissing: amznExpected && !hasAmzn,
      ssMissing:   ssExpected   && !hasSs,
    };
  };

  /* helper: build ad break from one SS VAST row (client-side VAST flow) */
  const buildBreakFromVast = (vastRow, streamTs) => {
    const vastTs  = ts(vastRow);
    const vv      = vastRow.validData?.vast || {};
    const beacons = inWindow(vastTrackRows, vastTs, 0, 30 * 60_000);
    const errors  = inWindow(vastErrorRows, vastTs, 0, 60_000);
    beacons.forEach(claim); errors.forEach(claim);
    const firedQuartiles = new Set(
      beacons.map(r => r.validData?.event?.toLowerCase()).filter(Boolean)
    );
    const creative = vv.creative || null;
    const quartileTimes = quartileTimeline(beacons);
    const timing = computeBreakTiming(vastTs, quartileTimes, creative?.durationSeconds);
    return {
      _k: vastRow._k ?? vastRow.id,
      row: vastRow,
      timestamp: vastTs,
      delta: vastTs - streamTs,
      filled: vv.filled ?? null,
      type: vv.type || null,
      duration: vv.duration || null,
      firedQuartiles,
      quartileTimes,
      timing,
      creative,
      vastType: vv.type || null,
      wrapperDepth: vv.wrapperDepth || 0,
      ...detectSkip(beacons, creative),
      beacons,
      errors,
      source: 'vast',
    };
  };

  /* helper: build ad break from a vast_tracking 'start' event (SSAI/DAI flow).
   * In server-side SSAI the VAST is fetched server-side — client only sees
   * the tracking beacons. A 'start' beacon = ad filled and playing.
   * The SS tracking URL carries auction metadata: dtid, a_cc, _rcc. */
  const buildBreakFromTracking = (startRow, streamTs, windowMs = 30 * 60_000) => {
    const startTs  = ts(startRow);
    const rawP     = startRow.validData?.rawParams || {};
    // Collect all beacons for this break: events within `windowMs` after start,
    // but stop before the next 'start' event (handled by claimed set + windowMs cap).
    const beacons  = [startRow, ...inWindow(vastTrackRows, startTs, 0, windowMs)];
    const errors   = inWindow(vastErrorRows, startTs, 0, 60_000);
    beacons.forEach(claim); errors.forEach(claim);
    const firedQuartiles = new Set(
      beacons.map(r => r.validData?.event?.toLowerCase()).filter(Boolean)
    );
    const quartileTimes = quartileTimeline(beacons);
    // Auction winner signals from the SS impression/tracking beacon
    // Prefer the pre-parsed auctionWin object if proxy already extracted it,
    // otherwise fall back to reading directly from rawParams.
    const aw            = startRow.validData?.auctionWin || {};
    const dealId        = aw.dealId      || rawP.dtid        || null;   // SpringServe deal/tag ID
    const creativeChain = aw.chain       || rawP.a_cc        || null;   // rs.N-s.N-d.N
    const buyerRef      = aw.buyerRef    || rawP._rcc        || null;   // bs.N buyer seat ref
    const gamAdUnit     = aw.gamAdUnit   || rawP.gam_ad_unit || null;
    const contentId     =                   rawP.content_id  || null;
    // Ad break DURATION = the AD's own length, NOT the content length. SS beacons
    // may carry it as ad_len/ad_duration/creative_len; content_len is the video,
    // so we keep it separate (contentLength) and never show it as the ad duration.
    const adDurRaw      = rawP.ad_len ?? rawP.ad_duration ?? rawP.creative_len ?? rawP.adlen ?? null;
    const adDurSec      = adDurRaw != null ? parseFloat(adDurRaw) : null;
    const duration      = (adDurSec != null && Number.isFinite(adDurSec) && adDurSec > 0) ? `${Math.round(adDurSec)}s` : null;
    const contentLength = rawP.content_len ? `${Math.round(rawP.content_len)}s` : null;
    // Advertiser / price signals
    const bidPrice      = aw.bidPrice  != null ? aw.bidPrice  : (rawP.bpa != null ? parseFloat(rawP.bpa) : (rawP.bp != null ? parseFloat(rawP.bp) : null));
    const advertiser    = aw.adomain   || rawP.adomain  || null;  // e.g. "gillette.com"
    const creativeId    = aw.creative  || rawP.crid     || null;  // e.g. "2307:27xyt89s"
    const bidFloor      = aw.bidFloor  != null ? aw.bidFloor  : (rawP.bf  != null ? parseFloat(rawP.bf)  : null);
    return {
      _k: startRow._k ?? startRow.id,
      row: startRow,
      timestamp: startTs,
      delta: startTs - streamTs,
      filled: true,    // start beacon = ad is playing = filled
      type: 'linear',
      duration,
      contentLength,
      firedQuartiles,
      quartileTimes,
      timing: computeBreakTiming(streamTs, quartileTimes, adDurSec || null),
      ...detectSkip(beacons, null),
      beacons,
      errors,
      source: 'tracking',
      // Auction metadata
      dealId,
      creativeChain,
      buyerRef,
      gamAdUnit,
      contentId,
      // Winner signals
      bidPrice,
      advertiser,
      creativeId,
      bidFloor,
    };
  };

  const sessions = [];

  /* ── Session boundary map ──────────────────────────────────────────────────
     Build a sorted list of all session anchor timestamps (gam_video + ima).
     Used to cap tracking-event windows so one session can't steal beacons
     that belong to a later session.
  ─────────────────────────────────────────────────────────────────────────── */
  const allSessionAnchors = [
    ...gamVideoRows.map(r => ts(r)),
    ...imaRows.map(r => ts(r)),
    ...freewheelAdRows.map(r => ts(r)),
  ].filter(Boolean).sort((a, b) => a - b);

  // Returns the max ms to look ahead from `refTs` before hitting the next session.
  // Always allow at least 5 minutes so a slow-loading ad still gets its beacons.
  const boundedWindow = (refTs) => {
    const next = allSessionAnchors.find(t => t > refTs + 5_000);
    if (!next) return 30 * 60_000;          // nothing after this → 30 min
    return Math.max(5 * 60_000, next - refTs - 2_000); // at least 5 min
  };

  /* ── Standard VAST sessions (run FIRST so IMA rows get claimed before DAI loop) ── */
  for (const gamRow of gamVideoRows) {
    const gamTs = ts(gamRow);
    const vd    = gamRow.validData || {};

    const amazonRow_v      = nearest(amazonVideoRows, gamTs, 10_000, 2_000);
    const ssC2sRow_v       = nearest(ssAdRequestRows, gamTs, 10_000, 2_000);
    const imaRow_v         = nearest(imaRows,         gamTs, 8_000,  5_000);
    const ssVastRow_v      = nearest(ssVastRows,      gamTs, 1_000,  10_000);
    const contentMetaRow_v = nearest(contentMetaRows, gamTs, 60_000, 0, false);
    // Cap tracking window at the start of the next session so we don't steal beacons
    // from a subsequent DAI or VAST session that starts later in the same history.
    const vastBoundedW = boundedWindow(gamTs);
    const trackingBeacons_v = inWindow(vastTrackRows, gamTs, 0, vastBoundedW);
    const errorBeacons_v    = inWindow(vastErrorRows,  gamTs, 0, Math.min(60_000, vastBoundedW));

    claim(amazonRow_v); claim(ssC2sRow_v); claim(imaRow_v);
    claim(ssVastRow_v); claim(gamRow);
    trackingBeacons_v.forEach(claim); errorBeacons_v.forEach(claim);

    const bids_v = bidAnalysis(amazonRow_v, ssC2sRow_v, imaRow_v?.validData?.expanded);
    const ssFilled_v   = ssVastRow_v?.validData?.vast?.filled ?? null;
    const ssVastType_v = ssVastRow_v?.validData?.vast?.type   || null;
    const ssDuration_v = ssVastRow_v?.validData?.vast?.duration || null;
    const firedQuartiles_v = new Set(
      trackingBeacons_v.map(r => r.validData?.event?.toLowerCase()).filter(Boolean)
    );

    const issues_v = [];
    errorBeacons_v.forEach(r =>
      issues_v.push({ level: 'fail', msg: `VAST error ${r.validData?.errorCode}: ${r.validData?.description}` })
    );
    if (ssFilled_v === false)               issues_v.push({ level: 'warn', msg: 'SpringServe: no fill' });
    if (!imaRow_v)                          issues_v.push({ level: 'warn', msg: 'IMA SSAI POST not captured' });
    if (bids_v.amznMissing)                 issues_v.push({ level: 'warn', msg: `APS bid not forwarded to IMA (src: ${bids_v.custParamSource})` });
    if (bids_v.ssMissing)                   issues_v.push({ level: 'warn', msg: `SS bid not forwarded to IMA (src: ${bids_v.custParamSource})` });
    if (vd.contentType !== 'live' && !vd.vid) issues_v.push({ level: 'warn', msg: 'Missing vid= content ID' });
    (gamRow.issues || []).forEach(i => issues_v.push({ level: i.level || 'warn', msg: i.msg }));

    const hasError_v = issues_v.some(i => i.level === 'fail' || i.level === 'error');
    const hasWarn_v  = issues_v.some(i => i.level === 'warn');

    sessions.push({
      sessionType: 'vast',
      _k: gamRow._k ?? gamRow.id,
      timestamp: gamTs,
      adUnit: vd.adUnit || '?',
      contentType: vd.contentType || 'unknown',
      contentMeta: contentMetaRow_v ? contentMetaRow_v.validData : null,
      adFormat: vd.adFormat || 'unknown',
      correlator: vd.correlator || null,
      vid: vd.vid || null, sz: vd.sz || null, ppos: vd.ppos || null, pod: vd.pod || null,
      bids: bids_v,
      ima:     { row: imaRow_v, tokensOk: bids_v.tokensOk },
      gamVideo: { row: gamRow },
      vast:     { row: ssVastRow_v, filled: ssFilled_v, type: ssVastType_v, duration: ssDuration_v,
                  creative: ssVastRow_v?.validData?.vast?.creative || null,
                  wrapperDepth: ssVastRow_v?.validData?.vast?.wrapperDepth || 0 },
      // Pod structure + creative parsed from the GAM VAST/VMAP response body.
      pods:        gamRow.validData?.vastResponse?.vmap || null,
      gamCreative: gamRow.validData?.vastResponse?.creative || null,
      gamFilled:   gamRow.validData?.vastResponse?.filled ?? null,
      viewability: omidNear(gamTs),
      hlsErrors:   hlsFailNear(gamTs),
      capture:     captureSummary([gamRow, amazonRow_v, ssC2sRow_v, imaRow_v, ssVastRow_v]),
      bidSignals:  { ss: ssC2sRow_v?.validData?.videoOrtb || null, aps: amazonRow_v?.validData?.videoOrtb || null },
      tracking: { beacons: trackingBeacons_v, firedQuartiles: firedQuartiles_v, errors: errorBeacons_v,
                  quartileTimes: quartileTimeline(trackingBeacons_v) },
      issues: issues_v,
      overallStatus: hasError_v ? 'fail' : hasWarn_v ? 'warn' : 'pass',
    });
  }

  /* ── FreeWheel (Comcast MRM) sessions ───────────────────────────────────────
     One session per freewheel_ad get-ads request. The pod + creative are already
     parsed onto validData.vastResponse by the proxy; freewheel_tracking callbacks
     (cn=) supply the quartile timeline. FreeWheel is the VIDEO ad-server anchor (the
     GAM-equivalent of the video path) — its real demand (GAM exchange via _fw_exin,
     direct-sold) is decided server-side and off-wire, so there's no APS/SS/IMA chain
     to show. The one wire-observable upstream is an adjacent SpringServe video bid,
     attached as videoDemand at 'inferred' confidence. bids/ima/vast are left empty
     and the card renders a FreeWheel-specific pipeline (flavor: 'freewheel').
  ─────────────────────────────────────────────────────────────────────────── */
  for (const fwRow of freewheelAdRows) {
    const fwTs = ts(fwRow);
    const vd   = fwRow.validData || {};
    const vr   = vd.vastResponse || null;

    const fwWindow  = boundedWindow(fwTs);
    const fwBeacons = inWindow(fwTrackRows, fwTs, 0, fwWindow);
    claim(fwRow); fwBeacons.forEach(claim);

    const firedQuartiles_fw = new Set(
      fwBeacons.map(r => r.validData?.event?.toLowerCase()).filter(Boolean)
    );

    // Proxy already emits the substantive findings (unsubstituted macro, OMID
    // absent, missing consent, no-fill) on the row — surface them verbatim.
    const issues_fw = (fwRow.issues || []).map(i => ({ level: i.level || 'warn', msg: i.msg }));
    const hasError_fw = issues_fw.some(i => i.level === 'fail' || i.level === 'error');
    const hasWarn_fw  = issues_fw.some(i => i.level === 'warn');

    const slotLabel = vd.siteSectionId || vd.profile || (vd.networkId ? `nw ${vd.networkId}` : '?');

    // Video-demand adjacency (#2): a client-side SpringServe video bid (bidr) that
    // fired just before the FreeWheel request. This is the ONLY wire-observable
    // upstream — FreeWheel's real demand (GAM exchange via _fw_exin, direct-sold) is
    // server-side and invisible. The link is UNPROVABLE: there is no shared token, so
    // it's timing-only → confidence 'inferred', and we never claim() the SS row (a
    // legitimate SSAI session may still own it). FIREWALL (#4): the pool is strictly
    // springserve_ad_request (video bids) — display GAM/APS banner rows can never be
    // pulled in here, so the parallel display lifecycle stays separate.
    let videoDemand = null;
    const ssCand = inWindow(ssAdRequestRows, fwTs, 2500, 0, false)
      .sort((a, b) => ts(b) - ts(a))[0];   // nearest bid within 2.5s before FW
    if (ssCand) {
      const code = Number(ssCand.status ?? ssCand.responseCode ?? 0);
      const bidStatus = code === 204 ? 'no_bid'
        : (code >= 200 && code < 300) ? 'bid'
        : code ? 'error' : 'unknown';
      videoDemand = {
        partner: 'springserve',
        row: ssCand,
        status: bidStatus,        // 'bid' | 'no_bid' | 'error' | 'unknown'
        httpStatus: code || null,
        confidence: 'inferred',   // timing adjacency only — no shared join token
        deltaMs: fwTs - ts(ssCand),
      };
    }

    sessions.push({
      sessionType: 'vast',       // renders through the shared VAST pipeline / filter
      flavor: 'freewheel',       // …but the card & validation branch on this
      videoDemand,               // observed SpringServe video bid (inferred) or null
      _k: fwRow._k ?? fwRow.id,
      timestamp: fwTs,
      adUnit: slotLabel,
      contentType: vd.contentType || 'unknown',
      adFormat: vd.adFormat || 'unknown',
      // FreeWheel identity for the card header.
      freewheel: {
        networkId: vd.networkId || null,
        profile: vd.profile || null,
        siteSectionId: vd.siteSectionId || null,
        contentAssetId: vd.contentAssetId || null,
        omidPartner: vd.omidPartner || null,
        omidVersion: vd.omidVersion || null,
        usPrivacy: vd.usPrivacy || null,
        unsubstitutedMacros: vd.unsubstitutedMacros || [],
        deliveryMode: vd.csai?.deliveryMode || null,     // 'csai' | 'ssai' | 'unknown'
        prd: vd.csai?.conformance || null,               // CSAI PRD grade + findings
      },
      // Null-safe stand-ins so the shared card/validation never dereference undefined.
      bids: { amazon: { row: null, status: 'not_seen' }, ss: { row: null, status: 'not_seen' },
              tokensOk: true, amznMissing: false, ssMissing: false },
      ima:  { row: null, tokensOk: true },
      gamVideo: { row: fwRow },   // "jump to request" targets the FreeWheel get-ads row
      vast: { row: null, filled: null, type: null, duration: null, creative: null, wrapperDepth: 0 },
      // Pod + creative straight off the parsed FreeWheel VMAP/VAST response.
      pods:        vr?.vmap || null,
      gamCreative: vr?.creative || null,
      gamFilled:   vr?.filled ?? null,
      viewability: omidNear(fwTs),
      hlsErrors:   hlsFailNear(fwTs),
      capture:     captureSummary([fwRow]),
      tracking: { beacons: fwBeacons, firedQuartiles: firedQuartiles_fw, errors: [],
                  quartileTimes: quartileTimeline(fwBeacons) },
      issues: issues_fw,
      overallStatus: hasError_fw ? 'fail' : hasWarn_fw ? 'warn' : 'pass',
    });
  }

  /* ── DAI / SSAI streams ─────────────────────────────────────────────────────
     Lifecycle order: APS bid → SS C2S bid → IMA registration → VAST tracking.
     We anchor on IMA (the most reliable unique-per-stream signal), but we chain
     BACKWARD to APS/SS bids and FORWARD to VAST tracking events.
     Session display timestamp = earliest of (APS, SS, IMA) so the card shows
     when the ad request chain actually began, not when the SSAI stream registered.
  ─────────────────────────────────────────────────────────────────────────── */
  if (imaRows.length > 0 || amazonVideoRows.length > 0 || ssAdRequestRows.length > 0) {
    // Helper: derive a canonical stream ID from an IMA row
    const getStreamId = (r) => {
      const vd = r.validData || {};
      return vd.stream_id
        || r.url?.split('?')[0].split('/').filter(Boolean).slice(-2).join('/')
        || 'DAI Stream';
    };

    // Step 1: Group IMA rows into sessions (dedup parallel registrations within 30s).
    // Skip IMA rows already claimed by a standard VAST session above.
    const streamGroups = new Map(); // streamId -> Array<{ imaRow, duplicates[] }>
    for (const imaRow of imaRows) {
      if (claimed.has(imaRow._k ?? imaRow.id)) continue;
      const rawId = getStreamId(imaRow);
      const imaTs = ts(imaRow);
      if (!streamGroups.has(rawId)) streamGroups.set(rawId, []);
      const groups = streamGroups.get(rawId);
      const last   = groups.length > 0 ? groups[groups.length - 1] : null;
      if (last && (imaTs - ts(last.imaRow)) < 30_000) {
        last.duplicates.push(imaRow);
        claim(imaRow);
      } else {
        groups.push({ imaRow, duplicates: [] });
      }
    }

    // Set of all real stream GUIDs across sessions — used to decide whether a
    // DAI impression beacon's GUID belongs to a known session (so the time-window
    // fallback never steals a beacon that already binds to another stream by GUID).
    const allStreamGuids = new Set(
      [...streamGroups.keys()].filter(isRealStreamGuid)
    );

    // Step 2: Build tracking-window cap from IMA timestamps (not bid timestamps —
    // IMA is the most reliable per-stream anchor for the SSAI stream lifecycle).
    const allSessionTimes = [];
    for (const [, grps] of streamGroups) {
      for (const { imaRow: r } of grps) allSessionTimes.push(ts(r));
    }
    allSessionTimes.sort((a, b) => a - b);

    const sessionWindow = (refTs) => {
      const next = allSessionTimes.find(t => t > refTs + 5_000);
      if (!next) return 30 * 60_000;
      // Cap the look-ahead STRICTLY before the next IMA registration — any ad-break
      // beacon that fires after the next registration belongs to THAT session, not
      // this one. Previously this was floored at 60s, so two registrations <60s apart
      // (e.g. back-to-back clip replays ~22s apart) made the earlier session's window
      // overshoot and steal the later registration's ad break → the later card showed
      // a false "no ad — GAM returned no creative" while the earlier card showed an
      // extra filled break. The gap (minus a 2s guard) is the correct ceiling.
      return Math.max(5_000, next - refTs - 2_000);
    };

    for (const [rawId, groups] of streamGroups) for (const { imaRow, duplicates } of groups) {
      const imaTs   = ts(imaRow);
      const vd      = imaRow.validData || {};
      const expanded = vd.expanded || {};

      // Step 3: Chain backward to APS and SS bid requests.
      // Look up to 15s before IMA — bids are requested, then IMA fires once tokens are received.
      // Also allow a small forward window (2s) for bids that arrive slightly after IMA
      // due to parallel/async SDK calls.
      const amazonRow     = nearest(amazonVideoRows, imaTs, 15_000, 2_000);
      const ssC2sRow      = nearest(ssAdRequestRows, imaTs, 15_000, 2_000);
      // Find the most recent content API call before IMA registration (≤60s)
      const contentMetaRow = nearest(contentMetaRows, imaTs, 60_000, 0, false);
      claim(amazonRow); claim(ssC2sRow); claim(imaRow);

      // Step 4: Session display timestamp = earliest of APS, SS, IMA.
      // This makes the card show when the ad chain STARTED (bid phase), not just when
      // IMA registered. Ordering in the session list is based on this timestamp.
      const sessionStartTs = Math.min(
        amazonRow ? ts(amazonRow) : imaTs,
        ssC2sRow  ? ts(ssC2sRow)  : imaTs,
        imaTs
      );

      const bids = bidAnalysis(amazonRow, ssC2sRow, expanded);

      // Derive live vs VOD from the DAI stream-registration URL instead of
      // defaulting to 'live'. /linear|/live = live; /ondemand|/vod = VOD (clip
      // vs full-episode inferred from the IU). The old default mislabeled VOD
      // streams as "Live".
      const imaUrl = (imaRow.url || '').toLowerCase();
      const iuLower = (imaRow.validData?.expanded?.iu || rawId || '').toLowerCase();
      const daiManifest = nearestManifest(imaTs)?.validData?.manifest || imaRow.validData?.manifest || null;
      let contentType = vd.contentType;
      if (!contentType) {
        // Manifest is authoritative (HLS ENDLIST / DASH dynamic); fall back to URL/IU.
        if (daiManifest && daiManifest.isLive === true) contentType = 'live';
        else if (daiManifest && daiManifest.isLive === false) contentType = /clip/.test(iuLower) ? 'clip' : 'full_episode';
        else if (/\/(linear|live)\b/.test(imaUrl) || /\/(go|live)\//.test(iuLower)) contentType = 'live';
        else if (/\/(ondemand|vod)\b/.test(imaUrl)) contentType = /clip/.test(iuLower) ? 'clip' : 'full_episode';
        else contentType = /clip/.test(iuLower) ? 'clip' : /(\/fep\/|episode)/.test(iuLower) ? 'full_episode' : 'unknown';
      }

      // Cap the look-ahead window at the start of the next DAI session so tracking
      // events from a later clip aren't stolen by this session's 30-min window.
      const wAfter = sessionWindow(imaTs);

      // For DAI/SSAI the VAST is fetched server-side — client never sees a VAST request.
      // Ad break anchors — in priority order:
      //   1. SpringServe 'start' beacons  (have full auction data: bid price, creative, chain)
      //   2. DAI 'impression' beacons     (dai.google.com/impression/stream/ — no SS auction data
      //                                    but still confirm an ad played; fire when proxy captures
      //                                    the DAI ping but no SS tracking beacon was seen)
      //   3. Fall back to client-side SS VAST rows (non-DAI or unusual setup)
      const startTrackRows = inWindow(
        vastTrackRows.filter(r => r.validData?.event === 'start'),
        imaTs, 5_000, wAfter
      );
      // DAI impression beacons carry the stream GUID in their URL
      // (.../impression/stream/{guid}/...). Bind beacons to THIS session by exact
      // GUID match first — order-independent and playlist-proof, since same-iu
      // back-to-back clips each get a distinct DAI stream GUID. The time window is
      // used only as a fallback for beacons that carry no GUID, or whose GUID
      // matches no known session (so a beacon that already binds to another stream
      // by GUID can never be stolen by this session's window).
      const daiAll = vastTrackRows.filter(r => r.validData?.event === 'impression' &&
        (r.url || '').includes('dai.google.com'));
      const daiGuidMatched = isRealStreamGuid(rawId)
        ? daiAll.filter(r => daiStreamId(r) === rawId && !claimed.has(r._k ?? r.id))
        : [];
      const daiWindowFallback = inWindow(
        daiAll.filter(r => {
          const g = daiStreamId(r);
          return !g || !allStreamGuids.has(g);   // unGUID'd, or GUID not tied to any session
        }),
        imaTs, 5_000, wAfter
      );
      const daiImpressionRows = [...daiGuidMatched, ...daiWindowFallback];

      let adBreaks;
      if (startTrackRows.length > 0) {
        startTrackRows.forEach(claim);
        adBreaks = startTrackRows
          .map(r => buildBreakFromTracking(r, imaTs))
          .sort((a, b) => b.timestamp - a.timestamp);
      } else if (daiImpressionRows.length > 0) {
        // DAI impression beacons only — no SpringServe auction metadata available.
        // Google DAI fires the impression ping more than once per ad (retries /
        // multiple measurement URLs), so dedupe by stream_ad_id (fall back to a
        // 4s time bucket when it's absent) — otherwise one pre-roll looks like 3 ads.
        daiImpressionRows.forEach(claim);
        const byAd = new Map(); // key -> { row, beacons[] }
        for (const r of [...daiImpressionRows].sort((a, b) => ts(a) - ts(b))) {
          const adId = r.validData?.rawParams?.streamAdId;
          const key = adId || `t:${Math.floor(ts(r) / 4000)}`;
          if (!byAd.has(key)) byAd.set(key, { row: r, beacons: [r] });
          else byAd.get(key).beacons.push(r);
        }
        adBreaks = [...byAd.values()].map(({ row: r, beacons }) => {
          const rawP   = r.validData?.rawParams || {};
          const breakTs = ts(r);
          return {
            _k: r._k ?? r.id,
            row: r,
            timestamp: breakTs,
            delta: breakTs - imaTs,
            filled: true,
            type: 'linear',
            duration: null,
            firedQuartiles: new Set(['impression']),
            beacons,
            errors: [],
            source: 'dai_impression',
            // DAI carries stream_ad_id and network_id but no SS auction data
            streamAdId:  rawP.streamAdId  || null,
            networkId:   rawP.networkId   || null,
            dupePings:   beacons.length,   // duplicate impression pings collapsed
            dealId:      null,
            creativeChain: null,
            buyerRef:    null,
            bidPrice:    null,
            advertiser:  null,
            creativeId:  null,
            bidFloor:    null,
          };
        }).sort((a, b) => b.timestamp - a.timestamp);
      } else {
        // No 'start' beacon and no DAI impression ping — but SpringServe may have
        // fired quartile/complete/impression beacons that still PROVE an ad played
        // (they carry auction data: dtid, a_cc, advertiser, bid price). Anchoring
        // only on 'start' dropped these ads entirely ("no ad breaks captured").
        // Anchor a break on any SpringServe auction beacon, grouped by served-ad id.
        const ssBeacons = inWindow(
          vastTrackRows.filter(r => {
            const vd = r.validData || {};
            return vd.auctionWin || vd.rawParams?.dtid || /springserve\.com/i.test(r.url || '');
          }),
          imaTs, 5_000, wAfter
        );
        if (ssBeacons.length > 0) {
          // One break per distinct served ad (dtid); anchor on its earliest beacon.
          const earliestByAd = new Map();
          for (const r of [...ssBeacons].sort((a, b) => ts(a) - ts(b))) {
            const key = r.validData?.rawParams?.dtid || r.validData?.auctionWin?.dealId || 'ss-ad';
            if (!earliestByAd.has(key)) earliestByAd.set(key, r);
          }
          adBreaks = [...earliestByAd.values()]
            .map(r => buildBreakFromTracking(r, imaTs))
            .sort((a, b) => b.timestamp - a.timestamp);
        } else {
          // Fall back to client-side VAST rows (non-DAI or unusual setup)
          const breakRows = inWindow(ssVastRows, imaTs, 5_000, wAfter);
          breakRows.forEach(claim);
          adBreaks = breakRows
            .map(r => buildBreakFromVast(r, imaTs))
            .sort((a, b) => b.timestamp - a.timestamp);
        }
      }

      // Issues
      const issues = [];
      if (bids.amznMissing) issues.push({ level: 'warn', msg: `APS bid not forwarded to IMA cust_params (src: ${bids.custParamSource})` });
      if (bids.ssMissing)   issues.push({ level: 'warn', msg: `SS bid not forwarded to IMA cust_params (src: ${bids.custParamSource})` });
      if (bids.custParamSource === 'none' && (bids.amazon.expected || bids.ss.expected))
        issues.push({ level: 'warn', msg: 'Could not locate cust_params in IMA request — bid check may be inaccurate' });
      adBreaks.filter(b => b.source === 'vast' && b.filled === false).forEach(() =>
        issues.push({ level: 'warn', msg: 'One or more ad breaks: no fill from SpringServe' })
      );
      adBreaks.forEach(b => b.errors.forEach(r =>
        issues.push({ level: 'fail', msg: `VAST error ${r.validData?.errorCode}: ${r.validData?.description}` })
      ));
      // Pull IMA SDK / SpringServe / IAB validation checks from proxy validation.
      // severity 'error'|'warn' → requirements; 'info' → recommendations
      (imaRow.issues || []).forEach(i => {
        const level = i.level || i.severity || 'warn';
        const msg   = i.msg   || i.details  || i.label || '';
        issues.push({ level, msg, category: i.category, recommendation: i.recommendation, referenceUrl: i.referenceUrl, key: i.key });
      });
      // Also add checks that didn't fire as issues but are info-level recommendations
      // from the full checks list (not in imaRow.issues, those only have non-pass items)
      const imaChecks = imaRow.validData?.summary?.checks || [];
      imaChecks
        .filter(c => c.status === 'info')
        .forEach(c => {
          // Only add if not already in issues
          if (!issues.some(i => i.key === c.key)) {
            issues.push({ level: 'info', msg: c.details || c.label, category: c.category, recommendation: c.recommendation, referenceUrl: c.referenceUrl, key: c.key });
          }
        });

      const hasError = issues.some(i => i.level === 'fail' || i.level === 'error');
      const hasWarn  = issues.some(i => i.level === 'warn');
      const hasInfo  = issues.some(i => i.level === 'info');

      // Prefer the real GAM ad unit as the card title — QA engineers know the IU,
      // not the opaque stream_ad_id fragment (e.g. woreCANU_0Q).
      // Priority: 1) gam_ad_unit from SS tracking beacons (most specific, from actual served ad)
      //           2) iu override set in IMA stream registration (e.g. /4145/FNC.videos/iphone/clip)
      //           3) raw stream URL fragment as last resort
      const displayTitle = adBreaks.find(b => b.gamAdUnit)?.gamAdUnit
        || imaRow.validData?.expanded?.iu
        || rawId;

      const contentMeta = contentMetaRow ? contentMetaRow.validData : null;

      sessions.push({
        sessionType: 'dai_stream',
        _k: imaRow._k ?? imaRow.id,
        // Use earliest bid signal as card timestamp so the session list shows
        // when the ad chain started (APS/SS bid phase), not just when IMA registered.
        timestamp: sessionStartTs,
        imaTs,          // kept separately for sessionWindow and delta calculations
        streamId: rawId,
        displayTitle,
        contentType,
        contentMeta,
        imaRow,
        amazonRow,      // expose for UI — shown as first lifecycle step
        ssC2sRow,       // expose for UI — shown as second lifecycle step
        duplicateRegistrations: duplicates.length,
        bids,
        adBreaks,
        viewability: omidNear(imaTs),
        hlsErrors: hlsFailNear(imaTs),
        capture: captureSummary([imaRow, amazonRow, ssC2sRow]),
        // VAST errors anywhere in the stream window (even when no ad break formed)
        // so no-fill diagnosis can read error codes (303 empty VAST vs 3xx/4xx).
        vastErrors: inWindow(vastErrorRows, imaTs, 12_000, 180_000, false),
        manifest: daiManifest,
        streamManifestUrl: imaRow.validData?.stream_manifest || null,
        bidSignals: { ss: ssC2sRow?.validData?.videoOrtb || null, aps: amazonRow?.validData?.videoOrtb || null },
        issues,
        overallStatus: hasError ? 'fail' : hasWarn ? 'warn' : hasInfo ? 'info' : 'pass',
      });
    }
  }

  /* ── DAI impression recovery pass ────────────────────────────────────────────
     A DAI ad break is proven client-side by the dai.google.com/impression/stream
     beacon. The per-session window above can drop that beacon when the same clip
     is replayed back-to-back (tight windows between registrations). Here we attach
     ANY still-unclaimed DAI impression beacon to the session that owns it — by
     exact stream GUID first (order-independent, playlist-proof), falling back to
     the nearest preceding IMA registration only when the beacon carries no GUID.
     So replays / same-iu playlists don't falsely show "no ad" and the no-ad count
     is precise. Deduped by stream_ad_id so duplicate pings don't add phantom breaks. */
  {
    const daiImpr = vastTrackRows.filter(r =>
      r.validData?.event === 'impression' && (r.url || '').includes('dai.google.com') && !claimed.has(r._k ?? r.id));
    const daiSessions = sessions.filter(s => s.sessionType === 'dai_stream' && s.imaTs);
    for (const r of [...daiImpr].sort((a, b) => ts(a) - ts(b))) {
      const rt = ts(r);
      let best = null;
      // 1) Exact stream-GUID match wins regardless of timing.
      const g = daiStreamId(r);
      if (g) best = daiSessions.find(s => s.streamId === g) || null;
      // 2) Otherwise fall back to the nearest preceding IMA registration by time.
      //    Skip this fallback only when the beacon DOES carry a GUID (it just
      //    matched no session) — a GUID'd beacon must never be glued onto an
      //    unrelated stream by timing.
      if (!best && !g) {
        let bestGap = Infinity;
        for (const s of daiSessions) {
          const gap = rt - s.imaTs;
          if (gap < -5_000 || gap > 180_000) continue;
          if (gap < bestGap) { bestGap = gap; best = s; }
        }
      }
      if (!best) continue;
      claim(r);
      const rawP = r.validData?.rawParams || {};
      best.adBreaks = best.adBreaks || [];
      const sameAd = best.adBreaks.find(b => b.source === 'dai_impression' &&
        ((rawP.streamAdId && b.streamAdId === rawP.streamAdId) || Math.abs(b.timestamp - rt) < 4_000));
      if (sameAd) { sameAd.beacons.push(r); sameAd.dupePings = (sameAd.dupePings || 1) + 1; continue; }
      best.adBreaks.push({
        _k: r._k ?? r.id, row: r, timestamp: rt, delta: rt - best.imaTs,
        filled: true, type: 'linear', duration: null, firedQuartiles: new Set(['impression']),
        beacons: [r], errors: [], source: 'dai_impression',
        streamAdId: rawP.streamAdId || null, networkId: rawP.networkId || null, dupePings: 1,
        dealId: null, creativeChain: null, buyerRef: null, bidPrice: null, advertiser: null, creativeId: null, bidFloor: null,
      });
      best.adBreaks.sort((x, y) => y.timestamp - x.timestamp);
    }
  }

  /* ── SpringServe SSAI / direct-VAST WITHOUT IMA or GAM ───────────────────────
     Some publishers serve video ads via SpringServe directly:
     no Google DAI stream registration and no GAM /gampad/ads video request.
     The only client signals are the SpringServe C2S bid (POST), the VAST
     response (GET), and the vid-io-*.springserve.com tracking beacons. Anchor a
     session on those so the ad doesn't vanish from the Video tab. Uses only
     UNCLAIMED rows, so it never double-counts ads already built above. */
  {
    const isFree = (r) => !claimed.has(r._k ?? r.id);
    const freeStarts = vastTrackRows.filter(r => isFree(r) && r.validData?.event === 'start');
    const freeImpr   = vastTrackRows.filter(r => isFree(r) && r.validData?.event === 'impression');
    const freeVast   = ssVastRows.filter(isFree);
    const freeOther  = vastTrackRows.filter(r => isFree(r) && !['start', 'impression'].includes(r.validData?.event));

    // Break anchors, in priority order. Each anchor becomes one ad break.
    let anchors = [];
    if (freeStarts.length)      anchors = freeStarts.map(r => ({ kind: 'tracking', row: r }));
    else if (freeVast.length)   anchors = freeVast.map(r => ({ kind: 'vast', row: r }));
    else if (freeImpr.length)   anchors = freeImpr.map(r => ({ kind: 'tracking', row: r }));
    else if (freeOther.length)  anchors = [{ kind: 'tracking', row: [...freeOther].sort((a, b) => ts(a) - ts(b))[0] }];

    if (anchors.length) {
      anchors.sort((a, b) => ts(a.row) - ts(b.row));
      // Cluster anchors into sessions by a 5-min gap.
      const SESSION_GAP = 5 * 60_000;
      const clusters = [];
      for (const a of anchors) {
        const last = clusters[clusters.length - 1];
        if (last && ts(a.row) - ts(last[last.length - 1].row) <= SESSION_GAP) last.push(a);
        else clusters.push([a]);
      }

      for (const cluster of clusters) {
        const firstTs = ts(cluster[0].row);
        // If a DAI (IMA) or GAM video session already covers this timeframe, these
        // SpringServe beacons belong to THAT session — don't spawn a duplicate
        // standalone "SSAI" card. The fallback is only for genuinely IMA-less /
        // GAM-less SpringServe SSAI flows. Claim the strays so they don't recur.
        const coveredByDai = imaRows.some(r => Math.abs(ts(r) - firstTs) < 120_000)
          || gamVideoRows.some(r => Math.abs(ts(r) - firstTs) < 120_000);
        if (coveredByDai) { cluster.forEach(a => claim(a.row)); continue; }
        const ssReq   = nearest(ssAdRequestRows, firstTs, 30_000, 5_000);
        const amzReq  = nearest(amazonVideoRows, firstTs, 30_000, 5_000);
        const ssVastA = nearest(ssVastRows, firstTs, 30_000, 5_000, false);
        const contentMetaRow = nearest(contentMetaRows, firstTs, 120_000, 0, false);
        claim(ssReq); claim(amzReq);
        const anchorTs = Math.min(firstTs, ssReq ? ts(ssReq) : firstTs, amzReq ? ts(amzReq) : firstTs);

        const adBreaks = cluster.map((a, i) => {
          claim(a.row);
          if (a.kind === 'vast') return buildBreakFromVast(a.row, anchorTs);
          // cap each break's beacon window at the next anchor so they don't overlap
          const nextTs = cluster[i + 1] ? ts(cluster[i + 1].row) : null;
          const windowMs = nextTs ? Math.max(30_000, nextTs - ts(a.row) - 1_000) : 30 * 60_000;
          return buildBreakFromTracking(a.row, anchorTs, windowMs);
        }).sort((x, y) => y.timestamp - x.timestamp);

        const bids = bidAnalysis(amzReq, ssReq, null);
        const issues = [];
        if (bids.amznMissing) issues.push({ level: 'warn', msg: `APS bid not forwarded (src: ${bids.custParamSource})` });
        if (bids.ssMissing)   issues.push({ level: 'warn', msg: `SS bid not forwarded (src: ${bids.custParamSource})` });
        adBreaks.forEach(b => (b.errors || []).forEach(r =>
          issues.push({ level: 'fail', msg: `VAST error ${r.validData?.errorCode}: ${r.validData?.description}` })));
        const hasError = issues.some(i => i.level === 'fail');
        const hasWarn  = issues.some(i => i.level === 'warn');

        const displayTitle = adBreaks.find(b => b.gamAdUnit)?.gamAdUnit
          || ssVastA?.validData?.vast?.creative?.advertiser
          || adBreaks.find(b => b.advertiser)?.advertiser
          || 'SpringServe SSAI ad';

        const ssaiManifest = nearestManifest(anchorTs)?.validData?.manifest || null;
        // SpringServe SSAI is typically live linear; trust the manifest when present.
        const ssaiContentType = contentMetaRow?.validData?.contentType
          || (ssaiManifest && ssaiManifest.isLive === true ? 'live'
              : ssaiManifest && ssaiManifest.isLive === false ? 'full_episode'
              : 'live');

        sessions.push({
          sessionType: 'dai_stream',
          flavor: 'ssai',            // renders as SSAI, hides the IMA chip
          noIma: true,
          _k: `${cluster[0].row._k ?? cluster[0].row.id}:ssai`,
          timestamp: anchorTs,
          imaTs: anchorTs,
          streamId: displayTitle,
          displayTitle,
          contentType: ssaiContentType,
          contentMeta: contentMetaRow ? contentMetaRow.validData : null,
          imaRow: null,
          amazonRow: amzReq,
          ssC2sRow: ssReq,
          duplicateRegistrations: 0,
          bids,
          adBreaks,
          viewability: omidNear(anchorTs),
          hlsErrors: hlsFailNear(anchorTs),
          capture: captureSummary([amzReq, ssReq]),
          manifest: ssaiManifest,
          streamManifestUrl: null,
          bidSignals: { ss: ssReq?.validData?.videoOrtb || null, aps: amzReq?.validData?.videoOrtb || null },
          issues,
          overallStatus: hasError ? 'fail' : hasWarn ? 'warn' : 'pass',
        });
      }
    }
  }

  const sortedSessions = sessions.sort((a, b) => b.timestamp - a.timestamp);

  /* ── Suppress stale pre-registrations ───────────────────────────
     Some IMA implementations fire 2+ stream registrations for the same
     clip session: one when the player mounts (pre-registration) and one
     when the user actually hits play. These can be minutes apart and have
     different DAI session GUIDs — so the 30s within-stream dedup can't
     catch them.
     Rule: if two DAI sessions share the same normalized IU within 10 min
     and the NEWER one has no ad breaks, mark it as a stale pre-registration
     rather than showing it as a separate session. The older session (which
     has the real playback data) is kept as-is.
  ─────────────────────────────────────────────────────────────────── */
  // IMA SDK fires a stream registration on player mount (pre-registration) then again
  // on actual play. The gap is typically a few seconds up to ~60s. We suppress the
  // earlier empty registration and credit it to the real session.
  //
  // Window is intentionally short (90s) so that back-to-back clips on the same IU
  // (even 2-minute clips) are NEVER merged — each gets its own card.
  const PRE_REG_WINDOW = 90_000; // 90 seconds max between pre-reg and real session
  const normalizeIU = (s) => {
    // Use displayTitle (GAM IU from tracking) or adUnit, normalised to lowercase
    return (s.displayTitle || s.adUnit || '').toLowerCase().replace(/\/+$/, '');
  };

  const suppressed = new Set();
  for (let i = 0; i < sortedSessions.length; i++) {
    const newer = sortedSessions[i];
    if (newer.sessionType !== 'dai_stream') continue;
    if (newer.adBreaks && newer.adBreaks.length > 0) continue; // has ad data — keep it
    // Also keep empty sessions that have at least a bid row — they represent real
    // sessions where ad break beacons just weren't captured yet.
    if (newer.amazonRow || newer.ssC2sRow || newer.imaRow) {
      // Only suppress if the gap to the real session is very small (genuine pre-reg)
    }
    const newerIU = normalizeIU(newer);
    if (!newerIU) continue;
    // Look for an adjacent session (newer OR older) with the same IU within 90s that has ad breaks.
    // Sorted desc, so j > i means older timestamps. But pre-reg can appear either side
    // depending on whether sessionStartTs pulled the real session earlier via APS/SS bids.
    for (let j = 0; j < sortedSessions.length; j++) {
      if (j === i) continue;
      const other = sortedSessions[j];
      if (other.sessionType !== 'dai_stream') continue;
      if (!(other.adBreaks && other.adBreaks.length > 0)) continue;
      if (normalizeIU(other) !== newerIU) continue;
      const gap = Math.abs(newer.timestamp - other.timestamp);
      if (gap > PRE_REG_WINDOW) continue;
      // Only suppress if the empty session has NO bid signals of its own —
      // if it captured APS/SS/IMA rows it was a real (if ad-break-less) session.
      const hasBidSignal = !!(newer.imaRow || newer.amazonRow || newer.ssC2sRow);
      if (hasBidSignal && gap > 10_000) continue; // >10s apart with its own bids = distinct session
      suppressed.add(newer._k);
      other.preRegistrationCount = (other.preRegistrationCount || 0) + 1;
      break;
    }
  }

  return sortedSessions.filter(s => !suppressed.has(s._k));
}

/* ═══════════════════════════════════════════════════════════════
   expertDiagnostics
   Deep, industry-standard video-adtech problem detection. Encodes
   VAST/VMAP/IMA/DAI/OMID best practices as automated flags so QA
   engineers don't have to remember every spec rule. Returns an array
   of { key, label, status, detail, noTicket } that runVideoValidation
   folds into the checklist (and therefore the sign-off report).
   ═══════════════════════════════════════════════════════════════ */
function parseOffsetSec(v) {
  if (v == null) return null;
  const s = String(v);
  const hms = s.match(/(\d{1,2}):(\d{2}):(\d{2})/);
  if (hms) return (+hms[1]) * 3600 + (+hms[2]) * 60 + (+hms[3]);
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

export function expertDiagnostics(session) {
  const out = [];
  const add = (key, label, status, detail, noTicket = false) => out.push({ key, label, status, detail, noTicket });

  const isDai = session.sessionType === 'dai_stream';
  const breaks = session.adBreaks || [];
  const filledBreaks = breaks.filter(b => b.filled !== false);
  const creative = breaks.find(b => b.creative)?.creative || session.gamCreative || session.vast?.creative || null;
  const wrapperDepth = session.vast?.wrapperDepth || breaks.find(b => b.wrapperDepth)?.wrapperDepth || 0;
  const manifest = session.manifest || null;

  /* ── Wrapper chain depth (IAB: keep ≤5; deep chains add latency + 301/302 timeout risk) ── */
  if (wrapperDepth > 5)
    add('diag_wrapper_depth', 'VAST wrapper chain too deep', 'warn',
      `${wrapperDepth} wrapper redirects — IAB recommends ≤5; deep chains raise latency and VAST 301/302 timeout risk`);
  else if (wrapperDepth >= 3)
    add('diag_wrapper_depth', 'VAST wrapper chain depth', 'info',
      `${wrapperDepth} wrapper redirects — within limits but watch latency`, true);

  if (creative) {
    /* ── No progressive MP4 rendition (HLS/DASH-only or VPAID-only can break some players/SSAI) ── */
    const mfs = creative.mediaFiles || [];
    if (mfs.length > 0 && !creative.vpaid) {
      const hasMp4 = mfs.some(m => (m.mime || '').includes('mp4') || (m.delivery === 'progressive' && /mp4|video/i.test(m.mime || '')));
      if (!hasMp4)
        add('diag_no_mp4', 'No progressive MP4 rendition', 'warn',
          'Creative ships only streaming/VPAID media — some players and SSAI stitchers require a progressive MP4 fallback');
    }

    /* ── Universal Ad ID (VAST 4.x transparency; many SSPs require it) ── */
    if (!creative.universalAdId?.value)
      add('diag_uaid_missing', 'Universal Ad ID missing', 'warn',
        'No <UniversalAdId> in creative — required by VAST 4.x for creative transparency and dedup across the supply chain');

    /* ── OMID ad verification node (no node = no client-side viewability/IVT measurement) ── */
    if (!creative.hasOmid)
      add('diag_omid_node', 'No OMID verification in creative', isDai ? 'info' : 'warn',
        'Creative carries no <AdVerifications>/OMID node — viewability and IVT vendors cannot measure this impression', isDai);

    /* ── AdChoices icon (US self-regulatory requirement for interest-based ads) ── */
    const hasAdChoices = (creative.icons || []).some(i => /adchoices/i.test(i.program || ''));
    if (!hasAdChoices)
      add('diag_adchoices', 'AdChoices icon missing', 'info',
        'No AdChoices <Icon> — required disclosure for interest-based advertising in the US (AboutAds.info)', true);

    /* ── Skip-offset sanity (skip must be reachable before the ad ends) ── */
    if (creative.skippable) {
      const skipSec = parseOffsetSec(creative.skipOffset);
      if (skipSec != null && creative.durationSeconds != null && skipSec >= creative.durationSeconds)
        add('diag_skip_offset', 'Skip offset unreachable', 'warn',
          `skipoffset ${creative.skipOffset} ≥ duration ${creative.durationSeconds}s — viewers can never skip`);
    }

    /* ── Companion-only creative with no linear media ── */
    if ((creative.mediaFileCount || 0) === 0 && (creative.companions?.length || 0) > 0 && !creative.vpaid)
      add('diag_companion_only', 'Companion-only creative', 'warn',
        'Creative has companion(s) but no linear MediaFile — no video will play in the slot');

    /* ── High mobile bitrate (top rendition heavy for cellular) ── */
    const maxBr = mfs.reduce((mx, m) => Math.max(mx, m.bitrate || 0), 0);
    if (maxBr > 4000)
      add('diag_high_bitrate', 'High creative bitrate', 'info',
        `Top rendition ${maxBr}kbps — heavy for mobile/cellular; ensure lower-bitrate renditions exist`, true);
  }

  /* ── Quartile ordering + duplicate beacon anomalies (per filled break) ── */
  for (const b of filledBreaks) {
    const qt = b.quartileTimes || {};
    const present = QUARTILE_ORDER.filter(q => qt[q] != null);
    // out-of-order timestamps
    let outOfOrder = false;
    for (let i = 1; i < present.length; i++) if (qt[present[i]] < qt[present[i - 1]]) outOfOrder = true;
    if (outOfOrder)
      add('diag_quartile_order', 'Quartile beacons out of order', 'warn',
        'Quartile timestamps are non-monotonic — tracking misfire or clock skew; metrics may be unreliable');
    // complete without start = impossible lifecycle
    if (qt.complete != null && qt.start == null)
      add('diag_complete_no_start', 'Complete without start', 'warn',
        'A complete beacon fired but no start beacon — the start tracker is likely missing or blocked');
    // duplicate beacons
    if (b.beacons?.length) {
      const counts = {};
      for (const r of b.beacons) { const e = r.validData?.event?.toLowerCase(); if (e) counts[e] = (counts[e] || 0) + 1; }
      const dupes = Object.entries(counts).filter(([e, n]) => QUARTILE_ORDER.includes(e) && n > 1).map(([e, n]) => `${e}×${n}`);
      if (dupes.length)
        add('diag_dupe_beacons', 'Duplicate quartile beacons', 'warn',
          `Repeated events (${dupes.join(', ')}) — double-counting inflates impression/quartile metrics and causes billing discrepancies`);
    }
    /* ── Bid below floor ── */
    if (b.bidPrice != null && b.bidFloor != null && b.bidPrice < b.bidFloor)
      add('diag_bid_below_floor', 'Bid below floor', 'warn',
        `Winning bid $${b.bidPrice.toFixed(2)} is under floor $${b.bidFloor.toFixed(2)} — auction/flooring misconfiguration`);
  }

  /* ── Manifest / stream-level diagnostics ── */
  if (manifest && manifest.ok) {
    if (manifest.isMaster && manifest.renditionCount === 1)
      add('diag_no_abr', 'No adaptive bitrate ladder', 'warn',
        'Stream master playlist has a single rendition — no ABR; playback cannot adapt to bandwidth changes');
    if ((manifest.adBreakCount || 0) > 0 && filledBreaks.length === 0)
      add('diag_markers_no_fill', 'Ad markers but no ad played', 'warn',
        `Manifest signals ${manifest.adBreakCount} ad break(s) but no ad impression/tracking was captured — slots may be unfilled or not stitched`);
  }

  /* ── HLS delivery failures (4xx/5xx on manifest/segment requests) ── */
  // A failed segment buffers/stalls the ad; a failed manifest can break the whole
  // stream or the ad-stitch. These are real ad-quality signals, distinct from VAST
  // errors (which are auction/creative failures).
  if (session.hlsErrors && session.hlsErrors.count > 0) {
    const codes = Object.entries(session.hlsErrors.byCode)
      .sort((a, b) => b[1] - a[1]).map(([c, n]) => `${c}×${n}`).join(', ');
    const has5xx = Object.keys(session.hlsErrors.byCode).some(c => +c >= 500);
    add('diag_hls_failures', 'HLS delivery failures', has5xx ? 'fail' : 'warn',
      `${session.hlsErrors.count} HLS manifest/segment request(s) returned an error (${codes}) during this stream — ` +
      `${has5xx ? '5xx = origin/CDN failure' : '4xx = missing/forbidden resource'}; causes ad buffering, stalls, or broken ad-stitch`);
  }

  /* ── VMAP pod sanity ── */
  if (session.pods) {
    if (session.pods.prerolls > 1)
      add('diag_dup_preroll', 'Multiple pre-roll pods', 'info',
        `${session.pods.prerolls} pre-roll breaks in one VMAP — usually only one pre-roll is expected`, true);
  }

  /* ── Pod competitive separation (same advertiser served twice) ── */
  // In an ad pod, the same advertiser/brand should not appear twice (competitive
  // separation). Flag when an adomain repeats across the filled breaks of a pod.
  {
    const advs = filledBreaks.map(b => (b.advertiser || '').toLowerCase()).filter(Boolean);
    const counts = {};
    advs.forEach(a => { counts[a] = (counts[a] || 0) + 1; });
    const repeated = Object.entries(counts).filter(([, n]) => n > 1);
    if (repeated.length)
      add('diag_competitive_sep', 'Competitive separation', 'warn',
        `Same advertiser served more than once in this stream: ${repeated.map(([a, n]) => `${a} ×${n}`).join(', ')} — pod competitive separation may be misconfigured`);
  }

  /* ── Consent signalling on C2S bid requests (GDPR/CCPA) ── */
  const ortb = session.bids?.ss?.row?.validData?.ortb;
  if (ortb && ortb.parsed && !ortb.parsed?.regs && !ortb.parsed?.user?.ext?.consent)
    add('diag_consent', 'No consent signal in C2S bid', 'info',
      'SpringServe C2S OpenRTB carries no regs/consent — DSPs may drop demand in GDPR/CCPA regions', true);

  /* ── OpenRTB video-object bid signal checks (from inspectVideoOrtb) ── */
  // The proxy already grades mimes/protocols/plcmt/api/pod fields; fold the
  // fail/warn ones in so they appear in the checklist, flag count, and sign-off.
  const bs = (session.bidSignals?.ss?.isVideo && session.bidSignals.ss)
          || (session.bidSignals?.aps?.isVideo && session.bidSignals.aps) || null;
  if (bs && Array.isArray(bs.checks)) {
    for (const c of bs.checks) {
      if (c.status !== 'fail' && c.status !== 'warn') continue;
      const key = `bidsig_${c.key}`;
      if (!out.some(o => o.key === key)) add(key, `Bid signal: ${c.label}`, c.status, c.detail, c.status === 'warn');
    }
  }

  return out;
}

/* Explains WHY no ad served when a C2S bid was present.
   Important: in Google DAI + SpringServe C2S, the SS bid is only a candidate
   forwarded to GAM via cust_params. GAM runs the auction SERVER-SIDE and that
   decision is NOT in client traffic — so we infer the most probable reason from
   the evidence we can see and point QA to where to confirm in GAM. */
export function diagnoseNoFill(session) {
  const ssBid  = session.bids?.ss?.status === 'bid';
  const apsBid = session.bids?.amazon?.status === 'bid';
  const ssUnknown  = session.bids?.ss?.status === 'unknown';
  const apsUnknown = session.bids?.amazon?.status === 'unknown';
  if (!ssBid && !apsBid) {
    // Can't claim a no-fill when we can't even confirm a bid was returned —
    // the response was never captured (history reload / stripped body).
    if (ssUnknown || apsUnknown)
      return { status: 'info', noTicket: true, unverified: true,
        label: 'Ad result unverified — bid response not captured',
        detail: 'This session was loaded from history after a proxy reconnect, so the SpringServe/APS bid response body was stripped. We cannot confirm whether a bid was returned or whether an ad served — re-run during live capture to verify. (This is a visibility gap, not a confirmed no-fill.)' };
    return null;                                       // genuine no bid → not a "lost serve"
  }

  const who = ssBid ? 'SpringServe' : 'APS';

  // ── 0. Bid never forwarded → GAM never saw it (upstream of the auction) ──
  if ((ssBid && session.bids?.ssMissing) || (apsBid && session.bids?.amznMissing))
    return { status: 'fail', label: 'No ad — bid not forwarded to GAM',
      detail: `${who} bid was not present in the IMA cust_params, so GAM never received it. The auction never saw the bid — fix the cust_params passthrough (this is upstream of the auction, not a GAM loss).` };

  // ── 1. VAST error codes → distinguish empty VAST (303) from creative error/timeout ──
  const errRows = [
    ...(session.adBreaks || []).flatMap(b => b.errors || []),
    ...(session.vastErrors || []),
  ];
  const errCodes = errRows.map(r => Number(r.validData?.errorCode)).filter(Boolean);
  const errDesc  = errRows.map(r => r.validData?.description).filter(Boolean)[0] || '';
  const has303   = errCodes.includes(303);
  const otherErr = errCodes.find(c => c !== 303);

  if (has303 && otherErr == null)
    return { status: 'fail', label: 'No ad — empty VAST (303)',
      detail: `${who}'s bid won but the VAST response carried no <Ad> — IAB error 303 ("No Ads VAST response after one or more Wrappers"). No creative was available at fill time. This is a SELL-SIDE no-fill, not an auction loss — check ${who} demand/backfill for this placement.` };

  if (otherErr != null) {
    const hint = vastErrorHint(otherErr);
    return { status: 'fail', label: `No ad — VAST error ${otherErr}`,
      detail: `${who}'s bid won but a VAST error fired during serving — error ${otherErr}${errDesc ? `: ${errDesc}` : ''}. ${hint} This is a creative/wrapper delivery failure, not an auction loss.` };
  }

  // ── 2. Manifest shows ad markers → DAI stitched an ad, but no impression captured ──
  const m = session.manifest;
  const stitched = !!(m && (m.adBreakCount > 0 || (Array.isArray(m.adMarkers) && m.adMarkers.length > 0)));
  if (stitched)
    return { status: 'warn', label: 'Ad stitched — impression not tracked',
      detail: `The stream manifest shows ${m.adBreakCount || m.adMarkers.length} ad marker(s) — DAI stitched an ad break — but no impression/tracking beacon was captured. The ad most likely PLAYED; this is a measurement gap, not a no-fill. Check OMID/impression beacon delivery and that trackers aren't blocked.` };

  // ── 3. Just registered → pod likely not reached yet ──
  const imaT = session.imaTs || (session.imaRow ? ts(session.imaRow) : null);
  const elapsedSec = imaT ? Math.round((Date.now() - imaT) / 1000) : null;
  if (elapsedSec != null && elapsedSec >= 0 && elapsedSec < 45)
    return { status: 'info', noTicket: true, label: 'No ad yet — pod likely not reached',
      detail: `IMA registered ${elapsedSec}s ago and no impression beacon has arrived. The ad break may simply not have been reached — re-check after playback advances.` };

  // ── 4. No error, nothing stitched → GAM returned no ad (lost auction or filtered) ──
  return { status: 'warn', label: 'No ad — GAM returned no creative',
    detail: `${who} C2S bid was forwarded to IMA, but the manifest shows no stitched ad and no error fired — GAM/DAI served no ad for this break. GAM picks the winner server-side and that decision isn't in client traffic: the ${who} line item either lost to other demand or was filtered (below floor / blocked / capped). These two can only be split with GAM Delivery Inspector or ${who} reporting (join by correlator/stream_id). Verify line-item price-bucket/key-value targeting, status, and priority.` };
}

/* Join keys for manually confirming a no-fill in GAM Delivery Inspector or
   SpringServe reporting (no API needed — paste into the console UI). */
export function buildLookupKeys(session) {
  const ima = session.imaRow;
  const gv  = session.gamVideo?.row;
  const urlOf = (r) => (r && r.url) || '';
  const grab = (re, ...rows) => { for (const r of rows) { const m = urlOf(r).match(re); if (m) return decodeURIComponent(m[1]); } return null; };

  const adUnit     = session.displayTitle || ima?.validData?.expanded?.iu || session.adUnit || null;
  const streamId   = ima?.validData?.stream_id || (typeof session.streamId === 'string' ? session.streamId : null);
  const correlator = ima?.validData?.correlator || session.correlator || grab(/[?&]correlator=([^&]+)/, ima, gv) || null;
  const ssKey      = grab(/ss_request_key=([^&]+)/i, ima, session.ssC2sRow) || grab(/ss_ad_cache_key=([^&]+)/i, ima, session.ssC2sRow) || null;
  const tIso       = session.timestamp ? new Date(session.timestamp).toISOString() : null;

  const obj = { adUnit, streamId, correlator, ssRequestKey: ssKey, timestampUtc: tIso };
  const text = [
    'AdLens FX — no-fill lookup keys',
    adUnit     && `ad unit:        ${adUnit}`,
    streamId   && `stream_id:      ${streamId}`,
    correlator && `correlator:     ${correlator}`,
    ssKey      && `ss_request_key: ${ssKey}`,
    tIso       && `time (UTC):     ${tIso}`,
    session.ssC2sRow?.url && `ss c2s url:     ${session.ssC2sRow.url}`,
  ].filter(Boolean).join('\n');
  return { obj, text };
}

// Plain-language hint for common IAB VAST error codes (4xx serving failures).
function vastErrorHint(code) {
  const map = {
    100: 'XML parsing error — malformed VAST.',
    101: 'VAST schema validation error.',
    102: 'VAST version not supported.',
    300: 'Wrapper general error.',
    301: 'Wrapper timeout — the wrapped VAST URI did not respond in time.',
    302: 'Wrapper redirect limit reached — chain too deep.',
    303: 'No Ads after wrapper (empty VAST).',
    400: 'General linear creative error.',
    401: 'MediaFile not found (404).',
    402: 'MediaFile fetch timeout.',
    403: 'No supported MediaFile — codec/MIME mismatch with the player.',
    405: 'MediaFile decode/display error.',
  };
  return map[code] || 'Creative/wrapper serving failure.';
}

/**
 * WP3d (docs/frontend-validation-port-spec.md §4): mirrors
 * proxy-server/lifecycle/video.js's primaryRequestRow — keep both in
 * lockstep, checked by videoParity.test.js.
 */
function primaryRequestRow(session) {
  if (!session || typeof session !== 'object') return null;
  return session.gamVideo?.row || session.imaRow || session.ssC2sRow || session.amazonRow || null;
}

/**
 * WP3d: mirrors proxy-server/lifecycle/video.js's buildSessionPassport.
 */
function buildSessionPassport(session) {
  const row = primaryRequestRow(session);
  if (!row) return { passport: null, coherence: null, podRealization: null };
  return {
    passport: buildVideoPassport(row),
    coherence: videoCoherence(row),
    podRealization: videoPodRealization(row),
  };
}

/* ═══════════════════════════════════════════════════════════════
   runVideoValidation
   Runs a structured set of named checks against a built session
   object and returns { checks, passed, warned, failed, total }.
   ═══════════════════════════════════════════════════════════════ */
export function runVideoValidation(session) {
  const checks = [];
  // noTicket=true → informational warning; expected/normal condition, no action needed
  const add = (key, label, status, detail = '', noTicket = false) =>
    checks.push({ key, label, status, detail, noTicket });

  const { bids, adBreaks = [], imaRow, sessionType, contentMeta, displayTitle, adUnit } = session;
  const isDai = sessionType === 'dai_stream';

  /* ── FreeWheel: self-contained VAST/VMAP flow, no APS/SS/IMA chain ──────────
     Skip the C2S/IMA checks (which would all read "not seen" and mislead) and run
     FreeWheel-appropriate checks: fill, OMID, consent, unsubstituted macros, and
     the quartile timeline. */
  if (session.flavor === 'freewheel') {
    const fw = session.freewheel || {};

    if (session.gamFilled === true)       add('fw_fill', 'FreeWheel fill', 'pass', 'FreeWheel returned an ad pod');
    else if (session.gamFilled === false) add('fw_fill', 'FreeWheel fill', 'warn', 'FreeWheel returned no ad (empty pod)', true);
    else                                  add('fw_fill', 'FreeWheel fill', 'info', 'Ad response not captured — fill unconfirmed', true);

    if (fw.omidPartner && fw.omidVersion)
      add('fw_omid', 'OMID measurement', 'pass', `OMID active (${fw.omidPartner} ${fw.omidVersion})`);
    else
      add('fw_omid', 'OMID measurement', 'warn', 'No OMID partner/version on the FreeWheel request — open measurement may be off');

    add('fw_consent', 'US Privacy signal', fw.usPrivacy ? 'pass' : 'warn',
        fw.usPrivacy ? `us_privacy=${fw.usPrivacy}` : 'No _fw_us_privacy (CCPA) string on the request', !fw.usPrivacy);

    if ((fw.unsubstitutedMacros || []).length)
      // warn (not fail): the ad can still fill; it's the PAL/limited-ads signaling
      // that's broken. Kept in step with the proxy row's own 'warn' severity so the
      // card border and this check don't disagree.
      add('fw_macros', 'Macro substitution', 'warn',
          `Unsubstituted request macro(s): ${fw.unsubstitutedMacros.join(', ')} — SDK/player never filled them in`, true);
    else
      add('fw_macros', 'Macro substitution', 'pass', 'All request macros substituted');

    const fired = session.tracking?.firedQuartiles || new Set();
    if (fired.size) {
      const missing = QUARTILE_ORDER.filter(q => !fired.has(q));
      add('fw_quartiles', 'Playback quartiles',
          missing.length ? 'warn' : 'pass',
          missing.length ? `Fired ${fired.size}/${QUARTILE_ORDER.length}; missing: ${missing.join(', ')}`
                         : 'All quartile callbacks fired', missing.length > 0);
    } else {
      add('fw_quartiles', 'Playback quartiles', 'info', 'No FreeWheel quartile callbacks captured', true);
    }

    // Context only (info, never gates): the demand picture. The SpringServe bid is a
    // timing-adjacent guess, and the GAM exchange is off-wire — neither is a proven hop.
    const vd = session.videoDemand;
    if (vd) {
      const outcome = vd.status === 'no_bid' ? 'no-bid' : vd.status === 'bid' ? 'bid' : vd.status;
      add('fw_demand', 'Video demand (observed)', 'info',
          `SpringServe video bid ${Math.round(vd.deltaMs)}ms prior → ${outcome} (HTTP ${vd.httpStatus ?? '—'}). Inferred link — no shared token, not proven to feed FreeWheel.`, true);
    }
    // CSAI PRD conformance (TMZ FreeWheel Migration). The proxy already graded the
    // tag against the PRD; surface the rollup + each finding as its own check so the
    // drill-in reads as an AdTag audit, not a generic issue list.
    if (fw.prd) {
      const map = { fail: 'fail', warn: 'warn', info: 'info', pass: 'pass' };
      add('fw_prd', `PRD conformance (${fw.deliveryMode || 'csai'})`,
          map[fw.prd.status] || 'info',
          fw.prd.status === 'pass' ? `Tag conforms to ${fw.prd.spec}`
            : `${fw.prd.findings.filter(f => f.level !== 'info').length} issue(s) vs ${fw.prd.spec}`,
          fw.prd.status !== 'fail');
      for (const f of (fw.prd.findings || [])) {
        add(f.code || 'fw_prd_finding', 'PRD', map[f.level] || 'info', f.msg, f.level !== 'fail');
      }
    }

    const passed = checks.filter(c => c.status === 'pass').length;
    const warned = checks.filter(c => c.status === 'warn').length;
    const failed = checks.filter(c => c.status === 'fail').length;
    // WP3d: mirrors proxy-server/lifecycle/video.js's freewheel-branch return.
    return { checks, passed, warned, failed, total: checks.length, ...buildSessionPassport(session) };
  }

  /* ── 1. C2S bids ─────────────────────────────────────────── */
  const ssStatus  = bids?.ss?.status  || 'not_seen';
  const apsStatus = bids?.amazon?.status || 'not_seen';

  if (ssStatus === 'not_seen')
    add('c2s_ss', 'SS C2S bid', 'warn', 'No SpringServe C2S bid captured — may not be configured for this placement');
  else if (ssStatus === 'unknown')
    // Request was seen but its response was never captured (status 0 / dropped /
    // body stripped). We must NOT show this as a confirmed bid — bid vs no-bid is
    // unverifiable. Reported as info so a green check isn't mistaken for a fact.
    add('c2s_ss', 'SS C2S bid', 'info', 'SS C2S request seen but response not captured — bid/no-bid unconfirmed', true);
  else if (ssStatus === 'nobid')
    add('c2s_ss', 'SS C2S bid', 'warn', 'SS C2S responded with no-bid', true); // no-bid = expected, no ticket
  else
    add('c2s_ss', 'SS C2S bid', 'pass', 'SS C2S bid captured');

  if (apsStatus === 'not_seen')
    add('c2s_aps', 'APS C2S bid', 'warn', 'No APS C2S request captured — verify APS OTT SDK initialisation');
  else if (apsStatus === 'unknown')
    add('c2s_aps', 'APS C2S bid', 'info', 'APS C2S request seen but response not captured — bid/no-bid unconfirmed', true);
  else if (apsStatus === 'nobid')
    add('c2s_aps', 'APS C2S bid', 'warn', 'APS C2S returned no-bid', true); // no-bid = expected, no ticket
  else
    add('c2s_aps', 'APS C2S bid', 'pass', 'APS C2S bid captured');

  // Real IMA stream-registration time (NOT the session card timestamp, which is
  // the earliest of APS/SS/IMA and would mis-measure the ordering below).
  const imaRegTs = imaRow ? ts(imaRow) : null;

  /* ── 2. C2S → IMA timing ──────────────────────────────────── */
  if (bids?.ss?.row && imaRow) {
    const ssDelta = imaRegTs - ts(bids.ss.row); // positive = SS fired before IMA
    if (ssDelta < -500)
      add('c2s_timing', 'C2S → IMA order', 'fail',
        `SS C2S fired ${Math.abs(ssDelta / 1000).toFixed(1)}s AFTER IMA registration — bid tokens cannot reach IMA in time`);
    else
      add('c2s_timing', 'C2S → IMA order', 'pass',
        `SS C2S fired ${Math.max(0, ssDelta / 1000).toFixed(1)}s before IMA registration`);
  }

  /* ── 3. IMA stream registration ───────────────────────────── */
  // SSAI-via-SpringServe (no Google DAI) legitimately has no IMA registration.
  if (isDai && !session.noIma) {
    if (!imaRow)
      add('ima_reg', 'IMA stream registration', 'fail', 'IMA DAI POST not captured — check proxy is intercepting pubads.g.doubleclick.net');
    else
      add('ima_reg', 'IMA stream registration', 'pass', `Registered at ${fmtTime(imaRegTs)}`);
  }

  /* ── 4. Bid token passthrough ─────────────────────────────── */
  if (bids?.ssMissing)
    add('ima_tokens_ss', 'SS bid → IMA cust_params', 'fail', 'SS bid token not present in IMA stream registration cust_params');
  else if (ssStatus === 'bid')
    add('ima_tokens_ss', 'SS bid → IMA cust_params', 'pass', 'SS bid token passed to IMA');
  else if (ssStatus === 'unknown')
    add('ima_tokens_ss', 'SS bid → IMA cust_params', 'info', 'SS bid response not captured — passthrough cannot be verified', true);
  else
    add('ima_tokens_ss', 'SS bid → IMA cust_params', 'warn', 'SS no-bid — passthrough N/A', true); // no-bid = expected, no ticket

  if (bids?.amznMissing)
    add('ima_tokens_aps', 'APS bid → IMA cust_params', 'fail', 'APS bid token (amzn_b) not present in IMA stream registration cust_params');
  else if (apsStatus === 'bid')
    add('ima_tokens_aps', 'APS bid → IMA cust_params', 'pass', 'APS bid token passed to IMA');
  else if (apsStatus === 'unknown')
    add('ima_tokens_aps', 'APS bid → IMA cust_params', 'info', 'APS bid response not captured — passthrough cannot be verified', true);
  else
    add('ima_tokens_aps', 'APS bid → IMA cust_params', 'warn', 'APS no-bid — passthrough N/A', true); // no-bid = expected, no ticket

  /* ── 5. Ad fill ───────────────────────────────────────────── */
  const filledBreaks = adBreaks.filter(b => b.filled !== false);
  const emptyBreaks  = adBreaks.filter(b => b.filled === false);

  const noFillReason = diagnoseNoFill(session);   // why no ad served despite a bid
  if (adBreaks.length === 0) {
    if (noFillReason)
      add('ad_filled', noFillReason.label, noFillReason.status, noFillReason.detail, noFillReason.noTicket);
    else
      add('ad_filled', 'Ad break detected', 'warn', 'No VAST tracking events captured — ad may not have played yet');
  } else if (filledBreaks.length === 0)
    add('ad_filled', 'Ad fill', 'fail',
      noFillReason ? noFillReason.detail
        : `${emptyBreaks.length} break(s) detected but none filled — SpringServe returned no creative`);
  else
    add('ad_filled', 'Ad fill', 'pass',
      `${filledBreaks.length} of ${adBreaks.length} break(s) filled`);

  /* ── 6. Quartile tracking (best filled break) ─────────────── */
  // Pick the break with the most quartiles fired for the completeness check.
  // Prefer non-DAI breaks (which carry full SS quartile data) over DAI-impression-only breaks.
  const nonDaiBreaks = filledBreaks.filter(b => b.source !== 'dai_impression');
  const breakPool    = nonDaiBreaks.length > 0 ? nonDaiBreaks : filledBreaks;
  const bestBreak    = breakPool.length > 0
    ? breakPool.reduce((best, b) => canonicalQuartiles(b) > canonicalQuartiles(best) ? b : best, breakPool[0])
    : null;

  // DAI-impression-only breaks: Google confirmed the ad impression but SpringServe
  // tracking beacons weren't captured (SSAI serves the ad, no client-side VAST URL).
  // In this case quartile events are server-side only — skip the quartile checks and
  // show an info note instead of a false-positive 'start beacon missing' failure.
  const isDaiImpressionOnly = filledBreaks.length > 0 && filledBreaks.every(b => b.source === 'dai_impression');
  if (isDaiImpressionOnly) {
    add('q_start', 'VAST quartile tracking', 'info',
      'DAI SSAI — impression confirmed via dai.google.com beacon; SS quartile events not captured (server-side VAST)');
  } else if (bestBreak) {
    const q           = bestBreak.firedQuartiles;
    const hasStart    = q.has('start');
    const hasQ1       = q.has('firstquartile');
    const hasMid      = q.has('midpoint');
    const hasQ3       = q.has('thirdquartile');
    const hasComplete = q.has('complete');
    const count       = [hasStart, hasQ1, hasMid, hasQ3, hasComplete].filter(Boolean).length;

    if (!hasStart)
      add('q_start', 'VAST start beacon', 'fail', 'js_start not captured — confirm ad actually began playing');
    else
      add('q_start', 'VAST start beacon', 'pass', 'js_start captured');

    if (hasComplete)
      add('q_complete', 'VAST complete beacon', 'pass', 'js_complete — ad played to 100%');
    else if (hasMid)
      add('q_complete', 'VAST complete beacon', 'warn', `Reached midpoint but not complete (${count}/5)`);
    else
      add('q_complete', 'VAST complete beacon', 'warn', `Only ${count}/5 quartile events captured so far`);

    if (count === 5) {
      add('q_all', 'All quartiles in order', 'pass', '▶ → 25% → 50% → 75% → ✓ complete');
    } else {
      const missing = ['start', 'firstquartile', 'midpoint', 'thirdquartile', 'complete']
        .filter(e => !q.has(e))
        .map(e => ({ start: '▶', firstquartile: '25%', midpoint: '50%', thirdquartile: '75%', complete: '✓' }[e]));
      // If start AND complete both fired the ad ran to 100% regardless — the missing
      // intermediary beacons (25%, 75%) are likely just not configured in the SS
      // placement. Downgrade to info so it doesn't inflate the warn count.
      const adRanToCompletion = hasStart && hasComplete;
      add('q_all', 'All quartiles in order',
        adRanToCompletion ? 'info' : 'warn',
        adRanToCompletion
          ? `${count}/5 fired — missing: ${missing.join(', ')} (ad completed; intermediary beacons may not be configured in SS)`
          : `${count}/5 fired — missing: ${missing.join(', ')}`
      );
    }
  }

  /* ── 7. VAST errors ───────────────────────────────────────── */
  const totalErrors = adBreaks.reduce((sum, b) => sum + (b.errors?.length || 0), 0);
  if (totalErrors > 0)
    add('vast_errors', 'VAST errors', 'fail', `${totalErrors} VAST error event(s) fired during playback`);
  else if (adBreaks.length > 0)
    add('vast_errors', 'No VAST errors', 'pass', 'No VAST error events');

  /* ── 8. Auction winner signal ─────────────────────────────── */
  // Skip for DAI-impression-only breaks: no SS beacon = no auction metadata available.
  if (bestBreak && !isDaiImpressionOnly) {
    const { advertiser, bidPrice } = bestBreak;
    if (advertiser || bidPrice != null) {
      const parts = [];
      if (advertiser) parts.push(advertiser);
      if (bidPrice != null) parts.push(`$${bidPrice.toFixed(2)}`);
      add('auction_winner', 'Auction winner signal', 'pass', `Winner: ${parts.join(' · ')}`);
    } else {
      add('auction_winner', 'Auction winner signal', 'warn',
        'No bid price or advertiser domain in SS tracking beacons — check bpa/adomain params');
    }
  } else if (isDaiImpressionOnly) {
    add('auction_winner', 'Auction winner signal', 'info',
      'DAI SSAI mode — auction data comes from server-side VAST; not visible in client traffic');
  }

  /* ── 9. IMA SDK param checks ──────────────────────────────── */
  if (imaRow) {
    const imaChecks = (imaRow.validData?.summary?.checks || [])
      .filter(c => c.status === 'fail' || c.status === 'warn');
    if (imaChecks.length === 0)
      add('ima_sdk', 'IMA SDK requirements', 'pass', 'All IMA SDK param checks passed');
    else {
      const hasFail = imaChecks.some(c => c.status === 'fail');
      const preview = imaChecks.slice(0, 2).map(c => c.label || c.key).join(', ');
      add('ima_sdk', 'IMA SDK requirements', hasFail ? 'fail' : 'warn',
        `${imaChecks.length} issue(s): ${preview}${imaChecks.length > 2 ? '…' : ''}`);
    }
  }

  /* ── 10. Content-flag → IU routing check (pack brandRules) ──── */
  // Generalizes an old hardcoded brand/video-IU check: any App Pack can
  // declare contentApis.brandRules (when a media tag matches X, the video IU
  // must/must-not contain Y). Resolved via the pack matched by this session's
  // own video IU — no brand literals here, see evaluateBrandRule() above and
  // proxy-server/config/apps/README.md's contentApis.brandRules schema.
  if (contentMeta) {
    // Resolve the IU: DAI sessions use displayTitle, VAST sessions use adUnit
    const iu = (isDai ? displayTitle : adUnit) || '';
    const mediaTags = contentMeta.fnMediaTags || [];
    const pack = findPackByIu(iu);
    const brandRules = pack?.contentApis?.brandRules || [];

    if (brandRules.length === 0) {
      // No pack matched, or matched pack declares no brandRules — nothing to check.
    } else {
      for (const rule of brandRules) {
        const evald = evaluateBrandRule(rule, { mediaTags, iu });
        if (!evald) continue;
        const { tagMatched, hasRequired, hasForbidden, mustContain, mustNotContain } = evald;
        const code = rule.code || rule.id || 'brand_rule_iu';
        const label = rule.label || 'Content-flag IU routing';
        const severity = rule.severity || 'warn';

        if (tagMatched) {
          if (hasRequired && !hasForbidden) {
            add(code, label, 'pass', rule.note || rule.message || `Content flag matched and IU correctly routes (IU: ${iu})`);
          } else if (hasForbidden) {
            add(code, label, severity === 'info' ? 'warn' : 'fail',
              rule.message || `Content flag matched but IU uses "${mustNotContain}" — must be "${mustContain}" (IU: ${iu})`);
          } else {
            add(code, label, severity,
              rule.message || `Content flag matched — verify IU uses "${mustContain}" segment (IU: ${iu || '?'})`);
          }
        } else if (mustContain && iu.toLowerCase().includes(mustContain)) {
          // IU routed to the flagged path but the content flag itself didn't match — likely mis-routing.
          add(code, label, 'warn',
            `IU uses "${mustContain}" but content flag did not match this pack's rule — possible mis-routing`);
        }
        // else: no tag match + no forbidden-path routing = correct, no noise
      }
    }
  }

  /* ── 11. Playback timing ──────────────────────────────────── */
  const timedBreak = bestBreak;
  if (timedBreak?.timing) {
    const { bufferMs, stall } = timedBreak.timing;
    if (bufferMs != null) {
      if (bufferMs > 4000)
        add('timing_buffer', 'Ad buffer latency', 'warn',
          `${fmtMs(bufferMs)} from break request to start beacon — slow ad load`);
      else
        add('timing_buffer', 'Ad buffer latency', 'pass', `${fmtMs(bufferMs)} request → start`);
    }
    if (stall)
      add('timing_stall', 'Playback stall', 'warn',
        `Long gap ${stall.from} → ${stall.to}: ${fmtMs(stall.ms)} (expected ~${fmtMs(stall.expectedMs)})`);
  }

  /* ── 12. Creative / media files ───────────────────────────── */
  const creative = timedBreak?.creative || session.vast?.creative || session.gamCreative || null;
  if (creative) {
    if (creative.vpaid)
      add('creative_vpaid', 'VPAID creative', 'warn', 'VPAID detected — deprecated by IAB; OMID + plain VAST media preferred');
    if (creative.mediaFileCount === 0 && !creative.vpaid)
      add('creative_media', 'Media files', 'fail', 'No MediaFile in creative — player has nothing to render');
    else if (creative.mediaFileCount > 0)
      add('creative_media', 'Media files', 'pass', `${creative.mediaFileCount} rendition(s) available`);
    if (creative.universalAdId?.value)
      add('creative_uaid', 'Universal Ad ID', 'pass', `${creative.universalAdId.registry || '?'}: ${creative.universalAdId.value}`);
  }

  /* ── 13. Viewability (OMID) — decoded measurement verdict ─────── */
  const vw = session.viewability;
  if (vw) {
    const detail = `${vw.scripts || 0} script · ${vw.sessions} session · ${vw.impressions} impression · ${vw.videoEvents} video event(s)`;
    if (vw.verdict === 'measured')
      add('omid', 'OMID measurement executed', 'pass', `Viewability measured end-to-end — ${detail}`);
    else if (vw.verdict === 'session-only')
      add('omid', 'OMID measurement incomplete', 'warn', `Session started but no impression registered — measurement may not have completed (${detail})`);
    else if (vw.verdict === 'script-only')
      add('omid', 'OMID measurement incomplete', 'warn', `Verification script loaded but no OMID session started — measurement did not run (${detail})`);
    else if (filledBreaks.length > 0)
      add('omid', 'OMID viewability', 'info',
        'No client-side OMID events captured — verification may run server-side (SSAI) or be disabled', true);
  }

  /* ── 14. Expert video-adtech diagnostics ──────────────────── */
  // Deep spec-based problem detection (wrapper depth, UAID, OMID node,
  // quartile anomalies, ABR, ad-marker fill, bid floor, consent, …).
  for (const d of expertDiagnostics(session)) {
    if (!checks.some(c => c.key === d.key)) add(d.key, d.label, d.status, d.detail, d.noTicket);
  }

  const passed = checks.filter(c => c.status === 'pass').length;
  const warned = checks.filter(c => c.status === 'warn').length;
  const failed = checks.filter(c => c.status === 'fail').length;

  // WP3d: mirrors proxy-server/lifecycle/video.js's main-path return.
  return { checks, passed, warned, failed, total: checks.length, ...buildSessionPassport(session) };
}

/* ═══════════════════════════════════════════════════════════════
   generateBugTicket
   Formats a copy-paste-ready bug ticket (Jira / Linear / GitHub)
   from a single validation check + the session context.
   ═══════════════════════════════════════════════════════════════ */
function generateBugTicket(check, session = {}) {
  const severity = check.status === 'fail' ? 'FAIL' : 'WARN';
  const isDai    = session.sessionType === 'dai_stream';
  const iu       = isDai ? (session.displayTitle || '?') : (session.adUnit || '?');
  const device   = session.imaRow?.deviceLabel || session.imaRow?.devicePlatform || '?';
  const ts       = session.timestamp ? new Date(session.timestamp).toISOString().replace('T', ' ').slice(0, 19) : '?';
  const url      = session.imaRow?.url || '';
  const tags     = session.contentMeta?.fnMediaTags?.join(', ') || '';

  const lines = [
    `[AdTech QA – ${severity}] ${check.label}`,
    '─'.repeat(52),
    '',
    `Severity   : ${severity}`,
    `Check      : ${check.key}`,
    `Session    : ${iu}`,
    `Device     : ${device}`,
    `Time       : ${ts}`,
    '',
    'OBSERVED',
    check.detail || '(see validation checklist)',
    '',
    'EXPECTED',
    getTicketContext(check.key).expected,
    '',
    ...(tags ? [`CONTENT SIGNAL`, `content media tags: ${tags}`, ''] : []),
    ...(url ? [`REQUEST`, url.slice(0, 120) + (url.length > 120 ? '…' : ''), ''] : []),
    'STEPS TO REPRODUCE',
    '1. Open the app under test with AdLens FX proxy enabled',
    `2. Navigate to a ${isDai ? 'DAI video stream' : 'video page'} (${iu !== '?' ? iu : 'see session above'})`,
    '3. Let ad play through or interact with the content',
    '4. Open AdLens FX → Video tab → find this session → Lifecycle Validation',
    `5. Check "${check.label}" — expected ${severity === 'FAIL' ? 'pass' : 'no warning'}, got ${severity}`,
    '',
    '─'.repeat(52),
    `Generated by AdLens FX · ${new Date().toISOString().slice(0, 10)}`,
  ];

  return lines.join('\n');
}

/* ═══════════════════════════════════════════════════════════════
   ValidationChecklist
   Renders the structured check results as a two-column grid
   with a score progress bar. Each fail/warn check gets a
   "Copy Ticket" button that copies a pre-filled bug report.
   ═══════════════════════════════════════════════════════════════ */
function ValidationChecklist({ validation, session }) {
  const { checks, passed, warned, failed, total } = validation;
  const [copiedKey, setCopiedKey] = React.useState(null);
  if (!total) return null;

  const pct        = total ? Math.round((passed / total) * 100) : 0;
  const barColor   = failed > 0 ? '#e53e3e' : warned > 0 ? '#dd6b20' : '#38a169';
  const scoreColor = failed > 0 ? '#e53e3e' : warned > 0 ? '#dd6b20' : '#38a169';

  const statusIcon  = (s) => s === 'pass' ? '✓' : s === 'warn' ? '⚠' : s === 'info' ? 'ℹ' : '✗';
  const statusColor = (s) => s === 'pass' ? '#38a169' : s === 'warn' ? '#dd6b20' : s === 'info' ? '#3182ce' : '#e53e3e';

  const copyTicket = (check) => {
    const text = generateBugTicket(check, session);
    navigator.clipboard.writeText(text).then(() => {
      setCopiedKey(check.key);
      setTimeout(() => setCopiedKey(null), 2000);
    }).catch(() => {
      // Fallback for older browsers
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopiedKey(check.key);
      setTimeout(() => setCopiedKey(null), 2000);
    });
  };

  return (
    <div style={{ marginTop: 10 }}>
      {/* Score bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: scoreColor, flexShrink: 0 }}>
          {passed}/{total} checks passed
        </div>
        <div style={{
          flex: 1, height: 5, borderRadius: 3,
          background: 'rgba(0,0,0,0.08)', overflow: 'hidden',
        }}>
          <div style={{
            height: '100%', width: `${pct}%`,
            background: barColor, borderRadius: 3,
            transition: 'width 0.4s ease',
          }} />
        </div>
        {failed > 0 && (
          <span style={{ fontSize: 10, color: '#e53e3e', flexShrink: 0 }}>
            {failed} fail
          </span>
        )}
        {warned > 0 && (
          <span style={{ fontSize: 10, color: '#dd6b20', flexShrink: 0 }}>
            {warned} warn
          </span>
        )}
      </div>

      {/* Check grid — single column so ticket button fits.
         Hierarchy: passing rows are quiet (no fill, faint rule) so warn/fail/info
         visibly stand out. Same row height/padding — density unchanged. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {checks.map(c => {
          // noTicket=true means the warning is informational / expected behaviour (e.g. no-bid)
          const needsTicket = (c.status === 'fail' || c.status === 'warn') && !c.noTicket;
          const isCopied    = copiedKey === c.key;
          const col = statusColor(c.status);
          const isPass = c.status === 'pass';
          const isQuiet = isPass || c.status === 'info';
          const rowBg = isPass ? 'transparent' : c.status === 'info' ? `${col}0a` : `${col}16`;
          const rowBorder = isPass ? 'rgba(160,174,192,0.35)' : c.status === 'info' ? `${col}66` : col;
          return (
            <div
              key={c.key}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 7,
                padding: '5px 8px', borderRadius: 4,
                background: rowBg,
                borderLeft: `3px solid ${rowBorder}`,
              }}
            >
              <span style={{
                fontSize: 10, fontWeight: 800, color: isPass ? `${col}cc` : col,
                flexShrink: 0, lineHeight: 1.7,
              }}>
                {statusIcon(c.status)}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 10.5, fontWeight: isQuiet ? 600 : 700,
                  color: isPass ? 'var(--muted)' : 'var(--ink)',
                  lineHeight: 1.4,
                }}>
                  {c.label}
                </div>
                {c.detail && (
                  <div style={{
                    fontSize: 9.5, color: 'var(--muted)',
                    lineHeight: 1.35, marginTop: 1,
                  }}>
                    {c.detail}
                  </div>
                )}
              </div>
              {needsTicket && (
                <button
                  title="Copy bug ticket to clipboard"
                  onClick={() => copyTicket(c)}
                  style={{
                    flexShrink: 0, marginTop: 1,
                    padding: '2px 7px', borderRadius: 4,
                    border: `1px solid ${isCopied ? '#38a169' : statusColor(c.status)}55`,
                    background: isCopied ? 'rgba(56,161,105,0.15)' : 'var(--card, #fff)',
                    color: isCopied ? '#38a169' : statusColor(c.status),
                    fontSize: 9, fontWeight: 600, cursor: 'pointer',
                    transition: 'all 0.2s',
                    lineHeight: 1.5,
                  }}
                >
                  {isCopied ? '✓ copied' : '📋 ticket'}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Small shared components
   ═══════════════════════════════════════════════════════════════ */

function StatusDot({ status, size = 8 }) {
  const c = status === 'pass' ? '#38a169'
          : status === 'fail' ? '#e53e3e'
          : status === 'warn' ? '#dd6b20'
          : status === 'info' ? '#3182ce'
          : '#a0aec0';
  return <div style={{ width: size, height: size, borderRadius: '50%', background: c, flexShrink: 0 }} />;
}

function Badge({ children, color = '#718096', bg }) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
      background: bg || color + '22', color, flexShrink: 0, lineHeight: 1.5,
    }}>
      {children}
    </span>
  );
}

function BidChip({ label, status, passed, expected, onClick, time }) {
  // status: 'bid' | 'nobid' | 'unknown' | 'not_seen'
  // passed: boolean (made it into IMA)
  const color = status === 'bid' && passed ? '#38a169'
              : status === 'bid' && !passed ? '#dd6b20'
              : status === 'unknown' ? '#d69e2e'   // amber — response not captured, can't confirm
              : status === 'nobid' ? '#a0aec0'
              : '#a0aec0';
  const bg    = color + '18';
  const sub   = status === 'not_seen' ? ''
              : status === 'unknown' ? 'bid?'
              : status === 'nobid' ? 'no bid'
              : passed ? 'bid ✓' : 'bid → missing';
  const titleNote = status === 'unknown'
    ? 'response not captured — cannot confirm a bid (loaded from history after reconnect)'
    : (sub || 'not seen');
  return (
    <div
      onClick={onClick}
      title={`${label}: ${titleNote}${time ? ` @ ${time}` : ''}`}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <div style={{
        padding: '3px 8px', borderRadius: 4, background: bg,
        border: `1px solid ${color}44`, fontSize: 10, fontWeight: 600, color,
      }}>
        {label}
      </div>
      {sub && <div style={{ fontSize: 8.5, color, lineHeight: 1 }}>{sub}</div>}
      {time && <div style={{ fontSize: 7.5, color: 'var(--muted)', lineHeight: 1, marginTop: 1 }}>{time}</div>}
    </div>
  );
}

/* ─── Quartile progress bar ──────────────────────────────────── */
// Segments: 0%──▶──25%──────50%──────75%──────100%
// Each segment fills green when the trailing quartile fires.
// Markers show the label at each quartile boundary.
const Q_POSITIONS = [
  { key: 'start',         pct: 0,   label: '▶'   },
  { key: 'firstquartile', pct: 25,  label: '25%' },
  { key: 'midpoint',      pct: 50,  label: '50%' },
  { key: 'thirdquartile', pct: 75,  label: '75%' },
  { key: 'complete',      pct: 100, label: '✓'   },
];

function QuartileBar({ firedQuartiles }) {
  // Find the furthest fired quartile to determine fill extent
  let fillPct = 0;
  if (firedQuartiles.has('complete'))      fillPct = 100;
  else if (firedQuartiles.has('thirdquartile')) fillPct = 75;
  else if (firedQuartiles.has('midpoint'))      fillPct = 50;
  else if (firedQuartiles.has('firstquartile')) fillPct = 25;
  else if (firedQuartiles.has('start'))         fillPct = 5; // just started

  const barColor   = fillPct === 100 ? '#38a169' : fillPct >= 50 ? '#3182ce' : '#dd6b20';
  const trackColor = 'rgba(160,174,192,0.15)';

  return (
    <div style={{ width: '100%', position: 'relative', paddingBottom: 14 }}>
      {/* Track */}
      <div style={{
        position: 'relative', height: 6, borderRadius: 3,
        background: trackColor, overflow: 'visible',
      }}>
        {/* Fill */}
        <div style={{
          position: 'absolute', left: 0, top: 0, bottom: 0,
          width: `${fillPct}%`, borderRadius: 3,
          background: barColor,
          transition: 'width 0.3s ease',
        }} />

        {/* Quartile markers */}
        {Q_POSITIONS.map(({ key, pct, label }) => {
          const fired = firedQuartiles.has(key);
          return (
            <div
              key={key}
              title={`${label}: ${fired ? 'fired' : 'not fired'}`}
              style={{
                position: 'absolute',
                left: `${pct}%`,
                top: '50%',
                transform: 'translate(-50%, -50%)',
                width: fired ? 10 : 8,
                height: fired ? 10 : 8,
                borderRadius: '50%',
                background: fired ? barColor : 'var(--surface)',
                border: `2px solid ${fired ? barColor : 'rgba(160,174,192,0.4)'}`,
                zIndex: 2,
                boxShadow: fired ? `0 0 0 2px ${barColor}33` : 'none',
                transition: 'all 0.2s ease',
              }}
            />
          );
        })}
      </div>

      {/* Labels below */}
      <div style={{ position: 'relative', height: 12, marginTop: 3 }}>
        {Q_POSITIONS.map(({ key, pct, label }) => {
          const fired = firedQuartiles.has(key);
          return (
            <div
              key={key}
              style={{
                position: 'absolute',
                left: `${pct}%`,
                transform: 'translateX(-50%)',
                fontSize: 8.5,
                fontWeight: fired ? 700 : 400,
                color: fired ? barColor : 'rgba(160,174,192,0.5)',
                whiteSpace: 'nowrap',
                lineHeight: 1,
              }}
            >
              {label}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Card header bar ────────────────────────────────────────── */
function CardHeader({ overallStatus, contentType, formatBadge, title, time, issueCount, infoCount, expanded, onToggle, onJump, jumpLabel }) {
  const ct = CONTENT_TYPE_META[contentType] || CONTENT_TYPE_META.unknown;
  // For header badge coloring, treat 'info' overallStatus like neutral (not red/orange)
  const badgeStatus = overallStatus === 'info' ? 'neutral' : overallStatus;
  return (
    <div
      onClick={onToggle}
      style={{
        display: 'flex', alignItems: 'center', gap: 7,
        padding: '8px 12px', cursor: 'pointer', userSelect: 'none',
      }}
    >
      <StatusDot status={overallStatus} />
      <Badge color={ct.color}>{ct.icon} {ct.label}</Badge>
      {formatBadge && <Badge color="#718096">{formatBadge}</Badge>}
      <span style={{
        fontSize: 11, fontFamily: 'monospace', fontWeight: 600,
        color: 'var(--ink)', flex: 1,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>{title}</span>
      <span style={{ fontSize: 10, color: 'var(--muted)', flexShrink: 0 }}>{time}</span>
      {issueCount > 0 && (
        <span style={{
          fontSize: 10, padding: '2px 6px', borderRadius: 10,
          background: badgeStatus === 'fail' ? '#e53e3e22' : '#dd6b2022',
          color: badgeStatus === 'fail' ? '#e53e3e' : '#dd6b20', flexShrink: 0,
        }}>
          {issueCount} issue{issueCount !== 1 ? 's' : ''}
        </span>
      )}
      {infoCount > 0 && (
        <span style={{
          fontSize: 10, padding: '2px 6px', borderRadius: 10,
          background: 'rgba(49,130,206,0.12)',
          color: '#3182ce', flexShrink: 0,
          title: `${infoCount} best-practice recommendation${infoCount !== 1 ? 's' : ''} — expand for details`,
        }}>
          ℹ {infoCount}
        </span>
      )}
      {overallStatus === 'pass' && issueCount === 0 && infoCount === 0 && (
        <span style={{ fontSize: 10, color: '#38a169', flexShrink: 0 }}>✓ pass</span>
      )}
      {onJump && (
        <span
          onClick={e => { e.stopPropagation(); onJump(); }}
          title={jumpLabel || 'Jump to request'}
          style={{
            fontSize: 10, color: 'var(--muted)', cursor: 'pointer',
            padding: '2px 5px', borderRadius: 3,
            border: '1px solid rgba(160,174,192,0.3)',
          }}
        >↗</span>
      )}
      <span style={{ fontSize: 10, color: 'var(--muted)', flexShrink: 0 }}>
        {expanded ? '▲' : '▼'}
      </span>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   DAI Stream Card
   ═══════════════════════════════════════════════════════════════ */
function DaiStreamCard({ session, onJumpTo, onOpenSummary, expandAll }) {
  const [expanded, setExpanded] = useState(false);
  useEffect(() => { if (expandAll !== undefined) setExpanded(expandAll); }, [expandAll]);
  const validation = useVideoValidation(session);
  const { displayTitle, contentType, overallStatus, bids, adBreaks, imaRow, issues, timestamp, duplicateRegistrations, preRegistrationCount, imaTs, amazonRow, ssC2sRow, viewability, flavor } = session;
  const isSsai = flavor === 'ssai';
  const st = S[overallStatus] || S.neutral;
  const jump = (row) => { if (row && onJumpTo) onJumpTo(row._k ?? row.id, row); };
  const imaRewriteLines = rewriteLinesForRow(imaRow);

  // Separate counts for header badges
  const reqIssues  = (issues || []).filter(i => i.level !== 'info');
  const infoIssues = (issues || []).filter(i => i.level === 'info');

  const filledBreaks = adBreaks.filter(b => b.filled !== false).length;
  const totalBreaks  = adBreaks.length;
  const breaksSummary = totalBreaks === 0 ? null : `${filledBreaks}/${totalBreaks} filled`;

  // Bid economics for the compact slot-card line (SS bid vs rendered winner).
  const _bestBreak = adBreaks.length
    ? adBreaks.reduce((b, x) => (canonicalQuartiles(x) > canonicalQuartiles(b) ? x : b), adBreaks[0])
    : null;
  const _creative = _bestBreak?.creative || session.gamCreative || session.vast?.creative || null;
  const bidEcon = computeBidEconomics(session, _bestBreak, _creative);

  return (
    <div style={{
      border: `1.5px solid ${st.border}`,
      borderRadius: 8, background: st.bg,
      overflow: 'hidden', marginBottom: 8,
    }}>
      <CardHeader
        overallStatus={overallStatus}
        contentType={contentType}
        formatBadge={isSsai ? 'SSAI' : 'DAI'}
        title={displayTitle}
        time={fmtTime(timestamp)}
        issueCount={reqIssues.length}
        infoCount={infoIssues.length}
        expanded={expanded}
        onToggle={() => (onOpenSummary ? onOpenSummary(session) : setExpanded(e => !e))}
        onJump={onOpenSummary ? () => onOpenSummary(session) : (imaRow ? () => jump(imaRow) : null)}
        jumpLabel={onOpenSummary ? 'Open summary in right panel' : 'Jump to IMA stream registration'}
      />

      {/* IU Rewrite on the stream-create request (e.g. ppid swapped in the form body) */}
      {imaRewriteLines.length > 0 && (
        <div data-testid="dai-rewrite" style={{ borderTop: '1px solid var(--line)', padding: '6px 14px' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)' }}>{REWRITE_LABEL}</div>
          <ul style={{ margin: '2px 0 0', paddingLeft: 16, fontSize: 12, color: 'var(--ink)' }}>
            {imaRewriteLines.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}

      {/* Bid setup + break summary — always visible */}
      <div style={{
        borderTop: '1px solid rgba(0,0,0,0.06)',
        padding: '8px 14px',
        display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap',
      }}>
        {/* C2S bid chips */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <BidChip
            label="APS C2S"
            status={bids.amazon.status}
            passed={bids.amazon.passed}
            expected={bids.amazon.expected}
            onClick={bids.amazon.row ? () => jump(bids.amazon.row) : null}
            time={amazonRow ? fmtTime(ts(amazonRow)) : (bids.amazon.row ? fmtTime(ts(bids.amazon.row)) : null)}
          />
          <div style={{ width: 14, height: 1, background: 'rgba(160,174,192,0.3)', alignSelf: 'flex-start', marginTop: 11 }} />
          <BidChip
            label="SS C2S"
            status={bids.ss.status}
            passed={bids.ss.passed}
            expected={bids.ss.expected}
            onClick={bids.ss.row ? () => jump(bids.ss.row) : null}
            time={ssC2sRow ? fmtTime(ts(ssC2sRow)) : (bids.ss.row ? fmtTime(ts(bids.ss.row)) : null)}
          />
          <div style={{ width: 14, height: 1, background: 'rgba(160,174,192,0.3)', alignSelf: 'flex-start', marginTop: 11 }} />
          {isSsai ? (
            /* SSAI flow — no IMA registration; ads come straight from SpringServe */
            <div title="Server-side ad insertion via SpringServe — no IMA/DAI registration"
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <div style={{
                padding: '3px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                background: 'rgba(128,90,213,0.12)', border: '1px solid rgba(128,90,213,0.3)', color: '#6b46c1',
              }}>SSAI</div>
              <div style={{ fontSize: 8.5, color: '#805ad5' }}>SpringServe</div>
            </div>
          ) : (
            /* IMA chip — different semantics: shows stream registration status, not bid pass-through */
            <div
              onClick={imaRow ? () => jump(imaRow) : undefined}
              title={`IMA stream registration${imaTs ? ` @ ${fmtTime(imaTs)}` : ''}`}
              style={{ cursor: imaRow ? 'pointer' : 'default', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}
            >
              <div style={{
                padding: '3px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                background: !imaRow ? 'rgba(160,174,192,0.15)' : bids.tokensOk ? 'rgba(56,161,105,0.12)' : 'rgba(221,107,32,0.12)',
                border: `1px solid ${!imaRow ? 'rgba(160,174,192,0.3)' : bids.tokensOk ? 'rgba(56,161,105,0.3)' : 'rgba(221,107,32,0.3)'}`,
                color: !imaRow ? '#a0aec0' : bids.tokensOk ? '#276749' : '#9c4221',
              }}>
                IMA {imaRow ? (bids.tokensOk ? '✓' : '⚠') : '—'}
              </div>
              <div style={{ fontSize: 8.5, color: !imaRow ? '#a0aec0' : bids.tokensOk ? '#38a169' : '#dd6b20' }}>
                {!imaRow ? 'not seen' : bids.tokensOk ? 'registered' : 'tokens missing'}
              </div>
              {imaTs && <div style={{ fontSize: 7.5, color: 'var(--muted)', lineHeight: 1, marginTop: 1 }}>{fmtTime(imaTs)}</div>}
            </div>
          )}

          {/* Duplicate registration note (parallel, within 30s) */}
          {duplicateRegistrations > 0 && (
            <div title={`IMA SDK sent ${duplicateRegistrations + 1} parallel stream registrations — deduplicated`}
              style={{ fontSize: 9, color: '#a0aec0', alignSelf: 'center', marginLeft: 4 }}>
              ×{duplicateRegistrations + 1}
            </div>
          )}
          {/* Pre-registration note (empty session ≤10 min before this one, same IU) */}
          {preRegistrationCount > 0 && (
            <div
              title={`${preRegistrationCount} earlier empty registration(s) with the same IU suppressed — IMA SDK pre-registered the stream before playback began`}
              style={{ fontSize: 9, color: '#a0aec0', alignSelf: 'center', marginLeft: 4,
                       background: 'rgba(160,174,192,0.1)', padding: '1px 5px', borderRadius: 3 }}>
              +{preRegistrationCount} pre-reg
            </div>
          )}
        </div>

        {/* Bid economics — compact CPM (SS bid → rendered winner) */}
        {bidEcon && (bidEcon.ss || bidEcon.winner) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, alignSelf: 'center', flexWrap: 'wrap' }}
               title={bidEcon.winner?.note || 'SpringServe C2S bid price (from bid response)'}>
            {bidEcon.ss && (
              <span style={{
                fontSize: 9.5, fontWeight: 700, padding: '1px 6px', borderRadius: 3,
                background: 'rgba(49,130,206,0.1)', color: '#2c5282', border: '1px solid rgba(49,130,206,0.25)',
              }}>
                SS ${bidEcon.ss.price.toFixed(2)}
              </span>
            )}
            {bidEcon.ss && bidEcon.ss.adomain && (
              <span title={`Advertiser domain on the SpringServe bid${bidEcon.ss.seat ? ` · seat ${bidEcon.ss.seat}` : ''}`}
                style={{
                  fontSize: 9.5, fontWeight: 600, padding: '1px 6px', borderRadius: 3,
                  background: 'var(--neutral-bg, #f3f4f6)', color: 'var(--muted, #6b7280)',
                  border: '1px solid var(--neutral-border, var(--line))', maxWidth: 150,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                🏷 {bidEcon.ss.adomain}
              </span>
            )}
            {bidEcon.ss && bidEcon.winner && (
              <span style={{ fontSize: 10, color: bidEcon.outcome === 'ss_outbid' ? '#dd6b20' : '#718096' }}>
                {bidEcon.outcome === 'ss_outbid' ? '↘ outbid' : '→'}
              </span>
            )}
            {bidEcon.winner && (
              <span style={{
                fontSize: 9.5, fontWeight: 700, padding: '1px 6px', borderRadius: 3,
                background: bidEcon.winner.confidence === 'inferred' ? 'rgba(221,107,32,0.12)' : 'rgba(56,161,105,0.12)',
                color: bidEcon.winner.confidence === 'inferred' ? '#9c4221' : '#276749',
                border: `1px solid ${bidEcon.winner.confidence === 'inferred' ? 'rgba(221,107,32,0.3)' : 'rgba(56,161,105,0.3)'}`,
              }}>
                {bidEcon.outcome === 'ss_won' ? 'won' : 'winner'} {bidEcon.winner.confidence === 'inferred' ? '≥' : ''}${bidEcon.winner.price.toFixed(2)}
                {bidEcon.winner.confidence === 'inferred' ? ' est' : ''}
              </span>
            )}
          </div>
        )}

        {/* Ad break summary */}
        <div style={{
          marginLeft: 'auto', fontSize: 11,
          color: 'var(--muted)',
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          {(validation.failed > 0 || validation.warned > 0) && (
            <span
              title="Problems flagged by the video adtech diagnostics — expand for detail"
              style={{
                fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10,
                background: validation.failed > 0 ? 'rgba(229,62,62,0.12)' : 'rgba(221,107,32,0.12)',
                color: validation.failed > 0 ? '#e53e3e' : '#dd6b20',
              }}>
              ⚑ {validation.failed + validation.warned} flagged
            </span>
          )}
          {viewability?.present && <ViewabilityChip viewability={viewability} />}
          {totalBreaks === 0 ? (
            (() => {
              const r = diagnoseNoFill(session);
              // Unverified (response not captured) is NOT a no-fill — show it plainly,
              // never prefixed with "no ad".
              if (r?.unverified) {
                return (
                  <span style={{ color: '#d69e2e', fontStyle: 'italic' }} title={r.detail}>
                    ⚠ {r.label}
                  </span>
                );
              }
              const tone = r?.status === 'fail' ? '#e53e3e' : r?.status === 'info' ? 'var(--muted)' : '#dd6b20';
              return (
                <span style={{ color: r ? tone : 'var(--muted)', fontStyle: 'italic' }}
                  title={r ? r.detail : 'No ad breaks captured for this stream'}>
                  {r ? `no ad — ${r.label.replace(/^No ad(?: yet)? — /, '')}` : 'no ad breaks captured'}
                </span>
              );
            })()
          ) : (
            <>
              <span style={{ fontWeight: 600, color: 'var(--ink)' }}>
                {totalBreaks} ad break{totalBreaks !== 1 ? 's' : ''}
              </span>
              <span>·</span>
              <span style={{ color: filledBreaks === totalBreaks ? '#38a169' : '#dd6b20' }}>
                {breaksSummary}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Ad break list */}
      {adBreaks.length > 0 && (
        <div style={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}>
          <div style={{
            padding: '5px 14px',
            fontSize: 10, fontWeight: 600, color: 'var(--muted)',
            textTransform: 'uppercase', letterSpacing: '0.05em',
            background: 'rgba(0,0,0,0.02)',
            display: 'flex', gap: 12,
          }}>
            <span style={{ flex: '0 0 60px' }}>Time</span>
            <span style={{ flex: '0 0 70px' }}>Fill</span>
            <span style={{ flex: '0 0 55px' }}>Duration</span>
            <span style={{ flex: '0 0 40px' }}>Events</span>
            <span style={{ marginLeft: 'auto', color: '#3182ce' }}>Winner</span>
          </div>
          {adBreaks.map((brk, idx) => (
            <AdBreakRow
              key={brk._k || idx}
              brk={brk}
              streamTs={timestamp}
              onJump={() => jump(brk.row)}
            />
          ))}
        </div>
      )}


      {/* Detail (validation, stream-registration params, bid signals, stream
          details, creative) lives in the right-panel summary — click the card. */}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Creative / media / pod / timing / viewability renderers
   ═══════════════════════════════════════════════════════════════ */

function MiniBadge({ children, color = '#718096' }) {
  return (
    <span style={{
      fontSize: 8.5, fontWeight: 700, padding: '1px 5px', borderRadius: 3,
      background: color + '1c', color, whiteSpace: 'nowrap',
    }}>{children}</span>
  );
}

/* Media-file rendition ladder — codec / bitrate / resolution / delivery */
function MediaFilesTable({ mediaFiles = [] }) {
  if (!mediaFiles.length) return null;
  return (
    <div style={{ marginTop: 4 }}>
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr',
        gap: '1px 8px', fontSize: 9, fontWeight: 700,
        color: 'var(--muted)', textTransform: 'uppercase',
        letterSpacing: '0.04em', paddingBottom: 2,
      }}>
        <span>Resolution</span><span>Bitrate</span><span>Codec</span><span>Delivery</span>
      </div>
      {mediaFiles.slice(0, 8).map((mf, i) => (
        <div key={i} style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr',
          gap: '1px 8px', fontSize: 10, fontFamily: 'monospace',
          color: 'var(--ink)', lineHeight: 1.6,
        }}>
          <span>{mf.width && mf.height ? `${mf.width}×${mf.height}` : '—'}</span>
          <span>{mf.bitrate ? `${mf.bitrate}k` : '—'}</span>
          <span>{mf.codec || (mf.mime ? mf.mime.replace('video/', '') : '—')}</span>
          <span>{mf.delivery || '—'}{mf.apiFramework ? ` (${mf.apiFramework})` : ''}</span>
        </div>
      ))}
    </div>
  );
}

/* Full creative detail — the "understand everything in the ad" block */
function CreativeBlock({ creative, label = 'Creative' }) {
  if (!creative) return null;
  const c = creative;
  const rows = [
    c.advertiser    && { k: 'Advertiser',     v: c.advertiser },
    c.adTitle       && { k: 'Ad title',       v: c.adTitle },
    c.creativeId    && { k: 'Creative ID',    v: c.creativeId, mono: true },
    c.adServingId   && { k: 'Ad Serving ID',  v: c.adServingId, mono: true },
    c.universalAdId?.value && { k: 'Universal Ad ID', v: `${c.universalAdId.registry || '?'} · ${c.universalAdId.value}`, mono: true },
    c.category?.value && { k: 'Category', v: `${c.category.value}${c.category.authority ? ` (${c.category.authority})` : ''}` },
    c.durationSeconds != null && { k: 'Duration', v: fmtDur(c.durationSeconds) },
    c.pricing?.value && { k: 'Pricing', v: `${c.pricing.value} ${c.pricing.currency || ''} ${c.pricing.model || ''}`.trim() },
    c.viewableImpression && { k: 'Viewable impression', v: [c.viewableImpression.viewable && 'viewable', c.viewableImpression.notViewable && 'not-viewable', c.viewableImpression.viewUndetermined && 'undetermined'].filter(Boolean).join(' / ') || '—' },
    c.progressTrackers?.length > 0 && { k: 'Progress trackers', v: c.progressTrackers.map(p => p.offset).filter(Boolean).join(', ') || `${c.progressTrackers.length}` },
    c.clickThrough  && { k: 'Click-through', v: c.clickThrough, mono: true },
  ].filter(Boolean);

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{
        fontSize: 9.5, fontWeight: 600, color: 'var(--muted)',
        textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4,
        display: 'flex', alignItems: 'center', gap: 6,
      }}>
        {label}
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {c.skippable && <MiniBadge color="#805ad5">skip {c.skipOffset || ''}</MiniBadge>}
          {c.hasOmid && <MiniBadge color="#3182ce">OMID ✓</MiniBadge>}
          {c.simid && <MiniBadge color="#38a169">SIMID</MiniBadge>}
          {c.vpaid && <MiniBadge color="#dd6b20">VPAID</MiniBadge>}
          {c.hasMezzanine && <MiniBadge color="#3182ce">mezzanine ✓</MiniBadge>}
          {c.hasClosedCaptions && <MiniBadge color="#38a169">CC</MiniBadge>}
          {c.icons?.some(i => /adchoices/i.test(i.program || '')) && <MiniBadge color="#38a169">AdChoices</MiniBadge>}
          {c.companions?.length > 0 && <MiniBadge color="#718096">{c.companions.length} companion</MiniBadge>}
          {c.nonLinearCount > 0 && <MiniBadge color="#718096">{c.nonLinearCount} non-linear</MiniBadge>}
        </div>
      </div>
      {rows.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '2px 10px', fontSize: 10.5 }}>
          {rows.map(({ k, v, mono }) => (
            <React.Fragment key={k}>
              <span style={{ color: 'var(--muted)', lineHeight: 1.6 }}>{k}</span>
              <span style={{ fontFamily: mono ? 'monospace' : 'inherit', fontSize: mono ? 10 : 10.5, color: 'var(--ink)', wordBreak: 'break-all', lineHeight: 1.6 }}>{v}</span>
            </React.Fragment>
          ))}
        </div>
      )}
      <MediaFilesTable mediaFiles={c.mediaFiles} />
    </div>
  );
}

/* VMAP ad-pod structure — pre/mid/post-roll break timeline */
function PodStructure({ pods }) {
  if (!pods || !pods.breaks?.length) return null;
  const posColor = { preroll: '#3182ce', midroll: '#805ad5', postroll: '#dd6b20', unknown: '#718096' };
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontSize: 9.5, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
        Ad pods · {pods.breakCount} break{pods.breakCount !== 1 ? 's' : ''}
        <span style={{ fontWeight: 400, marginLeft: 6 }}>
          ({pods.prerolls} pre · {pods.midrolls} mid · {pods.postrolls} post)
        </span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
        {pods.breaks.map((b, i) => (
          <span key={i} title={`${b.position} · breakType=${b.breakType || '?'}${b.adTagUri ? ' · tag URI' : ''}`}
            style={{
              fontSize: 9.5, fontFamily: 'monospace', padding: '2px 7px', borderRadius: 4,
              background: (posColor[b.position] || '#718096') + '1c',
              color: posColor[b.position] || '#718096',
              border: `1px solid ${(posColor[b.position] || '#718096')}40`,
            }}>
            {b.position} @ {b.timeOffset || '?'}{b.hasInlineVast ? ' ◆' : b.adTagUri ? ' ↗' : ''}
          </span>
        ))}
      </div>
    </div>
  );
}

/* Per-break playback timing strip */
function TimingStrip({ timing }) {
  if (!timing) return null;
  const { bufferMs, playMs, stall } = timing;
  if (bufferMs == null && playMs == null && !stall) return null;
  return (
    <div style={{ display: 'flex', gap: 12, fontSize: 9.5, color: 'var(--muted)', marginTop: 2 }}>
      {bufferMs != null && (
        <span>buffer <b style={{ color: bufferMs > 4000 ? '#dd6b20' : '#38a169' }}>{fmtMs(bufferMs)}</b></span>
      )}
      {playMs != null && <span>played <b style={{ color: 'var(--ink)' }}>{fmtMs(playMs)}</b></span>}
      {stall && <span style={{ color: '#dd6b20' }}>⚠ stall {stall.from}→{stall.to} {fmtMs(stall.ms)}</span>}
    </div>
  );
}

/* HLS/DASH stream details — bitrate ladder + ad markers */
/* ── Creative-rendering diagnostics (read-only) ──────────────────────────────
   Renders the parseManifest render-readiness + provenance analysis. Purely
   descriptive: explains how the HLS/DASH stream is set up, where it came from,
   and whether the creative can play. Never affects fill / bid / sign-off. */
const RENDER_VERDICT_META = {
  renderable: { label: 'Renderable',  color: '#38a169', blurb: 'Stream is set up to play normally' },
  degraded:   { label: 'Degraded',    color: '#dd6b20', blurb: 'Will play but at risk — see reasons' },
  blocked:    { label: "Won't render", color: '#e53e3e', blurb: 'No playable media in this manifest' },
  unknown:    { label: 'Unknown',     color: '#a0aec0', blurb: 'Not enough manifest detail to assess' },
};
const REASON_COLOR = { fail: '#e53e3e', warn: '#dd6b20', info: '#3182ce' };

function RenderReadiness({ render }) {
  if (!render || !render.verdict) return null;
  const meta = RENDER_VERDICT_META[render.verdict] || RENDER_VERDICT_META.unknown;
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontSize: 9.5, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4, display: 'flex', gap: 6, alignItems: 'center' }}>
        Creative rendering
        <MiniBadge color={meta.color}>{meta.label}</MiniBadge>
        <span style={{ fontSize: 9, fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: 'var(--muted)' }}>{meta.blurb}</span>
      </div>
      {render.reasons?.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {render.reasons.map((r, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', fontSize: 10.5, lineHeight: 1.5 }}>
              <span style={{ flexShrink: 0, marginTop: 1, width: 6, height: 6, borderRadius: '50%', background: REASON_COLOR[r.level] || '#718096' }} />
              <span style={{ color: 'var(--ink)' }}>
                <span style={{ fontFamily: 'monospace', fontSize: 9.5, color: REASON_COLOR[r.level] || '#718096', marginRight: 5 }}>{r.code}</span>
                {r.msg}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ fontSize: 10, color: 'var(--muted)' }}>No rendering issues detected — clean bitrate ladder, codecs and segments.</div>
      )}
    </div>
  );
}

function StreamProvenance({ provenance, manifestUrl }) {
  if (!provenance) return null;
  const p = provenance;
  const rows = [
    p.origin && { k: 'Origin host', v: p.origin, mono: true },
    p.cdn && { k: 'CDN', v: p.cdn },
    p.packager && { k: 'Packager / SSAI', v: p.packager },
    { k: 'Delivery', v: p.delivery === 'live' ? 'Live linear' : p.delivery === 'vod' ? 'VOD / on-demand' : 'Unknown' },
    { k: 'Ad insertion', v: p.isStitched ? (p.ssai ? 'Server-side stitched (SSAI)' : 'Stitched — ad markers present') : 'No stitched ad markers seen' },
    p.segmentHosts?.length > 1 && { k: 'Segment hosts', v: p.segmentHosts.join(', '), mono: true },
  ].filter(Boolean);
  if (!rows.length && !manifestUrl) return null;
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontSize: 9.5, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
        Where it came from
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: '2px 10px', fontSize: 10.5 }}>
        {rows.map(({ k, v, mono }) => (
          <React.Fragment key={k}>
            <span style={{ color: 'var(--muted)', lineHeight: 1.6 }}>{k}</span>
            <span style={{ fontFamily: mono ? 'monospace' : 'inherit', fontSize: mono ? 9.5 : 10.5, color: 'var(--ink)', wordBreak: 'break-all', lineHeight: 1.6 }}>{v}</span>
          </React.Fragment>
        ))}
      </div>
      {manifestUrl && (
        <div style={{ fontSize: 9, color: 'var(--muted)', marginTop: 3, fontFamily: 'monospace', wordBreak: 'break-all' }}>
          manifest: {manifestUrl}
        </div>
      )}
    </div>
  );
}

function StreamDetails({ manifest, manifestUrl }) {
  if (!manifest || !manifest.ok) return null;
  const m = manifest;
  const fmtKbps = (bw) => bw ? (bw >= 1000000 ? `${(bw / 1000000).toFixed(1)} Mbps` : `${Math.round(bw / 1000)} kbps`) : '—';
  const markerColor = { 'cue-out': '#dd6b20', 'cue-in': '#38a169', scte35: '#805ad5', interstitial: '#3182ce', daterange: '#718096' };
  const enc = m.encryption;
  return (
    <div style={{ marginTop: 8 }}>
      {/* Render-readiness verdict + provenance (creative-rendering diagnostics) */}
      <RenderReadiness render={m.render} />
      <StreamProvenance provenance={m.provenance} manifestUrl={manifestUrl} />

      <div style={{ fontSize: 9.5, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4, marginTop: 8, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        Stream details
        <MiniBadge color="#3182ce">{m.format.toUpperCase()}{m.isMaster ? ' master' : ' media'}</MiniBadge>
        {m.version != null && <MiniBadge color="#718096">v{m.version}</MiniBadge>}
        {m.renditionCount > 0 && <MiniBadge color="#718096">{m.renditionCount} renditions</MiniBadge>}
        {m.durationSec ? <MiniBadge color="#718096">{fmtDur(m.durationSec)}</MiniBadge> : null}
        {enc?.encrypted && <MiniBadge color="#805ad5">{enc.drm || enc.method || 'encrypted'}</MiniBadge>}
        {m.audioTracks?.length > 0 && <MiniBadge color="#718096">{m.audioTracks.length} audio</MiniBadge>}
        {m.subtitleTracks?.length > 0 && <MiniBadge color="#718096">{m.subtitleTracks.length} cc/sub</MiniBadge>}
      </div>

      {/* Codec summary */}
      {(m.codecSummary?.video?.length > 0 || m.codecSummary?.audio?.length > 0) && (
        <div style={{ fontSize: 9.5, color: 'var(--muted)', marginBottom: 4 }}>
          codecs: {[...(m.codecSummary.video || []), ...(m.codecSummary.audio || [])].join(' · ') || '—'}
        </div>
      )}

      {/* Encryption / DRM */}
      {enc?.encrypted && (
        <div style={{ fontSize: 9.5, color: 'var(--muted)', marginBottom: 4 }}>
          encryption: {enc.method}{enc.drm ? ` · ${enc.drm} DRM` : ' · clear-key'}{enc.uri ? ` · key ${enc.uri.replace(/^https?:\/\//, '').slice(0, 40)}` : ''}
        </div>
      )}

      {/* Audio / subtitle tracks */}
      {m.audioTracks?.length > 0 && (
        <div style={{ fontSize: 9.5, color: 'var(--muted)', marginBottom: 4 }}>
          audio: {m.audioTracks.map(t => [t.name || t.groupId, t.language, t.default ? '(default)' : ''].filter(Boolean).join(' ')).join(', ')}
        </div>
      )}
      {m.subtitleTracks?.length > 0 && (
        <div style={{ fontSize: 9.5, color: 'var(--muted)', marginBottom: 4 }}>
          captions: {m.subtitleTracks.map(t => [t.name || t.groupId, t.language].filter(Boolean).join(' ')).join(', ')}
        </div>
      )}
      {m.initSegment && (
        <div style={{ fontSize: 9, color: 'var(--muted)', marginBottom: 4, fontFamily: 'monospace', wordBreak: 'break-all' }}>
          init segment (CMAF/fMP4): {m.initSegment}
        </div>
      )}

      {/* Bitrate ladder */}
      {m.variants?.length > 0 && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.4fr', gap: '1px 8px', fontSize: 9, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.04em', paddingBottom: 2 }}>
            <span>Resolution</span><span>Bitrate</span><span>Codecs</span>
          </div>
          {m.variants.slice(0, 10).map((v, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.4fr', gap: '1px 8px', fontSize: 10, fontFamily: 'monospace', color: 'var(--ink)', lineHeight: 1.6 }}>
              <span>{v.width && v.height ? `${v.width}×${v.height}` : '—'}{v.frameRate ? ` @${v.frameRate}` : ''}</span>
              <span>{fmtKbps(v.bandwidth)}</span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.codecs || '—'}</span>
            </div>
          ))}
          {m.maxBitrate && (
            <div style={{ fontSize: 9, color: 'var(--muted)', marginTop: 2 }}>
              ladder {fmtKbps(m.minBitrate)} → {fmtKbps(m.maxBitrate)}
            </div>
          )}
        </>
      )}

      {/* Ad markers */}
      {m.adMarkers?.length > 0 && (
        <div style={{ marginTop: 6 }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--muted)', marginBottom: 3 }}>
            AD MARKERS · {m.adBreakCount} break{m.adBreakCount !== 1 ? 's' : ''}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {m.adMarkers.slice(0, 16).map((mk, i) => (
              <span key={i} title={`${mk.type}${mk.id ? ' · ' + mk.id : ''}${mk.duration ? ' · ' + mk.duration + 's' : ''}`}
                style={{ fontSize: 9, fontFamily: 'monospace', padding: '1px 6px', borderRadius: 3, background: (markerColor[mk.type] || '#718096') + '1c', color: markerColor[mk.type] || '#718096', border: `1px solid ${(markerColor[mk.type] || '#718096')}40` }}>
                {mk.type}{mk.duration ? ` ${mk.duration}s` : ''}
              </span>
            ))}
          </div>
        </div>
      )}

      {(m.segmentCount > 0 || m.discontinuityCount > 0 || m.gapCount > 0) && (
        <div style={{ fontSize: 9, color: 'var(--muted)', marginTop: 4 }}>
          {[
            m.segmentCount > 0 && `${m.segmentCount} segments`,
            m.targetDuration && `target ${m.targetDuration}s`,
            m.discontinuityCount > 0 && `${m.discontinuityCount} discontinuit${m.discontinuityCount === 1 ? 'y' : 'ies'}`,
            m.gapCount > 0 && `${m.gapCount} gap${m.gapCount === 1 ? '' : 's'}`,
            m.iframeStreamCount > 0 && `${m.iframeStreamCount} I-frame stream${m.iframeStreamCount === 1 ? '' : 's'}`,
          ].filter(Boolean).join(' · ')}
        </div>
      )}
    </div>
  );
}

/* OpenRTB video-object signals from the C2S bid request (the request-side signals) */
function BidSignals({ bidSignals }) {
  if (!bidSignals) return null;
  const sig = (bidSignals.ss?.isVideo && bidSignals.ss) || (bidSignals.aps?.isVideo && bidSignals.aps) || null;
  if (!sig || !sig.imps?.length) return null;
  const v = sig.imps[0];
  const partner = (bidSignals.ss?.isVideo) ? 'SpringServe' : 'Amazon APS';
  const rows = [
    { k: 'Partner', v: partner },
    v.mimes?.length && { k: 'MIMEs', v: v.mimes.join(', '), mono: true },
    v.protocols?.length && { k: 'VAST protocols', v: v.protocols.map(p => p.label).join(', ') },
    (v.minduration != null || v.maxduration != null) && { k: 'Duration', v: `${v.minduration ?? 0}–${v.maxduration ?? '?'}s` },
    (v.w && v.h) && { k: 'Player size', v: `${v.w}×${v.h}` },
    v.startdelayLabel && { k: 'Position', v: v.startdelayLabel },
    (v.plcmt || v.placement) && { k: 'Placement', v: (v.plcmt || v.placement).label + (v.plcmt ? ' (plcmt)' : ' (legacy)') },
    v.linearity && { k: 'Linearity', v: v.linearity.label },
    v.playbackmethod?.length && { k: 'Playback', v: v.playbackmethod.map(p => p.label).join(', ') },
    v.delivery?.length && { k: 'Delivery', v: v.delivery.map(d => d.label).join(', ') },
    v.api?.length && { k: 'API frameworks', v: v.api.map(a => a.label).join(', ') },
    (v.skip != null) && { k: 'Skip', v: v.skip ? `yes${v.skipafter != null ? ` after ${v.skipafter}s` : ''}` : 'no' },
    v.podid != null && { k: 'Ad pod', v: `podid=${v.podid}${v.slotinpod != null ? ` · slot ${v.slotinpod}` : ''}${v.poddur != null ? ` · ${v.poddur}s` : ''}${v.rqddurs ? ` · rqddurs ${v.rqddurs.join('/')}s` : ''}` },
  ].filter(Boolean);
  const content = sig.content;
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontSize: 9.5, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4, display: 'flex', gap: 6, alignItems: 'center' }}>
        Bid request signals (OpenRTB video)
        {v.hasOmid && <MiniBadge color="#3182ce">OMID</MiniBadge>}
        {v.hasSimid && <MiniBadge color="#38a169">SIMID</MiniBadge>}
        {v.hasVpaid && <MiniBadge color="#dd6b20">VPAID</MiniBadge>}
        {v.podid != null && <MiniBadge color="#805ad5">CTV pod</MiniBadge>}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: '2px 10px', fontSize: 10.5 }}>
        {rows.map(({ k, v: val, mono }) => (
          <React.Fragment key={k}>
            <span style={{ color: 'var(--muted)', lineHeight: 1.6 }}>{k}</span>
            <span style={{ fontFamily: mono ? 'monospace' : 'inherit', fontSize: mono ? 10 : 10.5, color: 'var(--ink)', wordBreak: 'break-word', lineHeight: 1.6 }}>{val}</span>
          </React.Fragment>
        ))}
      </div>
      {content && (content.series || content.title || content.livestream != null) && (
        <div style={{ marginTop: 5, fontSize: 9.5, color: 'var(--muted)' }}>
          content: {[content.series, content.title, content.livestream === 1 ? 'LIVE' : content.livestream === 0 ? 'VOD' : null, content.len ? `${content.len}s` : null].filter(Boolean).join(' · ')}
        </div>
      )}
    </div>
  );
}

/* OMID viewability chip */
function ViewabilityChip({ viewability }) {
  if (!viewability) return null;
  const v = viewability;
  const verdict = v.verdict || (v.present ? 'partial' : 'none');
  const color = verdict === 'measured' ? '#38a169'
    : (verdict === 'session-only' || verdict === 'script-only' || verdict === 'partial') ? '#dd6b20'
    : '#a0aec0';
  const label = verdict === 'measured' ? 'measured'
    : verdict === 'session-only' ? 'session only'
    : verdict === 'script-only' ? 'script only'
    : verdict === 'partial' ? 'partial'
    : '—';
  return (
    <span
      title={v.present
        ? `OMID ${verdict}: ${v.scripts || 0} script · ${v.sessions} session · ${v.impressions} impression · ${v.videoEvents} video event(s)`
        : 'No client-side OMID events captured — viewability not measured'}
      style={{
        fontSize: 9.5, fontWeight: 600, padding: '2px 7px', borderRadius: 4,
        background: color + '1c', color, border: `1px solid ${color}40`, whiteSpace: 'nowrap',
      }}>
      👁 OMID {verdict === 'measured' ? '✓' : ''} {label}
    </span>
  );
}

/* ─── Bid economics ──────────────────────────────────────────────
 * Reconcile the SpringServe C2S bid price (read from the bid RESPONSE) with the
 * creative that actually rendered.
 *
 * For server-side Google DAI VOD the winning clearing price is decided inside
 * Google's ad server and is NOT returned to the client. When we can't read it
 * directly (VAST <Pricing> or the SS win beacon) we fall back to a clearly
 * labelled best-effort estimate so a QA sign-off is never misled by a fabricated
 * exact number.
 *
 * Returns null when there's nothing economic to show, else:
 *   { ss, winner, outcome }
 *   ss     : { price, currency, seat, crid, adomain, dealid } | null  (measured — bid response)
 *   winner : { price, currency, source, confidence, note }    | null
 *   outcome: 'ss_won' | 'ss_outbid' | 'unknown'
 *   confidence: 'measured' (read from traffic) | 'inferred' (estimate)
 */
function computeBidEconomics(session, bestBreak, creative) {
  const br    = session?.bids?.ss?.row?.validData?.bidResponse || null;
  const ssTop = br?.topBid || null;
  const ss = (ssTop && ssTop.price != null) ? {
    price:    ssTop.price,
    currency: br.currency || 'USD',
    seat:     ssTop.seat || null,
    crid:     ssTop.crid || null,
    adomain:  (Array.isArray(ssTop.adomain) && ssTop.adomain[0]) || null,
    dealid:   ssTop.dealid || null,
  } : null;

  // SpringServe rendered its own creative → its vid-io win beacon carries the price.
  const renderedBySs = bestBreak?.source === 'tracking' && bestBreak?.bidPrice != null;

  // 1) Strongest measured winner signal: <Pricing> on the rendered creative VAST.
  const vp = creative?.pricing;
  const vastPrice = (vp && vp.value != null && !isNaN(parseFloat(vp.value))) ? parseFloat(vp.value) : null;

  let winner = null;
  if (vastPrice != null) {
    winner = { price: +vastPrice.toFixed(4), currency: vp.currency || ss?.currency || 'USD',
               source: 'VAST <Pricing>', confidence: 'measured',
               note: 'Clearing price declared in the rendered creative VAST.' };
  } else if (renderedBySs) {
    winner = { price: bestBreak.bidPrice, currency: ss?.currency || 'USD',
               source: 'SpringServe win beacon', confidence: 'measured',
               note: 'SpringServe rendered — price read from its vid-io win beacon.' };
  } else if (ss) {
    // Server-side DAI: clearing price not exposed client-side. The rendered winner
    // cleared at or above the SS bid → show as a lower-bound estimate, clearly flagged.
    winner = { price: ss.price, currency: ss.currency,
               source: 'inferred ≥ SS bid', confidence: 'inferred',
               note: 'Server-side DAI decision — the clearing price is not exposed client-side. The rendered ad cleared at or above the SS bid, so this is a lower-bound estimate, not the actual paid price.' };
  }

  if (!ss && !winner) return null;

  // Who actually rendered? Positive SS-win evidence:
  //   • a SpringServe vid-io win beacon carried the price (renderedBySs), OR
  //   • the rendered creative's ad system is SpringServe, OR
  //   • the rendered break carries SpringServe AUCTION ATTRIBUTION (deal id,
  //     creative chain, advertiser) — proof SS's creative played even when the
  //     price param wasn't on the beacon. (Matches bidHealth.js ssRendered, so the
  //     economics panel agrees with the session "SS rendered" header.), OR
  //   • the SS bid's deal/adomain matches what rendered (e.g. SS bid walmart.com →
  //     a walmart.com creative rendered = SS won, not outbid).
  const ssAttributed = !!bestBreak && bestBreak.source === 'tracking' &&
    (bestBreak.creativeChain || bestBreak.dealId || bestBreak.bidPrice != null);
  const dealMatch = !!(ss && ss.dealid && bestBreak?.dealId && String(ss.dealid) === String(bestBreak.dealId));
  const adomainMatch = !!(ss && ss.adomain && bestBreak?.advertiser &&
    bestBreak.advertiser.toLowerCase() === ss.adomain.toLowerCase());
  // Positive SS-loss evidence: a DIFFERENT advertiser than the SS bid rendered.
  const differentAdvertiser = !!(ss && ss.adomain && bestBreak?.advertiser &&
    bestBreak.advertiser.toLowerCase() !== ss.adomain.toLowerCase());

  let outcome = 'unknown';
  if (ss) {
    // Precedence: a confirmed deal/advertiser match (or mismatch) on the rendered
    // ad is the strongest signal and beats the generic attribution heuristic.
    if (dealMatch || adomainMatch) outcome = 'ss_won';
    else if (differentAdvertiser) outcome = 'ss_outbid';   // known: a different advertiser rendered
    else if (renderedBySs || /springserve/i.test(creative?.adSystem || '') || ssAttributed)
      outcome = 'ss_won';       // SS creative rendered (price param may be absent)
    else outcome = 'unknown';   // server-side, ad filled, winner not exposed — don't assert "outbid"
  }
  return { ss, winner, outcome };
}

/* ─── Ad break row ───────────────────────────────────────────── */
function AdBreakRow({ brk, streamTs, onJump }) {
  const { timestamp, delta, filled, type, duration, firedQuartiles, errors,
          source, dealId, creativeChain, buyerRef, gamAdUnit,
          bidPrice, advertiser, creativeId, bidFloor, timing, creative,
          skippable, skipped, skipOffset } = brk;
  const hasError = errors.length > 0;
  const filled_ = filled ?? null;
  const fillColor = filled_ === false ? '#e53e3e' : filled_ === true ? '#38a169' : '#a0aec0';
  const fillLabel = filled_ === false ? 'no fill' : filled_ === true ? type || 'filled' : '—';
  // Count only canonical quartile names — firedQuartiles may also contain
  // non-quartile events like 'vast_impression' which would inflate the count.
  const quartileCount = QUARTILE_ORDER.filter(q => firedQuartiles.has(q)).length;

  // Auction winner summary for SSAI breaks (from SS tracking beacon params)
  // a_cc = "rs.31343-s.729506-d.1388047" → seat 729506, deal 1388047
  const seatMatch    = creativeChain?.match(/\bs\.(\d+)/);
  const dealMatch    = creativeChain?.match(/\bd\.(\d+)/);
  const seatId       = seatMatch?.[1] || null;
  const dealIdParsed = dealMatch?.[1] || dealId || null;
  const buyerSeat    = buyerRef?.replace(/^bs\./, '') || null;

  const hasWinnerData = !!(advertiser || bidPrice != null || creativeId);
  const hasAuctionInfo = !!(seatId || dealIdParsed || buyerSeat || hasWinnerData);

  // Build tooltip
  const tooltipParts = [
    advertiser             ? `Advertiser: ${advertiser}`                : '',
    bidPrice    != null    ? `Bid: $${bidPrice.toFixed(2)}`             : '',
    bidFloor    != null    ? `Floor: $${bidFloor.toFixed(2)}`           : '',
    seatId                 ? `Seat: ${seatId}`                         : '',
    dealIdParsed           ? `Deal: ${dealIdParsed}`                   : '',
    buyerSeat              ? `Buyer: ${buyerSeat}`                     : '',
    creativeId             ? `Creative: ${creativeId}`                 : '',
  ].filter(Boolean);

  return (
    <div
      style={{
        borderTop: '1px solid rgba(0,0,0,0.04)',
        cursor: 'pointer',
        transition: 'background 0.1s',
      }}
      onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.03)'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
      onClick={onJump}
    >
      {/* ── Top row: time / fill / duration / winner ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '7px 14px 4px' }}>
        {/* Time */}
        <div style={{ flex: '0 0 60px' }}>
          <div style={{ fontSize: 10, color: 'var(--ink)', fontFamily: 'monospace' }}>
            {fmtTime(timestamp)}
          </div>
          {delta > 1000 && (
            <div style={{ fontSize: 9, color: 'var(--muted)' }}>
              {fmtDelta(delta)}
            </div>
          )}
        </div>

        {/* Fill status */}
        <div style={{ flex: '0 0 70px' }}>
          <span style={{
            fontSize: 10, fontWeight: 600, color: fillColor,
            padding: '2px 5px', borderRadius: 3,
            background: fillColor + '18',
          }}>
            {fillLabel}
          </span>
          {source === 'tracking' && filled_ === true && (
            <div style={{ fontSize: 8.5, color: '#718096', marginTop: 1 }}>SSAI</div>
          )}
          {source === 'dai_impression' && (
            <div style={{ fontSize: 8.5, color: '#718096', marginTop: 1 }}>DAI</div>
          )}
        </div>

        {/* Duration */}
        <div style={{ flex: '0 0 55px', fontSize: 10, color: 'var(--muted)' }}>
          {duration || '—'}
        </div>

        {/* Quartile count badge */}
        <div style={{ fontSize: 9.5, color: 'var(--muted)', flexShrink: 0 }}>
          {quartileCount}/{QUARTILE_ORDER.length}
          {hasError && <span style={{ color: '#e53e3e', marginLeft: 4 }}>· {errors.length} err</span>}
        </div>

        {/* Skippable / skipped badge */}
        {(skippable || skipped) && (
          <span
            title={skipped ? 'Viewer skipped this ad (skip beacon fired)' : `Ad is skippable${skipOffset ? ` after ${skipOffset}` : ''}`}
            style={{
              fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 3, flexShrink: 0,
              background: skipped ? 'rgba(128,90,213,0.18)' : 'rgba(128,90,213,0.1)',
              color: '#6b46c1', border: '1px solid rgba(128,90,213,0.35)',
            }}>
            ⏭ {skipped ? 'skipped' : `skippable${skipOffset ? ' ' + skipOffset : ''}`}
          </span>
        )}

        {/* Auction winner */}
        {hasAuctionInfo && (
          <div
            title={tooltipParts.join(' · ')}
            style={{ display: 'flex', alignItems: 'center', gap: 5, marginLeft: 'auto', flexShrink: 0 }}
          >
            {advertiser && (
              <span style={{
                fontSize: 9.5, fontWeight: 600,
                color: 'var(--ink)',
                maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {advertiser}
              </span>
            )}
            {bidPrice != null && (
              <span style={{
                fontSize: 9.5, fontWeight: 700, color: '#38a169',
                background: 'rgba(56,161,105,0.12)',
                padding: '1px 5px', borderRadius: 3, flexShrink: 0,
              }}>
                ${bidPrice.toFixed(2)}
              </span>
            )}
            {(seatId || dealIdParsed) && (
              <span style={{
                fontSize: 9, color: '#3182ce',
                background: 'rgba(49,130,206,0.08)',
                padding: '1px 5px', borderRadius: 3,
                fontFamily: 'monospace', flexShrink: 0,
                maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {seatId ? `s:${seatId}` : ''}{dealIdParsed ? ` d:${dealIdParsed}` : ''}
              </span>
            )}
          </div>
        )}

        {/* Jump arrow */}
        <span style={{ marginLeft: hasAuctionInfo ? 0 : 'auto', fontSize: 10, color: 'var(--muted)', flexShrink: 0 }}>↗</span>
      </div>

      {/* ── Quartile progress bar + timing ── */}
      <div style={{ padding: '0 14px 8px', paddingLeft: 145 }}>
        <QuartileBar firedQuartiles={firedQuartiles} />
        <TimingStrip timing={timing} />
      </div>

      {/* ── Creative detail (client-side VAST breaks carry parsed creative) ── */}
      {creative && (
        <div style={{ padding: '0 14px 10px', paddingLeft: 145 }} onClick={e => e.stopPropagation()}>
          <CreativeBlock creative={creative} />
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Standard VAST Session Card
   ═══════════════════════════════════════════════════════════════ */
function VastSessionCard({ session, onJumpTo, onOpenSummary, expandAll }) {
  const [expanded, setExpanded] = useState(false);
  useEffect(() => { if (expandAll !== undefined) setExpanded(expandAll); }, [expandAll]);
  const validation = useVideoValidation(session);
  const { adUnit, contentType, adFormat, overallStatus, bids, ima, gamVideo, vast, tracking, issues, timestamp } = session;
  const st = S[overallStatus] || S.neutral;
  const jump = (row) => { if (row && onJumpTo) onJumpTo(row._k ?? row.id, row); };

  // FreeWheel sessions render a self-contained pipeline (no APS/SS/IMA chain).
  const isFw = session.flavor === 'freewheel';
  const fw = session.freewheel || {};
  const fwFilled = session.gamFilled ?? null;
  const fwColor  = fwFilled === false ? '#e53e3e' : fwFilled === true ? '#38a169' : '#a0aec0';
  const fwPods   = session.pods?.breakCount ?? null;
  const vDemand  = session.videoDemand || null;      // adjacent SpringServe bid (inferred)
  const prd      = fw.prd || null;                    // CSAI PRD conformance grade
  const prdColor = prd?.status === 'fail' ? '#e53e3e' : prd?.status === 'warn' ? '#dd6b20' : '#38a169';
  const [slackCopied, setSlackCopied] = useState(false);
  const copyForSlack = (e) => {
    e.stopPropagation();
    const msg = buildFreewheelSlackMessage(session);
    if (!msg) return;
    const done = () => { setSlackCopied(true); setTimeout(() => setSlackCopied(false), 1600); };
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(msg).then(done).catch(() => {});
    else done();
  };
  const vdColor  = vDemand?.status === 'bid' ? '#38a169'
                 : vDemand?.status === 'no_bid' ? '#a0aec0' : '#dd6b20';

  const reqIssues  = (issues || []).filter(i => i.level !== 'info');
  const infoIssues = (issues || []).filter(i => i.level === 'info');

  const slotLabel = adUnit.split('/').filter(Boolean).slice(-2).join('/') || adUnit;

  const phaseChips = [
    { label: 'APS C2S', ...bids.amazon, onClick: bids.amazon.row ? () => jump(bids.amazon.row) : null },
    { label: 'SS C2S',  ...bids.ss,     onClick: bids.ss.row    ? () => jump(bids.ss.row)    : null },
  ];

  const imaStatus = !ima.row ? 'not_seen'
    : ima.tokensOk ? 'bid' : 'nobid';

  const vastFilled  = vast.filled ?? null;
  const vastColor   = vastFilled === false ? '#e53e3e' : vastFilled === true ? '#38a169' : '#a0aec0';
  const vastLabel   = vastFilled === false ? 'no fill' : vastFilled === true ? vast.type || 'filled' : vast.row ? '?' : '—';
  const quartCount  = tracking.firedQuartiles.size;

  return (
    <div style={{
      border: `1.5px solid ${st.border}`,
      borderRadius: 8, background: st.bg,
      overflow: 'hidden', marginBottom: 8,
    }}>
      <CardHeader
        overallStatus={overallStatus}
        contentType={contentType}
        formatBadge={adFormat.toUpperCase()}
        title={slotLabel}
        time={fmtTime(timestamp)}
        issueCount={reqIssues.length}
        infoCount={infoIssues.length}
        expanded={expanded}
        onToggle={() => (onOpenSummary ? onOpenSummary(session) : setExpanded(e => !e))}
        onJump={onOpenSummary ? () => onOpenSummary(session) : (gamVideo.row ? () => jump(gamVideo.row) : null)}
        jumpLabel={onOpenSummary ? 'Open summary in right panel' : 'Jump to GAM VAST request'}
      />

      {(validation.failed > 0 || validation.warned > 0) && (
        <div style={{ padding: '4px 14px 0', display: 'flex', justifyContent: 'flex-end' }}>
          <span
            title="Problems flagged by the video adtech diagnostics — expand for detail"
            style={{
              fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10,
              background: validation.failed > 0 ? 'rgba(229,62,62,0.12)' : 'rgba(221,107,32,0.12)',
              color: validation.failed > 0 ? '#e53e3e' : '#dd6b20',
            }}>
            ⚑ {validation.failed + validation.warned} flagged
          </span>
        </div>
      )}

      {/* Pipeline row */}
      <div style={{
        borderTop: '1px solid rgba(0,0,0,0.06)',
        padding: '8px 14px',
        display: 'flex', alignItems: 'flex-start', gap: 6, flexWrap: 'wrap',
      }}>
        {/* FreeWheel pipeline — self-contained ad server, no APS/SS/IMA chain */}
        {isFw && (
          <>
            {/* Video-demand chip — adjacent SpringServe bid. Dashed border signals an
                INFERRED (timing-only, unprovable) link, distinct from solid chips. */}
            {vDemand && (
              <>
                <div
                  onClick={() => jump(vDemand.row)}
                  title={`SpringServe video bid ${Math.round(vDemand.deltaMs)}ms before the FreeWheel request · HTTP ${vDemand.httpStatus ?? '—'} · inferred link (no shared token)`}
                  style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}
                >
                  <div style={{
                    padding: '3px 8px', borderRadius: 4,
                    background: vdColor + '14', border: `1px dashed ${vdColor}88`,
                    fontSize: 10, fontWeight: 600, color: vdColor,
                  }}>SpringServe</div>
                  <div style={{ fontSize: 8.5, color: vdColor }}>
                    {vDemand.status === 'no_bid' ? 'no-bid'
                      : vDemand.status === 'bid' ? 'bid'
                      : vDemand.status === 'error' ? 'error' : 'bid?'}
                    <span style={{ opacity: 0.6 }}> · inferred</span>
                  </div>
                </div>
                <div style={{ alignSelf: 'flex-start', marginTop: 6, width: 12, height: 1, borderTop: '1px dashed rgba(160,174,192,0.5)' }} />
              </>
            )}
            {/* FreeWheel Ad request chip */}
            <div
              onClick={() => jump(gamVideo.row)}
              style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}
            >
              <div style={{
                padding: '3px 8px', borderRadius: 4,
                background: fwColor + '18', border: `1px solid ${fwColor}44`,
                fontSize: 10, fontWeight: 600, color: fwColor,
              }}>FreeWheel Ad</div>
              <div style={{ fontSize: 8.5, color: fwColor }}>
                {fwFilled === false ? 'no fill'
                  : fwFilled === true ? (fwPods != null ? `${fwPods} pod${fwPods !== 1 ? 's' : ''}` : 'filled')
                  : '↗'}
              </div>
            </div>
            <div style={{ alignSelf: 'flex-start', marginTop: 6, width: 12, height: 1, background: 'rgba(160,174,192,0.3)' }} />
            {/* OMID measurement chip */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <div style={{
                padding: '3px 8px', borderRadius: 4,
                background: fw.omidPartner ? 'rgba(56,161,105,0.12)' : 'rgba(221,107,32,0.12)',
                border: `1px solid ${fw.omidPartner ? 'rgba(56,161,105,0.3)' : 'rgba(221,107,32,0.3)'}`,
                fontSize: 10, fontWeight: 600, color: fw.omidPartner ? '#276749' : '#dd6b20',
              }}>OMID</div>
              <div style={{ fontSize: 8.5, color: fw.omidPartner ? '#38a169' : '#dd6b20' }}>
                {fw.omidPartner ? 'active' : 'absent'}
              </div>
            </div>
            {/* CSAI PRD conformance grade */}
            {prd && (
              <div
                title={`Graded against ${prd.spec}\n${(prd.findings || []).map(f => `[${f.level}] ${f.msg}`).join('\n')}`}
                style={{ alignSelf: 'center', marginLeft: 4, fontSize: 9, fontWeight: 700,
                         color: prdColor, background: prdColor + '14',
                         border: `1px solid ${prdColor}55`, borderRadius: 4,
                         padding: '2px 6px', whiteSpace: 'nowrap', textTransform: 'uppercase' }}
              >
                PRD {prd.status}
              </div>
            )}
            {/* Copy a Slack-ready report for the app team */}
            <button
              onClick={copyForSlack}
              title="Copy a Slack-ready report of this FreeWheel request for the app team"
              style={{ alignSelf: 'center', marginLeft: 4, fontSize: 9, fontWeight: 600,
                       cursor: 'pointer', color: slackCopied ? '#276749' : 'var(--ink)',
                       background: slackCopied ? 'rgba(56,161,105,0.12)' : 'transparent',
                       border: '1px solid rgba(160,174,192,0.5)', borderRadius: 4,
                       padding: '2px 6px', whiteSpace: 'nowrap' }}
            >
              {slackCopied ? '✓ Copied' : '📋 Slack'}
            </button>
          </>
        )}

        {/* Bid chips */}
        {!isFw && phaseChips.map((c) => (
          <React.Fragment key={c.label}>
            <BidChip
              label={c.label}
              status={c.status}
              passed={c.passed}
              expected={c.expected}
              onClick={c.onClick}
            />
            <div style={{ alignSelf: 'flex-start', marginTop: 6, width: 12, height: 1, background: 'rgba(160,174,192,0.3)' }} />
          </React.Fragment>
        ))}

        {/* APS→SS→IMA→GAM→SS VAST chain (non-FreeWheel sessions) */}
        {!isFw && (
        <>
        {/* IMA chip */}
        <BidChip
          label="IMA"
          status={imaStatus}
          passed={ima.tokensOk}
          expected={true}
          onClick={ima.row ? () => jump(ima.row) : null}
        />
        <div style={{ alignSelf: 'flex-start', marginTop: 6, width: 12, height: 1, background: 'rgba(160,174,192,0.3)' }} />

        {/* GAM VAST chip */}
        <div
          onClick={() => jump(gamVideo.row)}
          style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}
        >
          <div style={{
            padding: '3px 8px', borderRadius: 4,
            background: 'rgba(56,161,105,0.12)',
            border: '1px solid rgba(56,161,105,0.3)',
            fontSize: 10, fontWeight: 600, color: '#276749',
          }}>GAM VAST</div>
          <div style={{ fontSize: 8.5, color: '#38a169' }}>↗</div>
        </div>
        <div style={{ alignSelf: 'flex-start', marginTop: 6, width: 12, height: 1, background: 'rgba(160,174,192,0.3)' }} />

        {/* SS VAST chip */}
        <div
          onClick={vast.row ? () => jump(vast.row) : undefined}
          style={{ cursor: vast.row ? 'pointer' : 'default', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}
        >
          <div style={{
            padding: '3px 8px', borderRadius: 4,
            background: vastColor + '18',
            border: `1px solid ${vastColor}44`,
            fontSize: 10, fontWeight: 600, color: vastColor,
          }}>SS VAST</div>
          <div style={{ fontSize: 8.5, color: vastColor }}>{vastLabel}</div>
        </div>
        </>
        )}

        {/* Quartile pips */}
        {(quartCount > 0 || tracking.errors.length > 0) && (
          <>
            <div style={{ alignSelf: 'flex-start', marginTop: 6, width: 12, height: 1, background: 'rgba(160,174,192,0.3)' }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start', minWidth: 160 }}>
              <QuartileBar firedQuartiles={tracking.firedQuartiles} />
              <div style={{ fontSize: 8.5, color: 'var(--muted)' }}>
                {quartCount}/{QUARTILE_ORDER.length} events
                {tracking.errors.length > 0 && <span style={{ color: '#e53e3e', marginLeft: 4 }}>· {tracking.errors.length} error</span>}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Detail (ad-unit params, validation, creative, pods, viewability)
          lives in the right-panel summary — click the card. */}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Video toolbar — search · filters · sort · expand
   ═══════════════════════════════════════════════════════════════ */
function VideoToolbar({ search, setSearch, statusFilter, setStatus, typeFilter, setTypeFilter, sortBy, setSortBy, onExpandAll, onCollapseAll, expandAll, counts, showing }) {
  const Pill = ({ active, onClick, color, children, title }) => (
    <button onClick={onClick} title={title} style={{
      padding: '3px 9px', borderRadius: 14, cursor: 'pointer', fontSize: 10.5, fontWeight: 600,
      border: `1px solid ${active ? (color || '#3182ce') : 'rgba(160,174,192,0.35)'}`,
      background: active ? (color || '#3182ce') + '1c' : 'transparent',
      color: active ? (color || '#3182ce') : 'var(--muted)',
      whiteSpace: 'nowrap',
    }}>{children}</button>
  );
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
      marginBottom: 12, padding: '8px 10px', borderRadius: 8,
      background: 'rgba(160,174,192,0.06)', border: '1px solid rgba(160,174,192,0.18)',
    }}>
      {/* Search */}
      <div style={{ position: 'relative', flex: '1 1 200px', minWidth: 160 }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search ad unit, advertiser, stream…"
          style={{
            width: '100%', padding: '5px 26px 5px 28px', borderRadius: 6, fontSize: 11,
            border: '1px solid rgba(160,174,192,0.35)', background: 'var(--surface)',
            color: 'var(--ink)', outline: 'none',
          }}
        />
        <span style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', fontSize: 11, opacity: 0.6 }}>🔍</span>
        {search && (
          <span onClick={() => setSearch('')} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 11, cursor: 'pointer', opacity: 0.6 }}>✕</span>
        )}
      </div>

      {/* Status filters */}
      <div style={{ display: 'flex', gap: 4 }}>
        <Pill active={statusFilter === 'all'}  onClick={() => setStatus('all')}>All {counts.all}</Pill>
        <Pill active={statusFilter === 'fail'} onClick={() => setStatus('fail')} color="#e53e3e" title="Only failing sessions">✗ {counts.fail}</Pill>
        <Pill active={statusFilter === 'warn'} onClick={() => setStatus('warn')} color="#dd6b20" title="Only warnings">⚠ {counts.warn}</Pill>
        <Pill active={statusFilter === 'pass'} onClick={() => setStatus('pass')} color="#38a169" title="Only passing">✓ {counts.pass}</Pill>
      </div>

      {/* Type filters */}
      <div style={{ display: 'flex', gap: 4 }}>
        <Pill active={typeFilter === 'dai'}  onClick={() => setTypeFilter(typeFilter === 'dai' ? 'all' : 'dai')}   color="#805ad5" title="DAI/SSAI streams">DAI {counts.dai}</Pill>
        <Pill active={typeFilter === 'vast'} onClick={() => setTypeFilter(typeFilter === 'vast' ? 'all' : 'vast')} color="#3182ce" title="Client-side VAST">VAST {counts.vast}</Pill>
      </div>

      {/* Sort + expand */}
      <div style={{ display: 'flex', gap: 4, marginLeft: 'auto', alignItems: 'center' }}>
        <span style={{ fontSize: 10, color: 'var(--muted)' }}>sort</span>
        <Pill active={sortBy === 'time'}   onClick={() => setSortBy('time')}>newest</Pill>
        <Pill active={sortBy === 'status'} onClick={() => setSortBy('status')}>status</Pill>
        <Pill active={expandAll === true} onClick={expandAll === true ? onCollapseAll : onExpandAll} title="Expand or collapse all cards">
          {expandAll === true ? '▲ collapse' : '▼ expand all'}
        </Pill>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   VideoSummaryPanel — right-panel session overview (mirrors the
   Lifecycle summary). Click chips / ad-break rows to drill into the
   individual request via onJumpTo.
   ═══════════════════════════════════════════════════════════════ */
// One key/value row in a bordered decision table (mirrors the Lifecycle panel).
// Colour a key/value cell by its leading status glyph so the table scans fast:
// ✓ green, ✗ red, — muted. Explicit warn overrides to red.
function kvValueColor(value, warn) {
  if (warn) return '#e53e3e';
  const v = String(value ?? '').trimStart();
  if (v.startsWith('✓')) return '#276749';
  if (v.startsWith('✗')) return '#e53e3e';
  if (v.startsWith('—')) return 'var(--muted)';
  return 'var(--ink)';
}

function KVRow({ label, value, mono, last }) {
  return (
    <div style={{ display: 'flex', gap: 10, padding: '5px 10px', borderBottom: last ? 'none' : '1px solid rgba(160,174,192,0.18)', fontSize: 11 }}>
      <span style={{ color: 'var(--muted)', flex: '0 0 92px' }}>{label}</span>
      <span style={{ color: kvValueColor(value), fontFamily: mono ? 'monospace' : 'inherit', fontSize: mono ? 10.5 : 11, wordBreak: 'break-word', flex: 1 }}>{value}</span>
    </div>
  );
}

// IMA stream-registration params table (moved out of the inline card).
function StreamRegParams({ session }) {
  const imaRow = session.imaRow;
  if (!imaRow) return null;
  const exp = imaRow.validData?.expanded || {};
  const realStreamId = imaRow.validData?.stream_id || null;
  const bids = session.bids || {};
  const rows = [
    { label: 'GAM ad unit',     value: session.displayTitle || '— (from SS beacon)', mono: true },
    { label: 'Stream ID',       value: realStreamId || '— (not captured)', mono: true },
    { label: 'Registered',      value: fmtTime(session.imaTs || session.timestamp) },
    { label: 'dai / api',       value: exp.dai || '—' },
    { label: 'app',             value: exp.app || '—' },
    { label: 'cust_params',     value: bids.custParamSource && bids.custParamSource !== 'none' ? `✓ found in: ${bids.custParamSource}` : '✗ not found', warn: !bids.custParamSource || bids.custParamSource === 'none' },
    { label: 'amzn_b',          value: bids.amazon?.passed ? '✓ present' : bids.amazon?.expected ? '✗ missing' : '— (no APS bid)', warn: bids.amazon?.expected && !bids.amazon?.passed },
    { label: 'ss bid token',    value: bids.ss?.passed ? '✓ present (ss_request_key / ss_ad_cache_key)' : bids.ss?.expected ? '✗ missing' : '— (no SS bid)', warn: bids.ss?.expected && !bids.ss?.passed },
    { label: 'iu override',     value: exp.iu || '— (uses GAM DAI config)' },
    { label: 'description_url', value: exp.descriptionUrl ? '✓ present' : '— (uses GAM DAI config)' },
    { label: 'rdid / idtype',   value: exp.rdid ? `${exp.rdid.slice(0, 8)}… / ${exp.idtype}` : '— not present' },
    { label: 'npa / is_lat',    value: `npa=${exp.npa ?? '—'}  is_lat=${exp.isLat ?? '—'}` },
    { label: 'ppid',            value: exp.ppid || '— not present' },
    { label: 'IMA SDK',         value: exp.sdkName ? `${exp.sdkName} v${exp.sdkVersion || '?'}` : '—' },
  ];
  return (
    <>
      <div style={{ fontWeight: 700, color: 'var(--muted)', fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, margin: '12px 0 5px' }}>Stream registration params</div>
      <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: '4px 12px', fontSize: 11, border: '1px solid rgba(160,174,192,0.25)', borderRadius: 6, padding: '8px 10px' }}>
        {rows.map(({ label, value, mono, warn }) => (
          <React.Fragment key={label}>
            <span style={{ color: 'var(--muted)', lineHeight: 1.6, letterSpacing: 0.1 }}>{label}</span>
            <span style={{ fontFamily: mono ? 'monospace' : 'inherit', fontSize: mono ? 10.5 : 11, fontWeight: warn ? 600 : 400, color: kvValueColor(value, warn), wordBreak: 'break-all', lineHeight: 1.6 }}>{value}</span>
          </React.Fragment>
        ))}
      </div>
    </>
  );
}

export function VideoSummaryPanel({ session, onJumpTo }) {
  const validation = useVideoValidation(session);
  const [keysCopied, setKeysCopied] = useState(false);
  if (!session) return null;
  const jump = (row) => { if (row && onJumpTo) onJumpTo(row._k ?? row.id, row); };
  const isDai  = session.sessionType === 'dai_stream';
  const isSsai = session.flavor === 'ssai';
  const title  = isDai ? (session.displayTitle || session.streamId || 'DAI stream') : (session.adUnit || 'VAST');
  const fmtBadge = isSsai ? 'SSAI' : isDai ? 'DAI' : (session.adFormat || 'VAST').toUpperCase();
  const st = S[session.overallStatus] || S.neutral;
  const bids = session.bids || {};
  const breaks = session.adBreaks || [];
  const filled = breaks.filter(b => b.filled !== false).length;
  const bestBreak = breaks.length
    ? breaks.reduce((b, x) => (canonicalQuartiles(x) > canonicalQuartiles(b) ? x : b), breaks[0])
    : null;
  const qfired = bestBreak ? QUARTILE_ORDER.filter(q => bestBreak.firedQuartiles?.has(q)).length : 0;
  const completed = !!bestBreak?.firedQuartiles?.has('complete');
  // Pure Google DAI VOD fires quartile beacons SERVER-SIDE — the client only
  // sends the impression beacon, so we can't measure quartiles client-side.
  const daiServerSide = bestBreak?.source === 'dai_impression';
  const sizes = session.bidSignals?.ss?.imps?.[0] || session.bidSignals?.aps?.imps?.[0];
  const creative = bestBreak?.creative || session.gamCreative || session.vast?.creative || null;
  const bidEcon = computeBidEconomics(session, bestBreak, creative);

  // ── Phase nodes (APS → SS → IMA → Ad → Playback → OMID) ──
  const PHASES = [
    { key: 'aps', icon: '📦', label: 'APS C2S',
      fired: bids.amazon?.status === 'bid', dim: !bids.amazon || bids.amazon.status === 'not_seen',
      warn: bids.amznMissing || bids.amazon?.status === 'unknown',
      sub: bids.amazon?.status === 'bid' ? 'bid' : bids.amazon?.status === 'nobid' ? 'no-bid' : bids.amazon?.status === 'unknown' ? 'bid?' : 'not seen',
      row: bids.amazon?.row },
    { key: 'ss', icon: '🎯', label: 'SS C2S',
      fired: bids.ss?.status === 'bid', dim: !bids.ss || bids.ss.status === 'not_seen',
      warn: bids.ssMissing || bids.ss?.status === 'unknown',
      sub: bids.ss?.status === 'bid' ? 'bid' : bids.ss?.status === 'nobid' ? 'no-bid' : bids.ss?.status === 'unknown' ? 'bid?' : 'not seen',
      row: bids.ss?.row },
    { key: 'ima', icon: isSsai ? '🧩' : '📺', label: isSsai ? 'SSAI' : 'IMA',
      fired: !!session.imaRow || isSsai, dim: !session.imaRow && !isSsai,
      warn: !!session.imaRow && bids.tokensOk === false,
      sub: isSsai ? 'SpringServe' : session.imaRow ? (bids.tokensOk ? 'registered' : 'tokens ⚠') : 'not seen',
      row: session.imaRow, ts: session.imaTs },
    { key: 'ad', icon: '🎬', label: 'Ad break',
      fired: filled > 0, dim: breaks.length === 0, warn: breaks.length > 0 && filled === 0,
      sub: breaks.length === 0 ? 'none' : `${filled}/${breaks.length} filled`,
      row: bestBreak?.row },
    { key: 'play', icon: '▶', label: 'Playback',
      fired: daiServerSide || qfired > 0, dim: !daiServerSide && qfired === 0,
      warn: !daiServerSide && qfired > 0 && !completed,
      sub: daiServerSide ? 'served (DAI)' : completed ? 'complete' : qfired > 0 ? `${qfired}/5` : '—',
      row: bestBreak?.row },
    { key: 'view', icon: '👁', label: 'OMID',
      fired: session.viewability?.verdict === 'measured',
      dim: !session.viewability?.present,
      warn: session.viewability?.verdict === 'session-only' || session.viewability?.verdict === 'script-only',
      sub: session.viewability?.verdict === 'measured' ? 'measured'
        : session.viewability?.verdict === 'session-only' ? 'session only'
        : session.viewability?.verdict === 'script-only' ? 'script only'
        : session.viewability?.present ? 'partial' : '—',
      row: null },
  ];
  const phaseColor = (p) => {
    if (p.dim)  return { bg: 'transparent', border: 'rgba(160,174,192,0.4)', text: 'var(--muted)', dim: true };
    if (p.warn) return { bg: 'rgba(221,107,32,0.12)', border: '#dd6b20', text: '#9c4221', dim: false };
    if (p.fired) return { bg: 'rgba(56,161,105,0.12)', border: '#38a169', text: '#276749', dim: false };
    return { bg: 'transparent', border: 'rgba(160,174,192,0.4)', text: 'var(--muted)', dim: true };
  };

  // ── Ad decision values ──
  const winner = bestBreak?.advertiser || creative?.advertiser || null;
  const price  = bestBreak?.bidPrice != null ? `$${bestBreak.bidPrice.toFixed(2)}` : null;
  const dur    = creative?.durationSeconds != null ? fmtDur(creative.durationSeconds)
               : bestBreak?.duration || null;

  return (
    <div style={{ overflowY: 'auto', flex: 1, minHeight: 0, padding: '10px 12px', fontSize: 12 }}>

      {/* Header */}
      <div style={{
        borderLeft: `3px solid ${st.border}`, background: st.bg, borderRadius: 6,
        padding: '7px 12px', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
      }}>
        <span style={{ fontWeight: 700, fontSize: 12.5, flex: 1, minWidth: 0, fontFamily: 'monospace', wordBreak: 'break-all' }}>{title}</span>
        <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 8px', borderRadius: 20, flexShrink: 0,
          background: st.bg, border: `1px solid ${st.border}`, color: st.text, textTransform: 'uppercase' }}>
          {session.overallStatus}
        </span>
        <span style={{ fontSize: 10, color: 'var(--muted)', border: '1px solid rgba(160,174,192,0.3)', borderRadius: 20, padding: '1px 7px', flexShrink: 0 }}>{fmtBadge}</span>
        <div style={{ width: '100%', color: 'var(--muted)', fontSize: 10, marginTop: 1 }}>
          {sizes?.w && sizes?.h && <span>{sizes.w}×{sizes.h} · </span>}
          {session.contentType && session.contentType !== 'unknown' && <span>{session.contentType} · </span>}
          {fmtTime(session.timestamp)}
        </div>
      </div>

      {/* Phase diagram */}
      <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12, overflowX: 'auto', paddingBottom: 2 }}>
        {PHASES.map((p, i) => {
          const c = phaseColor(p);
          const pt = p.ts || (p.row ? ts(p.row) : null);
          return (
            <React.Fragment key={p.key}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                <div
                  onClick={() => p.row && jump(p.row)}
                  title={p.row ? `Click to view ${p.label} request` : `${p.label}: ${p.sub}`}
                  style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    width: 66, padding: '6px 4px', borderRadius: 8, textAlign: 'center',
                    background: c.bg, border: `1.5px solid ${c.border}`, opacity: c.dim ? 0.5 : 1,
                    cursor: p.row ? 'pointer' : 'default',
                  }}
                >
                  <div style={{ fontSize: 17, marginBottom: 2 }}>{p.icon}</div>
                  <div style={{ fontSize: 9.5, fontWeight: 600, color: c.text }}>{p.label}</div>
                  <div style={{ fontSize: 8.5, color: c.text, marginTop: 1, lineHeight: 1.3 }}>{p.sub}</div>
                </div>
                {pt ? <div style={{ fontSize: 8, color: 'var(--muted)', marginTop: 3 }}>{fmtTime(pt)}</div> : null}
              </div>
              {i < PHASES.length - 1 && (
                <div style={{ width: 14, height: 2, alignSelf: 'flex-start', marginTop: 24, flexShrink: 0,
                  background: p.fired ? '#38a169' : 'repeating-linear-gradient(90deg,rgba(160,174,192,0.4) 0,rgba(160,174,192,0.4) 3px,transparent 3px,transparent 6px)' }} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Issues (fail/warn from validation) */}
      {validation && validation.checks.filter(c => c.status === 'fail' || c.status === 'warn').length > 0 && (
        <div style={{ marginBottom: 10 }}>
          {validation.checks.filter(c => c.status === 'fail' || c.status === 'warn').slice(0, 6).map((c, i) => {
            const fail = c.status === 'fail';
            return (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '5px 10px', marginBottom: 3, borderRadius: 6,
                background: fail ? 'rgba(229,62,62,0.08)' : 'rgba(221,107,32,0.08)', borderLeft: `3px solid ${fail ? '#e53e3e' : '#dd6b20'}` }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: fail ? '#9b2c2c' : '#9c4221', textTransform: 'uppercase', marginTop: 1, flexShrink: 0 }}>{c.status}</span>
                <span style={{ fontSize: 11, color: 'var(--ink)' }}>{c.label}{c.detail ? ` — ${c.detail}` : ''}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* No-fill lookup keys — for manual confirmation in GAM / SpringServe consoles */}
      {filled === 0 && diagnoseNoFill(session) && (
        <div style={{ marginBottom: 10, border: '1px solid rgba(160,174,192,0.25)', borderRadius: 6, padding: '8px 10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ fontWeight: 600, color: 'var(--muted)', fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, flex: 1 }}>
              Confirm in GAM / SpringServe
            </span>
            <button
              onClick={() => {
                const { text } = buildLookupKeys(session);
                navigator.clipboard?.writeText(text).then(() => { setKeysCopied(true); setTimeout(() => setKeysCopied(false), 2000); }).catch(() => {});
              }}
              style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 6, cursor: 'pointer',
                border: '1px solid var(--line)', background: keysCopied ? 'rgba(56,161,105,0.15)' : 'var(--surface)',
                color: keysCopied ? '#276749' : 'var(--ink)' }}>
              {keysCopied ? '✓ copied' : '📋 Copy lookup keys'}
            </button>
          </div>
          {(() => {
            const { obj } = buildLookupKeys(session);
            return (
              <div style={{ fontSize: 10.5, fontFamily: 'monospace', color: 'var(--muted)', lineHeight: 1.5 }}>
                {obj.streamId   && <div>stream_id: <span style={{ color: 'var(--ink)' }}>{obj.streamId}</span></div>}
                {obj.correlator && <div>correlator: <span style={{ color: 'var(--ink)' }}>{obj.correlator}</span></div>}
                {obj.ssRequestKey && <div>ss_request_key: <span style={{ color: 'var(--ink)' }}>{obj.ssRequestKey}</span></div>}
                <div style={{ marginTop: 3, fontStyle: 'italic', fontFamily: 'inherit' }}>
                  Paste into GAM Delivery Inspector or SpringServe reporting to see the real bid → win → reason.
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Ad Decision */}
      {(winner || price || dur || breaks.length > 0) && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontWeight: 600, color: 'var(--muted)', fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 5 }}>
            {isDai ? 'DAI decision' : 'Ad decision'}
          </div>
          <div style={{ border: '1px solid rgba(160,174,192,0.25)', borderRadius: 6, overflow: 'hidden' }}>
            <KVRow label="Demand" value={isSsai ? 'SpringServe SSAI' : isDai ? 'Google DAI + SpringServe' : 'GAM VAST'} />
            {winner && <KVRow label="Advertiser" value={winner} />}
            {price && <KVRow label="Bid price" value={price} />}
            {(bestBreak?.creativeChain || bestBreak?.dealId) && <KVRow label="Deal / chain" value={bestBreak.dealId || bestBreak.creativeChain} mono />}
            {creative?.creativeId && <KVRow label="Creative ID" value={creative.creativeId} mono />}
            {dur && <KVRow label="Duration" value={dur} />}
            <KVRow label="Ad fill" value={breaks.length === 0 ? 'no ad break captured' : `${filled} of ${breaks.length} filled`} />
            <KVRow label="Quartiles" value={daiServerSide ? '— tracked server-side (DAI VOD)' : completed ? '✓ played to 100%' : `${qfired}/5 fired`} last />
          </div>
        </div>
      )}

      {/* Bid economics — SS bid CPM vs the creative that rendered */}
      {bidEcon && (bidEcon.ss || bidEcon.winner) && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontWeight: 600, color: 'var(--muted)', fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 5 }}>
            Bid economics
          </div>
          <div style={{ border: '1px solid rgba(160,174,192,0.25)', borderRadius: 6, overflow: 'hidden' }}>
            {bidEcon.ss && (
              <KVRow
                label="SpringServe bid"
                value={`$${bidEcon.ss.price.toFixed(2)} ${bidEcon.ss.currency}${bidEcon.outcome === 'ss_outbid' ? ' · outbid' : bidEcon.outcome === 'ss_won' ? ' · won' : ''}`}
              />
            )}
            {bidEcon.ss?.seat   && <KVRow label="SS seat"     value={bidEcon.ss.seat} mono />}
            {bidEcon.ss?.adomain && <KVRow label="SS adomain" value={bidEcon.ss.adomain} />}
            {bidEcon.winner && (
              <KVRow
                label={bidEcon.outcome === 'ss_won' ? 'Cleared at' : 'Rendered (winner)'}
                value={
                  <span title={bidEcon.winner.note}>
                    {bidEcon.winner.confidence === 'inferred' ? '≥ ' : ''}${bidEcon.winner.price.toFixed(2)} {bidEcon.winner.currency}
                    <span style={{
                      marginLeft: 6, fontSize: 9, fontWeight: 700, padding: '0px 5px', borderRadius: 3,
                      background: bidEcon.winner.confidence === 'inferred' ? 'rgba(221,107,32,0.14)' : 'rgba(56,161,105,0.12)',
                      color: bidEcon.winner.confidence === 'inferred' ? '#9c4221' : '#276749',
                    }}>
                      {bidEcon.winner.confidence === 'inferred' ? 'EST' : 'MEASURED'}
                    </span>
                  </span>
                }
                last={!bidEcon.winner.note}
              />
            )}
            {bidEcon.winner?.note && (
              <div style={{ padding: '5px 10px', fontSize: 9.5, color: 'var(--muted)', fontStyle: 'italic', lineHeight: 1.4, borderTop: '1px solid rgba(160,174,192,0.15)' }}>
                {bidEcon.winner.source} — {bidEcon.winner.note}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Validation checklist */}
      {validation && validation.total > 0 && (
        <>
          <div style={{ fontWeight: 600, color: 'var(--muted)', fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 }}>Lifecycle validation</div>
          <ValidationChecklist validation={validation} session={session} />
        </>
      )}

      {/* Stream registration params (moved here from the inline card) */}
      <StreamRegParams session={session} />

      {/* Bid request signals (OpenRTB video) */}
      <BidSignals bidSignals={session.bidSignals} />

      {/* Ad-pod structure */}
      {session.pods && <PodStructure pods={session.pods} />}

      {/* Parsed creative (media files etc.) */}
      <CreativeBlock creative={creative} label="Creative (parsed VAST)" />

      {/* Stream details */}
      {session.manifest && <StreamDetails manifest={session.manifest} manifestUrl={session.streamManifestUrl} />}

      {/* Scan completeness — requests whose response was never captured.
          Read-only: tells you which green checks are based on a request whose
          response the proxy never saw, so the scan isn't mistaken for complete. */}
      {session.capture && session.capture.uncaptured > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontWeight: 600, color: 'var(--muted)', fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 5, display: 'flex', gap: 6, alignItems: 'center' }}>
            Scan completeness
            <MiniBadge color="#dd6b20">{session.capture.captured}/{session.capture.total} responses captured</MiniBadge>
          </div>
          <div style={{ border: '1px solid rgba(221,107,32,0.4)', borderRadius: 6, overflow: 'hidden' }}>
            <KVRow label="Not captured" value={`${session.capture.uncaptured} request(s) — response never seen by the proxy (TLS drop / dropped / body stripped)`} />
            <KVRow label="Effect" value="Any check on these is unconfirmed — a green status does not prove what happened" last />
          </div>
          {session.capture.rows.slice(0, 6).map((c, i) => (
            <div key={i} onClick={() => jump(c.row)} title="Click to view this request"
              style={{ cursor: 'pointer', display: 'flex', gap: 8, alignItems: 'center', padding: '3px 8px', fontSize: 10.5, fontFamily: 'monospace' }}>
              <span style={{ fontWeight: 700, color: '#9c4221', flexShrink: 0 }}>{c.status || '—'}</span>
              <span style={{ color: 'var(--ink)', flexShrink: 0 }}>{c.role}</span>
              <span style={{ color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{(c.row.url || '').split('?')[0].split('/').slice(-2).join('/')}</span>
            </div>
          ))}
        </div>
      )}

      {/* HLS delivery failures (4xx/5xx on manifest/segment requests) */}
      {session.hlsErrors && session.hlsErrors.count > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontWeight: 600, color: 'var(--muted)', fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 5 }}>
            HLS delivery
          </div>
          <div style={{ border: '1px solid rgba(221,107,32,0.4)', borderRadius: 6, overflow: 'hidden' }}>
            <KVRow label="Failed requests" value={`${session.hlsErrors.count} (${Object.entries(session.hlsErrors.byCode).sort((a,b)=>b[1]-a[1]).map(([c,n])=>`${c}×${n}`).join(', ')})`} />
            <KVRow label="Impact" value={Object.keys(session.hlsErrors.byCode).some(c=>+c>=500) ? 'origin/CDN failure — buffering or broken stitch' : 'missing/forbidden segment — ad stalls'} last />
          </div>
          {session.hlsErrors.rows.slice(0, 5).map((r, i) => (
            <div key={i} onClick={() => jump(r)} title="Click to view this request"
              style={{ cursor: 'pointer', display: 'flex', gap: 8, alignItems: 'center', padding: '3px 8px', fontSize: 10.5, fontFamily: 'monospace' }}>
              <span style={{ fontWeight: 700, color: (Number(r.status ?? r.responseCode) >= 500) ? '#9b2c2c' : '#9c4221', flexShrink: 0 }}>{r.status ?? r.responseCode}</span>
              <span style={{ color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{(r.url || '').split('?')[0].split('/').slice(-2).join('/')}</span>
            </div>
          ))}
        </div>
      )}

      {/* Viewability */}
      {session.viewability && (
        <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 9.5, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Viewability</span>
          <ViewabilityChip viewability={session.viewability} />
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   VideoBidHealthPanel — video fill / SpringServe+APS bid rate / SS render
   share by app · platform. Lives in the Bid Health tab. The app-click filter
   is optional (only wired when onSelectApp is provided, e.g. embedded next to
   a session list); standalone it renders as a read-only scorecard.
   ═══════════════════════════════════════════════════════════════ */
export function VideoBidHealthPanel({ sessions = [], onSelectApp, activeApp = '' }) {
  const bidHealth = useMemo(() => computeVideoBidHealth(sessions), [sessions]);
  if (!bidHealth.length) return null;
  const clickable = typeof onSelectApp === 'function';
  return (
    <div style={{ marginBottom: 12, border: '1px solid rgba(160,174,192,0.3)', borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.3, color: 'var(--muted)', padding: '6px 12px', background: 'rgba(160,174,192,0.08)', borderBottom: '1px solid rgba(160,174,192,0.2)' }}>
        BID HEALTH · video fill &amp; SpringServe / APS bid rate by app · platform
      </div>
      {bidHealth.map((s) => {
        const C  = { fail: '#A32D2D', warn: '#854F0B', pass: '#1D9E75', info: '#6b7280' }[s.severity] || '#6b7280';
        const BG = { fail: 'rgba(229,62,62,0.06)', warn: 'rgba(221,107,32,0.06)', pass: 'rgba(56,161,105,0.06)', info: 'transparent' }[s.severity] || 'transparent';
        const appName = (s.key.split(' · ')[0] || s.key).trim();
        const active = clickable && activeApp.trim().toLowerCase() === appName.toLowerCase();
        return (
          <div key={s.key}
            onClick={clickable ? () => onSelectApp(active ? '' : appName) : undefined}
            title={`${s.headline}${s.flags.length ? '\n\n⚑ ' + s.flags.join('\n⚑ ') : ''}${clickable ? `\n\nClick to ${active ? 'clear filter' : 'filter sessions to ' + appName}` : ''}`}
            style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', fontSize: 12, background: active ? 'rgba(0,122,255,0.1)' : BG, borderTop: '1px solid rgba(0,0,0,0.04)', flexWrap: 'wrap', cursor: clickable ? 'pointer' : 'default', boxShadow: active ? 'inset 3px 0 0 #007aff' : 'none' }}
            onMouseEnter={clickable ? (e => { if (!active) e.currentTarget.style.background = 'rgba(0,0,0,0.03)'; }) : undefined}
            onMouseLeave={clickable ? (e => { if (!active) e.currentTarget.style.background = BG; }) : undefined}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: C, flexShrink: 0 }} />
            <span style={{ fontWeight: 600, minWidth: 120 }}>{s.key}</span>
            <span style={{ color: 'var(--muted)' }}>
              {s.sessions} session{s.sessions !== 1 ? 's' : ''} · fill {s.filled}/{s.sessions}
            </span>
            <span style={{ marginLeft: 'auto', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <span title={`SpringServe C2S returned a bid in ${s.ssBid}/${s.sessions} sessions`}>SS bid <b style={{ color: C }}>{s.ssBidRate}%</b> <span style={{ opacity: 0.6 }}>({s.ssBid}/{s.sessions})</span></span>
              <span title="Sessions where Amazon APS C2S returned a bid">APS bid <b>{s.apsBidRate}%</b></span>
              <span title="Filled ad breaks that played to 100%">▶ {s.completionRate}%</span>
              <span title="OMID viewable sessions">👁 {s.viewableRate}%</span>
              <span title={`Of the ${s.ssBid} times SpringServe bid, an SS ad actually rendered ${s.ssRendered} time(s). Outbid / no-fill ${s.ssOutbid}× — GAM picked other demand or the break didn't fill.`} style={{ color: s.ssWinRate >= 50 ? C : '#dd6b20', fontWeight: 700 }}>SS rendered {s.ssWinRate}% <span style={{ opacity: 0.6 }}>({s.ssRendered}/{s.ssBid})</span></span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   VideoLifecycleView — main export
   ═══════════════════════════════════════════════════════════════ */
/**
 * Server-computed validations, keyed by session object (WP1a of
 * docs/frontend-validation-port-spec.md). Context rather than prop-drilling:
 * the three consumers are nested render helpers, and threading a prop through
 * every level is how one of them quietly keeps calling the local copy.
 *
 * Default null = "no server answer", so useVideoValidation falls back to the
 * local call. Never a partial mixture: the map is set or cleared wholesale.
 */
const ServerValidationsContext = React.createContext(null);

/**
 * The actual resolution rule: prefer the server-computed verdict for this
 * session, fall back to the local computation only when the server has no
 * entry. Plain function (no hooks) so non-React call sites — the sign-off
 * report builder walks sessions outside the render tree, where React
 * context can't reach — can resolve verdicts too. This is the single seam
 * every validation consumer goes through; useVideoValidation below is just
 * its hook wrapper for in-render consumers.
 */
export function resolveVideoValidation(session, serverValidationsMap) {
  if (!session) return null;
  const v = serverValidationsMap && serverValidationsMap.get(session);
  return v != null ? v : runVideoValidation(session);
}

function useVideoValidation(session) {
  const fromServer = React.useContext(ServerValidationsContext);
  return useMemo(
    () => resolveVideoValidation(session, fromServer),
    [session, fromServer],
  );
}

export default function VideoLifecycleView({ allRows = [], onJumpTo, onOpenSummary }) {
  /* ── Sessions: server-authoritative, local fallback ────────────────────────
   * Mirrors the AdLifecycleView cutover. The proxy assembles these with the
   * same ported code (proxy-server/lifecycle/video.js), so the tab, the sign-off
   * report and any agent read one answer instead of three.
   *
   * The local build is KEPT as fallback: this tab works today with no proxy at
   * all (imported HAR review, offline triage) and server-only would regress
   * that to a blank panel. `sessionSource` records which produced what is on
   * screen — a fallback that does not announce itself is how two answers creep
   * back in.
   *
   * Sets survive the wire because api/lifecycle.js hydrates at the boundary;
   * see its note. Parity is gated by validation/videoParity.test.js.
   */
  const localSessions = useMemo(() => buildVideoSessions(allRows), [allRows]);
  const [serverSessions, setServerSessions] = useState(null);
  const [sessionSource, setSessionSource] = useState('pending');
  // session object -> server-computed validation (WP1a). Null whenever the
  // server did not answer, which is what makes the fallback below announce
  // itself rather than silently mixing sources.
  const [serverValidations, setServerValidations] = useState(null);

  const sessionsLatest = useLatestWins(); // see utils/latestWins.js
  useEffect(() => {
    if (!allRows.length) { sessionsLatest.invalidate(); setServerSessions(null); setSessionSource('empty'); }
  }, [allRows]); // eslint-disable-line react-hooks/exhaustive-deps
  // One POST in flight, at most every few seconds; in-flight answers still land.
  useCoalescedPost(() => {
      const seq = sessionsLatest.begin();
      return postVideoSessions(allRows, { validate: true })
        .then((res) => {
          if (!sessionsLatest.accept(seq)) return;
          const ok = Array.isArray(res && res.sessions);
          setServerSessions(ok ? res.sessions : null);
          // Index-aligned with sessions[] by contract (see the route). Kept in
          // a Map keyed by session identity rather than by index, so a later
          // filter/sort of `sessions` can never silently pair a session with
          // another session's verdict.
          setServerValidations(ok && Array.isArray(res.validations)
            ? new Map(res.sessions.map((sn, i) => [sn, res.validations[i]]))
            : null);
          setSessionSource(ok ? 'server' : 'local');
        })
        .catch(() => {
          if (!sessionsLatest.accept(seq)) return;
          setServerSessions(null);
          setServerValidations(null);
          setSessionSource('local');
        });
  }, [allRows], { enabled: allRows.length > 0 });

  const sessions = sessionSource === 'server' && serverSessions ? serverSessions : localSessions;

  const [search, setSearch]         = useState('');
  const [statusFilter, setStatus]   = useState('all'); // all | fail | warn | pass
  const [typeFilter, setTypeFilter] = useState('all'); // all | dai | vast
  const [sortBy, setSortBy]         = useState('time'); // time | status
  const [expandAll, setExpandAll]   = useState(undefined);
  const [noAdOnly, setNoAdOnly]     = useState(false); // show only sessions where no ad served
  const [hlsErrOnly, setHlsErrOnly] = useState(false); // show only sessions with HLS delivery failures

  // A session served "no ad" if none of its ad breaks filled.
  const isNoAd = (s) => {
    const breaks = s.adBreaks || (s.tracking ? [{ filled: s.vast?.filled }] : []);
    return breaks.filter(b => b.filled !== false).length === 0;
  };

  const total   = sessions.length;
  const passing = sessions.filter(s => s.overallStatus === 'pass').length;
  const failing = sessions.filter(s => s.overallStatus === 'fail').length;
  const warning = sessions.filter(s => s.overallStatus === 'warn').length;

  // Apply search / status / type filters + sort.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rank = { fail: 0, warn: 1, info: 2, pass: 3 };
    let list = sessions.filter(s => {
      if (noAdOnly && !isNoAd(s)) return false;
      if (hlsErrOnly && !(s.hlsErrors && s.hlsErrors.count > 0)) return false;
      if (statusFilter !== 'all' && s.overallStatus !== statusFilter) return false;
      if (typeFilter === 'dai' && s.sessionType !== 'dai_stream') return false;
      if (typeFilter === 'vast' && s.sessionType !== 'vast') return false;
      if (q) return sessionSearchText(s).includes(q);
      return true;
    });
    list = [...list].sort((a, b) => sortBy === 'status'
      ? (rank[a.overallStatus] ?? 4) - (rank[b.overallStatus] ?? 4) || b.timestamp - a.timestamp
      : b.timestamp - a.timestamp);
    return list;
  }, [sessions, search, statusFilter, typeFilter, sortBy, noAdOnly, hlsErrOnly]);

  const noAdCount = useMemo(() => sessions.filter(isNoAd).length, [sessions]);
  const hlsErr = useMemo(() => {
    const affected = sessions.filter(s => s.hlsErrors && s.hlsErrors.count > 0);
    const total = affected.reduce((n, s) => n + s.hlsErrors.count, 0);
    return { sessions: affected.length, total };
  }, [sessions]);
  // Always-visible request-level HLS watch (so it's clear monitoring is active at 0 fails).
  const hlsWatch = useMemo(() => hlsRequestStats(allRows), [allRows]);
  // Capture completeness across all sessions — how many lifecycle requests had
  // their response captured. Read-only signal that the scan may be partial.
  const captureWatch = useMemo(() => {
    let total = 0, uncaptured = 0, sess = 0;
    for (const s of sessions) {
      if (!s.capture) continue;
      total += s.capture.total; uncaptured += s.capture.uncaptured;
      if (s.capture.uncaptured > 0) sess++;
    }
    return { total, uncaptured, captured: total - uncaptured, sessions: sess };
  }, [sessions]);

  const daiStreams = filtered.filter(s => s.sessionType === 'dai_stream');
  const vastSessions = filtered.filter(s => s.sessionType === 'vast');

  // Aggregate completion + fill analytics across all sessions.
  const analytics = useMemo(() => {
    let completed = 0, withBreaks = 0, filledBreaks = 0, totalBreaks = 0, viewable = 0;
    for (const s of sessions) {
      const breaks = s.adBreaks || (s.tracking ? [{ firedQuartiles: s.tracking.firedQuartiles, filled: s.vast?.filled }] : []);
      let best = new Set();
      for (const b of breaks) {
        totalBreaks += 1;
        if (b.filled !== false) filledBreaks += 1;
        const q = b.firedQuartiles || new Set();
        if (q.size > best.size) best = q;
      }
      if (breaks.length) withBreaks += 1;
      if (best.has('complete')) completed += 1;
      if (s.viewability?.present) viewable += 1;
    }
    return {
      completionRate: withBreaks ? Math.round((completed / withBreaks) * 100) : null,
      completed, withBreaks, filledBreaks, totalBreaks, viewable,
    };
  }, [sessions]);

  const signOff = () => openVideoSignOff(filtered.length ? filtered : sessions, {
    validate: (session) => resolveVideoValidation(session, serverValidations),
  });

  const VIDEO_TYPES = new Set([
    'gam_video', 'ima', 'springserve_vast', 'springserve_ad_request', 'amazon_video',
    'vast_tracking', 'vast_error', 'omid_video', 'omid_session', 'omid_impression',
    'omid_measurement', 'video_content',
  ]);

  // Download the video requests as a standard HAR file (importable into other tools).
  const downloadVideoHar = () => {
    const videoRequests = (allRows || []).filter(r => VIDEO_TYPES.has(r.type || r.requestType));
    const har = buildHarFromRequests(videoRequests, { creator: 'AdLens FX — Video' });
    const blob = new Blob([JSON.stringify(har, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `video-requests-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.har`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  // Download all video requests + assembled sessions as a JSON file (richer than HAR).
  const downloadVideoData = () => {
    const safeRequest = (r) => ({
      type: r.type || r.requestType,
      method: r.method || 'GET',
      status: r.status ?? r.responseCode ?? null,
      time: r.startedAt || r.timestamp || null,
      device: r.deviceLabel || r.devicePlatform || null,
      url: r.url,
      note: r.note || null,
      issues: (r.issues || []).map(i => ({ level: i.level || i.severity, msg: i.msg || i.details || i.label })),
      validData: r.validData || null,
    });
    const safeSession = (s) => {
      let v = null;
      // Through the seam, like the sign-off report: an export whose verdicts
      // disagree with the screen that produced it is the same two-answers bug,
      // and a downloaded artifact is harder to catch it in.
      try { v = resolveVideoValidation(s, serverValidations); } catch {}
      return {
        type: s.sessionType, flavor: s.flavor || null,
        title: s.displayTitle || s.adUnit || null,
        contentType: s.contentType, status: s.overallStatus, time: s.timestamp,
        bids: { aps: s.bids?.amazon?.status || null, ss: s.bids?.ss?.status || null, tokensOk: s.bids?.tokensOk ?? null },
        adBreaks: (s.adBreaks || []).map(b => ({
          time: b.timestamp, filled: b.filled, source: b.source, duration: b.duration,
          quartiles: [...(b.firedQuartiles || [])], advertiser: b.advertiser || null,
          bidPrice: b.bidPrice ?? null, dealId: b.dealId || null, skippable: b.skippable || false, skipped: b.skipped || false,
        })),
        viewability: s.viewability?.present || false,
        validation: v ? { passed: v.passed, warned: v.warned, failed: v.failed, checks: v.checks.map(c => ({ key: c.key, label: c.label, status: c.status, detail: c.detail })) } : null,
      };
    };
    const useSessions = filtered.length ? filtered : sessions;
    const videoRequests = (allRows || []).filter(r => VIDEO_TYPES.has(r.type || r.requestType));
    const payload = {
      tool: 'AdLens FX — Video',
      exportedAt: new Date().toISOString(),
      summary: { sessions: useSessions.length, requests: videoRequests.length, pass: passing, warn: warning, fail: failing },
      sessions: useSessions.map(safeSession),
      requests: videoRequests.map(safeRequest),
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `video-requests-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.json`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  if (!sessions.length) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        height: 220, gap: 10, color: 'var(--muted)',
      }}>
        <div style={{ fontSize: 32 }}>🎬</div>
        <div style={{ fontSize: 13, fontWeight: 500 }}>No video ad sessions detected yet</div>
        <div style={{ fontSize: 11, opacity: 0.7, textAlign: 'center', maxWidth: 300 }}>
          Sessions appear when GAM VAST or IMA DAI requests are captured.<br />
          Make sure your device is proxied and play a video.
        </div>
      </div>
    );
  }

  return (
    // Provides the server-computed validations to the three nested consumers
    // via useVideoValidation. One provider, so a consumer cannot half-opt-out.
    <ServerValidationsContext.Provider value={serverValidations}>
    <div style={{ padding: '10px 14px', overflowY: 'auto', flex: 1 }}>
      {/* Summary bar */}
      <div style={{
        display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12,
        fontSize: 11, color: 'var(--muted)',
        flexWrap: 'wrap',
      }}>
        <span style={{ fontWeight: 600, color: 'var(--ink)' }}>
          {daiStreams.length > 0 && `${daiStreams.length} DAI stream${daiStreams.length !== 1 ? 's' : ''}`}
          {daiStreams.length > 0 && vastSessions.length > 0 && '  ·  '}
          {vastSessions.length > 0 && `${vastSessions.length} VAST session${vastSessions.length !== 1 ? 's' : ''}`}
        </span>
        {passing > 0 && <span style={{ color: '#38a169' }}>✓ {passing} pass</span>}
        {warning > 0 && <span style={{ color: '#dd6b20' }}>⚠ {warning} warn</span>}
        {failing > 0 && <span style={{ color: '#e53e3e' }}>✗ {failing} fail</span>}

        {analytics.completionRate != null && (
          <span title="Sessions where an ad break reached the complete (100%) beacon">
            ▶ completion <b style={{ color: analytics.completionRate >= 80 ? '#38a169' : analytics.completionRate >= 50 ? '#dd6b20' : '#e53e3e' }}>{analytics.completionRate}%</b>
          </span>
        )}
        {analytics.totalBreaks > 0 && (
          <span title="Filled ad breaks across all sessions">fill {analytics.filledBreaks}/{analytics.totalBreaks}</span>
        )}
        {/* Provenance — shown ONLY on fallback. "The server answered" is the
            expected case and a badge for it would be noise; a local build is the
            exception a reader needs, because it means this tab and a
            server-side sign-off could differ. */}
        {sessionSource === 'local' && (
          <span
            title="The proxy could not be reached, so these sessions were assembled in the browser. They may differ from a server-side sign-off report."
            style={{ color: '#854F0B', border: '1px solid #E5C07B', borderRadius: 4, padding: '1px 6px' }}
          >
            computed locally — proxy unreachable
          </span>
        )}
        {analytics.viewable > 0 && (
          <span style={{ color: '#3182ce' }} title="Sessions with client-side OMID viewability events">👁 {analytics.viewable} viewable</span>
        )}
        {noAdCount > 0 && (
          <span
            onClick={() => setNoAdOnly(v => !v)}
            title="Video sessions where no ad served (no ad break filled) — click to show only these"
            style={{
              cursor: 'pointer', fontWeight: 600, padding: '1px 8px', borderRadius: 12,
              background: noAdOnly ? 'rgba(229,62,62,0.16)' : 'rgba(229,62,62,0.08)',
              color: '#e53e3e', border: noAdOnly ? '1px solid #e53e3e' : '1px solid transparent',
            }}>
            🚫 {noAdCount} no-ad{noAdOnly ? ' ✕' : ''}
          </span>
        )}
        <span
          title={hlsWatch.total === 0
            ? 'No HLS manifest/segment requests have passed through the proxy yet. DAI/CDN segments that bypass the proxy are not counted.'
            : `${hlsWatch.total} HLS manifest/segment requests seen · ${hlsWatch.failed} failed (4xx/5xx). Monitoring is active.`}
          style={{
            fontWeight: 600, fontFamily: 'monospace', fontSize: 11,
            color: hlsWatch.total === 0 ? 'var(--muted)'
              : hlsWatch.failed ? '#dd6b20' : '#38a169',
          }}>
          🎞 {hlsWatch.total - hlsWatch.failed}/{hlsWatch.total} HLS ok
        </span>
        {captureWatch.total > 0 && (
          <span
            title={captureWatch.uncaptured === 0
              ? `All ${captureWatch.total} lifecycle request responses were captured — scan is complete.`
              : `${captureWatch.uncaptured} of ${captureWatch.total} lifecycle request responses were NOT captured (across ${captureWatch.sessions} session(s)). Checks on those requests are unconfirmed — the scan is partial.`}
            style={{
              fontWeight: 600, fontFamily: 'monospace', fontSize: 11,
              color: captureWatch.uncaptured ? '#dd6b20' : '#38a169',
            }}>
            📡 {captureWatch.captured}/{captureWatch.total} captured
          </span>
        )}
        {hlsErr.sessions > 0 && (
          <span
            onClick={() => setHlsErrOnly(v => !v)}
            title={`${hlsErr.total} HLS manifest/segment request(s) returned 4xx/5xx across ${hlsErr.sessions} session(s) — failed ad/content delivery (buffering, stalls, broken stitch). Click to show only these.`}
            style={{
              cursor: 'pointer', fontWeight: 600, padding: '1px 8px', borderRadius: 12,
              background: hlsErrOnly ? 'rgba(221,107,32,0.18)' : 'rgba(221,107,32,0.08)',
              color: '#dd6b20', border: hlsErrOnly ? '1px solid #dd6b20' : '1px solid transparent',
            }}>
            ⚠ {hlsErr.total} HLS fail{hlsErrOnly ? ' ✕' : ''}
          </span>
        )}

        <button
          onClick={downloadVideoHar}
          title="Download the video requests as a standard .har file (importable into Chrome DevTools, Charles, etc.)"
          style={{
            marginLeft: 'auto', padding: '5px 12px', borderRadius: 6,
            border: '1px solid rgba(160,174,192,0.5)', background: 'transparent',
            color: 'var(--muted)', fontSize: 11, fontWeight: 600, cursor: 'pointer',
          }}
        >
          ⬇ Download HAR
        </button>

        <button
          onClick={downloadVideoData}
          title="Download all video requests + assembled sessions as JSON (richer than HAR)"
          style={{
            padding: '5px 12px', borderRadius: 6,
            border: '1px solid rgba(160,174,192,0.5)', background: 'transparent',
            color: 'var(--muted)', fontSize: 11, fontWeight: 600, cursor: 'pointer',
          }}
        >
          ⬇ JSON
        </button>

        <button
          onClick={signOff}
          title="Open an exportable pass/fail video QA sign-off report"
          style={{
            padding: '5px 12px', borderRadius: 6,
            border: '1px solid rgba(0,122,255,0.4)', background: 'rgba(0,122,255,0.08)',
            color: '#007aff', fontSize: 11, fontWeight: 600, cursor: 'pointer',
          }}
        >
          📋 Video sign-off report
        </button>
      </div>

      {/* Bid health moved to its own Bid Health tab (VideoBidHealthPanel). */}

      {/* Toolbar: search · status · type · sort · expand */}
      <VideoToolbar
        search={search} setSearch={setSearch}
        statusFilter={statusFilter} setStatus={setStatus}
        typeFilter={typeFilter} setTypeFilter={setTypeFilter}
        sortBy={sortBy} setSortBy={setSortBy}
        onExpandAll={() => setExpandAll(v => (v === true ? undefined : true))}
        onCollapseAll={() => setExpandAll(false)}
        expandAll={expandAll}
        counts={{ all: total, fail: failing, warn: warning, pass: passing,
                  dai: sessions.filter(s => s.sessionType === 'dai_stream').length,
                  vast: sessions.filter(s => s.sessionType === 'vast').length }}
        showing={filtered.length}
      />

      {/* No matches under current filter */}
      {filtered.length === 0 && (
        <div style={{ padding: '30px 0', textAlign: 'center', color: 'var(--muted)', fontSize: 12 }}>
          No sessions match the current filter.
          <span onClick={() => { setSearch(''); setStatus('all'); setTypeFilter('all'); setNoAdOnly(false); }}
            style={{ marginLeft: 8, color: '#3182ce', cursor: 'pointer', textDecoration: 'underline' }}>clear filters</span>
        </div>
      )}

      {/* DAI streams */}
      {daiStreams.length > 0 && (
        <div>
          {daiStreams.map(session => (
            <DaiStreamCard
              key={session._k || session.timestamp}
              session={session}
              onJumpTo={onJumpTo}
              onOpenSummary={onOpenSummary}
              expandAll={expandAll}
            />
          ))}
        </div>
      )}

      {/* Standard VAST */}
      {vastSessions.length > 0 && (
        <div style={{ marginTop: daiStreams.length > 0 ? 12 : 0 }}>
          {daiStreams.length > 0 && (
            <div style={{
              fontSize: 10, fontWeight: 600, color: 'var(--muted)',
              textTransform: 'uppercase', letterSpacing: '0.05em',
              marginBottom: 6,
            }}>
              Client-side VAST
            </div>
          )}
          {vastSessions.map(session => (
            <VastSessionCard
              key={session._k || session.timestamp}
              session={session}
              onJumpTo={onJumpTo}
              onOpenSummary={onOpenSummary}
              expandAll={expandAll}
            />
          ))}
        </div>
      )}
    </div>
    </ServerValidationsContext.Provider>
  );
}
